<template>
  <div class="chat-container">
    <div class="chat-wrapper">
      <!-- 移动端遮罩层 -->
      <div 
        v-if="userInfo.admin === 1 && showUserListPanel" 
        class="mobile-overlay"
        @click="toggleUserListPanel"
      ></div>

      <!-- 管理员左侧用户列表 -->
      <div v-if="userInfo.admin === 1" class="user-list-panel" :class="{ show: showUserListPanel }">
        <div class="panel-header">
          <h3>会话列表</h3>
          <div class="user-count">{{ userList && userList.length ? userList.length : 0 }}</div>
        </div>
        <div class="user-list-content">
          <template v-if="groupedUserList && groupedUserList.length > 0">
            <div
              v-for="group in groupedUserList"
              :key="group.letter"
              class="user-group"
            >
              <div class="user-group-header">{{ group.letter }}</div>
              <div
                v-for="user in (group.users || [])"
                :key="user.id"
                :class="['user-item', { active: currentChatUserId === user.id }]"
                @click="selectUser(user.id)"
              >
                <div class="user-avatar">
                  <img v-if="user.avatar" :src="user.avatar" alt="头像" class="avatar-img" />
                  <span v-else>{{ (user.nickname || 'U').charAt(0).toUpperCase() }}</span>
                </div>
                <div class="user-info">
                  <div class="user-name-container">
                    <span class="user-name">{{ getDisplayName(user) }}</span>
                    <span v-if="getUnreadCountForUser(user) > 0" class="unread-badge">
                      {{ formatUnreadCount(getUnreadCountForUser(user)) }}
                    </span>
                  </div>
                  <div class="user-status">
                    <span v-if="isUserOnline(user.id)" class="status-online">
                      <span class="status-dot online"></span>在线中
                    </span>
                    <span v-else class="status-offline">
                      <span class="status-dot offline"></span>离线
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="empty-list">暂无用户</div>
        </div>
      </div>

      <!-- 聊天区域 -->
      <div class="chat-panel">
        <div class="chat-header">
          <div class="chat-header-left">
            <!-- 移动端菜单按钮（仅管理员显示） -->
            <button 
              v-if="userInfo.admin === 1" 
              class="mobile-menu-btn"
              @click="toggleUserListPanel"
              aria-label="打开会话列表"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <!-- 对方用户头像 -->
            <div class="chat-user-avatar" @click.stop="showAvatarMenu = !showAvatarMenu" v-click-outside="() => showAvatarMenu = false" :class="{ 'avatar-shaking': isAvatarShaking }">
              <img 
                v-if="userInfo.admin === 1 && currentChatUser && currentChatUser.avatar" 
                :src="currentChatUser.avatar" 
                alt="头像" 
                class="avatar-img" 
              />
              <span v-else-if="userInfo.admin === 1 && currentChatUser">
                {{ (currentChatUser.nickname || 'U').charAt(0).toUpperCase() }}
              </span>
              <img 
                v-else-if="userInfo.admin !== 1 && adminInfo && adminInfo.avatar" 
                :src="adminInfo.avatar" 
                alt="头像" 
                class="avatar-img" 
              />
              <span v-else-if="userInfo.admin !== 1 && adminInfo">
                {{ (adminInfo.nickname || 'A').charAt(0).toUpperCase() }}
              </span>
              <!-- 头像下拉菜单 -->
              <transition name="fade">
                <div v-if="showAvatarMenu" class="avatar-menu" @click.stop>
                  <div class="menu-item" @click="openAliasModal">
                    <span>设置别称</span>
                  </div>
                </div>
              </transition>
            </div>
            <div class="chat-title-section">
              <div class="chat-title">
                <span v-if="userInfo.admin === 1 && currentChatUser">
                  {{ getDisplayName(currentChatUser) }}
                </span>
                <span v-else-if="userInfo.admin !== 1 && adminInfo">
                  {{ getDisplayName(adminInfo) }}
                </span>
                <span v-else>小葱聊天室</span>
                <!-- 催Ta上线按钮（仅离线时显示，在用户名字右边） -->
                <button
                  v-if="shouldShowUrgeOnlineButton"
                  class="urge-online-btn"
                  :disabled="isUrgeOnlineDisabled"
                  @click="openUrgeOnlineModal"
                >
                  <span v-if="urgeOnlineCountdown > 0" class="countdown-text">
                    催Ta上线 ({{ formatCountdown(urgeOnlineCountdown) }})
                  </span>
                  <span v-else>催Ta上线</span>
                </button>
              </div>
              <div class="chat-status">
                <span v-if="userInfo.admin === 1 && currentChatUserId">
                  <span v-if="isUserOnline(currentChatUserId)" class="status-online">
                    <span class="status-dot online"></span>在线中
                  </span>
                  <span v-else class="status-offline">
                    <span class="status-dot offline"></span>{{ getOfflineTime(currentChatUserId) }}
                  </span>
                </span>
                <span v-else-if="userInfo.admin !== 1 && adminInfo">
                  <span v-if="isUserOnline(adminInfo.id)" class="status-online">
                    <span class="status-dot online"></span>在线中
                  </span>
                  <span v-else class="status-offline">
                    <span class="status-dot offline"></span>{{ getOfflineTime(adminInfo.id) }}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <!-- 声音提示开关（仅管理员） -->
            <button 
              v-if="userInfo.admin === 1"
              class="sound-toggle-btn" 
              @click="toggleSoundEnabled"
              :title="soundEnabled ? '关闭声音提示' : '开启声音提示'"
            >
              <span v-if="soundEnabled" class="sound-icon enabled">🔊</span>
              <span v-else class="sound-icon disabled">🔇</span>
            </button>
            <!-- 夜间模式切换按钮 -->
            <button 
              class="theme-toggle-btn" 
              @click="toggleDarkMode"
              :title="isDarkMode ? '切换到白天模式' : '切换到夜间模式'"
            >
              <span v-if="isDarkMode" class="theme-icon moon">🌙</span>
              <span v-else class="theme-icon sun">☀️</span>
            </button>
            <div class="user-avatar-small" @click="showProfile = true">
              <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="头像" class="avatar-img" />
              <span v-else>{{ (userInfo.nickname || 'U').charAt(0).toUpperCase() }}</span>
            </div>
          </div>
        </div>

        <div class="chat-messages" ref="messagesContainer">
          <div v-if="loadingMessages && messages.length === 0" class="empty-chat">
            <div class="empty-icon">⏳</div>
            <div class="empty-text">加载中...</div>
          </div>
          <div v-else-if="!currentChatUserId && userInfo.admin === 1" class="empty-chat">
            <div class="empty-icon">💬</div>
            <div class="empty-text">请选择一个用户开始聊天</div>
          </div>
          <div v-else-if="messages.length === 0" class="empty-chat">
            <div class="empty-icon">💬</div>
            <div class="empty-text">还没有消息，开始对话吧</div>
          </div>
          <transition-group 
            :name="enableMessageAnimation ? 'message' : ''" 
            tag="div"
            :css="enableMessageAnimation"
          >
            <div
              v-for="msg in messages"
              :key="msg.id"
              :class="['message-item', { own: msg.senderId === userInfo.id && !isMessageRecalled(msg) && !isMessagePatPat(msg), recalled: isMessageRecalled(msg), 'pat-pat': isMessagePatPat(msg) }]"
              :data-message-id="String(msg.id || '')"
              :data-message-time="msg.createTime ? String(new Date(msg.createTime).getTime()) : ''"
              :data-sender-id="String(msg.senderId || '')"
            >
              <!-- 撤回消息不显示头像 -->
              <div v-if="!isMessageRecalled(msg) && !isMessagePatPat(msg)" class="message-avatar" @dblclick.stop="handlePatPatFromMessage(msg)" :class="{ 'avatar-shaking': shakingAvatarUserId !== '' && String(shakingAvatarUserId) === String(msg.senderId), 'pat-pat-able': msg.senderId !== userInfo.id }" :data-shake-key="shakingAvatarUserId === String(msg.senderId) ? shakeAnimationKey : ''">
                <img 
                  v-if="getAvatarUrl(msg)" 
                  :src="getAvatarUrl(msg)" 
                  alt="头像" 
                  class="avatar-img" 
                />
                <span v-else>{{ getSenderName(msg).charAt(0).toUpperCase() }}</span>
              </div>
              <div class="message-content">
                <!-- 撤回提示 -->
                <div v-if="isMessageRecalled(msg)" class="recall-notice">
                  {{ getDisplayName(msg.senderId === userInfo.id ? userInfo : (msg.senderId === currentChatUserId ? currentChatUser : adminInfo)) }}撤回了一条消息
                </div>
                <!-- 拍一拍提示 -->
                <div v-else-if="isMessagePatPat(msg)" class="recall-notice pat-pat-notice">
                  <span v-if="msg.senderId === userInfo.id">
                    你拍了拍{{ getDisplayName(msg.receiverId === userInfo.id ? userInfo : (msg.receiverId === currentChatUserId ? currentChatUser : adminInfo)) }}
                  </span>
                  <span v-else>
                    {{ getDisplayName(msg.senderId === userInfo.id ? userInfo : (msg.senderId === currentChatUserId ? currentChatUser : adminInfo)) }}拍了拍你
                  </span>
                </div>
                <!-- 正常消息 -->
                <template v-else>
                  <!-- 表情消息（无气泡框） -->
                  <div v-if="msg.emojiUrl" class="message-emoji">
                    <img 
                      :src="msg.emojiUrl" 
                      alt="表情"
                      :class="['custom-emoji-display', { 'gif': msg.isGif === 1 || msg.isGif === true }]"
                    />
                  </div>
                  <!-- 普通消息（有气泡框） -->
                  <div v-else class="message-bubble">
                    <img 
                      v-if="msg.imageUrl" 
                      :src="msg.imageUrl" 
                      alt="图片" 
                      class="message-image"
                      @click="previewImage(msg.imageUrl)"
                    />
                    <span v-if="msg.content">{{ msg.content }}</span>
                  </div>
                  <div class="message-time">{{ formatTime(msg.createTime) }}</div>
                </template>
              </div>
            </div>
          </transition-group>
        </div>

        <div class="chat-input">
          <div class="input-wrapper">
            <input
              v-model="inputMessage"
              @keyup.enter="sendMessage"
              placeholder="输入一条温暖的消息..."
              :disabled="userInfo.admin === 1 && !currentChatUserId"
              autocomplete="off"
              ref="messageInput"
            />
            <!-- 表情选择面板 -->
            <transition name="emoji-panel">
              <div v-if="showEmojiPanel" class="emoji-panel">
                <div class="emoji-panel-header">
                  <span>选择表情</span>
                  <button class="emoji-panel-close" @click="closeEmojiPanel">×</button>
                </div>
                <div class="emoji-panel-tabs">
                  <button 
                    class="emoji-tab" 
                    :class="{ active: emojiTab === 'default' }"
                    @click="emojiTab = 'default'"
                  >
                    默认表情
                  </button>
                  <button 
                    class="emoji-tab" 
                    :class="{ active: emojiTab === 'custom' }"
                    @click="emojiTab = 'custom'"
                  >
                    小葱的表情
                  </button>
                </div>
                <!-- 默认表情 -->
                <div v-if="emojiTab === 'default'" class="emoji-list">
                  <button
                    v-for="(emoji, index) in emojiList"
                    :key="index"
                    class="emoji-item"
                    @click="insertEmoji(emoji)"
                    :title="emoji"
                  >
                    {{ emoji }}
                  </button>
                </div>
                <!-- 自定义表情 -->
                <div v-if="emojiTab === 'custom'" class="custom-emoji-list">
                  <div v-if="customEmojis.length === 0" class="custom-emoji-empty">
                    <button class="add-emoji-btn" @click="triggerEmojiUpload">
                      <span class="add-icon">+</span>
                      <span class="add-text">上传表情</span>
                    </button>
                  </div>
                  <div v-else class="custom-emoji-grid">
                    <div
                      v-for="emoji in customEmojis"
                      :key="emoji.id"
                      class="custom-emoji-item"
                      @click="sendCustomEmoji(emoji)"
                      @contextmenu.prevent="showEmojiContextMenu($event, emoji)"
                    >
                      <img 
                        v-if="emoji.isGif === 1 || emoji.isGif === true"
                        :src="emoji.emojiUrl" 
                        alt="表情"
                        class="custom-emoji-img gif"
                      />
                      <img 
                        v-else
                        :src="emoji.emojiUrl" 
                        alt="表情"
                        class="custom-emoji-img"
                      />
                      <button 
                        class="delete-emoji-btn"
                        @click.stop="deleteCustomEmoji(emoji.id)"
                        title="删除表情"
                      >
                        ×
                      </button>
                    </div>
                    <button class="add-emoji-btn" @click="triggerEmojiUpload" title="上传表情">
                      <span class="add-icon">+</span>
                    </button>
                  </div>
                </div>
              </div>
            </transition>
            <!-- 表情上传输入框（隐藏） -->
            <input
              type="file"
              ref="emojiInput"
              @change="handleEmojiUpload"
              accept="image/*"
              style="display: none"
            />
          </div>
          <input
            type="file"
            ref="imageInput"
            @change="handleImageSelect"
            accept="image/*"
            multiple
            style="display: none"
          />
          <button 
            class="image-button" 
            @click="selectImages"
            :disabled="userInfo.admin === 1 && !currentChatUserId"
            title="图片"
          >
            🖼️
          </button>
          <button 
            class="emoji-button" 
            @click="toggleEmojiPanel"
            :disabled="userInfo.admin === 1 && !currentChatUserId"
            title="表情"
          >
            😊
          </button>
          <button 
            class="send-button" 
            @click="sendMessage"
            :disabled="(userInfo.admin === 1 && !currentChatUserId) || !inputMessage.trim()"
          >
            发送
          </button>
        </div>
      </div>
    </div>

    <!-- 图片上传弹窗 -->
    <transition name="modal-fade">
      <div v-if="showImageUploadModal" class="image-upload-modal-overlay" @mousedown="handleModalMouseDown">
        <div class="image-upload-modal" @click.stop @mousedown.stop>
          <div class="image-upload-modal-header">
            <h3>选择图片</h3>
            <div class="image-count" :class="{ 'full': pendingImages && pendingImages.length >= 9 }">
              {{ pendingImages && pendingImages.length ? pendingImages.length : 0 }}/9
            </div>
            <button class="modal-close-btn" @click="closeImageUploadModal">×</button>
          </div>
          
          <div class="image-upload-modal-body">
            <div class="image-grid">
              <template v-if="pendingImages && Array.isArray(pendingImages) && pendingImages.length > 0">
                <div 
                  v-for="(image, index) in pendingImages" 
                  :key="index" 
                  class="image-item"
                >
                  <img 
                    :src="getImagePreview(image)" 
                    alt="预览" 
                    class="image-preview-thumb"
                    @click="previewPendingImage(index)"
                  />
                  <button class="image-delete-btn" @click="removePendingImage(index)">×</button>
                </div>
              </template>
              
              <!-- 添加图片按钮 -->
              <div 
                v-if="!pendingImages || !Array.isArray(pendingImages) || pendingImages.length < 9" 
                class="image-item add-image-item"
                @click="addMoreImages"
              >
                <div class="add-image-icon">+</div>
                <div class="add-image-text">添加图片</div>
              </div>
            </div>
            
            <div v-if="pendingImages && Array.isArray(pendingImages) && pendingImages.length >= 9" class="image-limit-tip">
              已选择9张图片，无法继续添加
            </div>
          </div>
          
          <div class="image-upload-modal-footer">
            <button class="btn-cancel" @click="closeImageUploadModal">取消</button>
            <button 
              class="btn-send" 
              @click="sendPendingImages"
              :disabled="!pendingImages || pendingImages.length === 0"
            >
              发送 ({{ pendingImages && pendingImages.length ? pendingImages.length : 0 }})
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 待发送图片预览弹窗 -->
    <transition name="image-preview">
      <div 
        v-if="previewImageIndex >= 0 && pendingImages && pendingImages[previewImageIndex]" 
        class="image-preview-overlay" 
        @click="previewImageIndex = -1"
      >
        <img 
          :src="getImagePreview(pendingImages[previewImageIndex])" 
          alt="预览图片" 
          class="image-preview-content" 
          @click.stop
        />
      </div>
    </transition>

    <!-- 催Ta上线弹窗 -->
    <transition name="modal-fade">
      <div v-if="showUrgeOnlineModal" class="urge-online-modal-overlay" @click.self="closeUrgeOnlineModal">
        <transition name="modal-scale">
          <div class="urge-online-modal" @click.stop>
            <div class="urge-online-modal-header">
              <h3>催Ta上线</h3>
              <button class="modal-close-btn" @click="closeUrgeOnlineModal">×</button>
            </div>
            <div class="urge-online-modal-content">
              <p class="urge-question">你确定要催Ta上线吗？</p>
              
              <!-- 缘由输入区域（点击"找Ta什么事呢？"后显示） -->
              <transition name="reason-fade-slide">
                <div v-if="showUrgeReasonInput" class="urge-reason-section">
                  <label class="urge-reason-label">找Ta什么事呢？</label>
                  <textarea
                    v-model="urgeReason"
                    class="urge-reason-input"
                    placeholder="请输入缘由（最多50字）"
                    maxlength="50"
                    rows="3"
                  ></textarea>
                  <div class="urge-reason-count">{{ urgeReason.length }}/50</div>
                </div>
              </transition>
              
              <!-- 找Ta什么事呢按钮 -->
              <transition name="reason-fade-slide">
                <div v-if="!showUrgeReasonInput" class="urge-reason-link">
                  <button class="urge-reason-btn" @click="showUrgeReasonInput = true">
                    找Ta什么事呢？
                  </button>
                </div>
              </transition>
            </div>
            <div class="urge-online-modal-footer">
              <button class="modal-btn cancel-btn" @click="closeUrgeOnlineModal">取消</button>
              <button class="modal-btn confirm-btn" @click="confirmUrgeOnline" :disabled="showUrgeReasonInput && !urgeReason.trim()">
                确认
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- 图片预览弹窗 -->
    <transition name="image-preview">
      <div 
        v-if="showImagePreview" 
        class="image-preview-overlay" 
        :class="{ 'dragging': isDraggingImage }"
        @click="handleImagePreviewClick"
        @mousemove="handleImagePreviewMouseMove"
        @mouseup="handleImagePreviewMouseUp"
        @mouseleave="handleImagePreviewMouseUp"
        @wheel.prevent="handleImagePreviewWheel"
      >
        <img 
          :src="imagePreviewUrl" 
          alt="预览图片" 
          class="image-preview-content" 
          :class="{ 'dragging': isDraggingImage }"
          :style="{
            transform: `scale(${imageScale}) translate(${imageTranslateX}px, ${imageTranslateY}px)`,
            transformOrigin: 'center center'
          }"
          @mousedown="handleImagePreviewMouseDown"
          @click.stop
        />
      </div>
    </transition>

    <!-- 个人信息弹窗 -->
    <transition name="profile-modal">
      <div v-if="showProfile" class="profile-modal-overlay" @mousedown="handleModalMouseDown">
        <div class="profile-modal-content" @click.stop @mousedown.stop>
        <div class="profile-modal-header">
          <h3>个人信息</h3>
          <button class="profile-close-btn" @click="showProfile = false">×</button>
        </div>
        
        <div class="profile-modal-body">
          <!-- 个人信息部分 -->
          <div class="profile-section">
            <h4 class="profile-section-title">个人信息</h4>
            
            <!-- 头像 -->
            <div class="profile-field">
              <label class="profile-label">头像</label>
              <div class="avatar-upload-area">
                <div class="avatar-preview-wrapper">
                  <img v-if="avatarPreview" :src="avatarPreview" alt="头像" class="avatar-preview" />
                  <div v-else class="avatar-preview-default">{{ nameInitial }}</div>
                </div>
                <label class="avatar-upload-btn">
                  <input type="file" accept="image/*" @change="handleAvatarChange" hidden />
                  更换头像
                </label>
              </div>
            </div>
            
            <!-- 昵称 -->
            <div class="profile-field">
              <label class="profile-label">昵称</label>
              <input
                v-model="profileForm.nickname"
                type="text"
                class="profile-input"
                placeholder="请输入昵称"
                @blur="validateNickname"
              />
              <div v-if="nicknameError" class="field-error">{{ nicknameError }}</div>
            </div>
            
            <!-- 邮箱 -->
            <div class="profile-field">
              <label class="profile-label">邮箱</label>
              <div class="email-field-row">
                <input
                  v-model="profileForm.email"
                  type="email"
                  class="profile-input"
                  placeholder="请输入邮箱"
                  :disabled="true"
                />
                <button class="btn-secondary email-change-btn" @click="showEmailChangeModal = true">变更</button>
              </div>
              <div v-if="emailError" class="field-error">{{ emailError }}</div>
            </div>
            
            <!-- 手机号 -->
            <div class="profile-field">
              <label class="profile-label">手机号</label>
              <div class="phone-field-row">
                <input
                  v-model="profileForm.phone"
                  type="text"
                  class="profile-input"
                  placeholder="请输入手机号"
                  :disabled="true"
                />
                <button class="btn-secondary phone-change-btn" @click="showPhoneChangeModal = true">变更</button>
              </div>
              <div v-if="phoneError" class="field-error">{{ phoneError }}</div>
            </div>
            
            <!-- 保存按钮 -->
            <div class="profile-actions">
              <button class="btn-primary" @click="updateProfile" :disabled="savingProfile">
                {{ savingProfile ? '保存中...' : '保存' }}
              </button>
              <button class="btn-secondary" @click="cancelProfile">取消</button>
            </div>
          </div>
          
          <!-- 修改密码部分 -->
          <div class="profile-section">
            <h4 class="profile-section-title">修改密码</h4>
            
            <!-- 旧密码 -->
            <div class="profile-field">
              <label class="profile-label">旧密码</label>
              <input
                v-model="passwordForm.oldPassword"
                type="password"
                class="profile-input"
                placeholder="请输入旧密码"
                @blur="validateOldPassword"
              />
              <div v-if="oldPasswordError" class="field-error">{{ oldPasswordError }}</div>
            </div>
            
            <!-- 新密码 -->
            <div class="profile-field">
              <label class="profile-label">新密码</label>
              <input
                v-model="passwordForm.newPassword"
                type="password"
                class="profile-input"
                placeholder="请输入新密码（至少8位）"
                @blur="validateNewPassword"
                @input="handlePasswordInput"
              />
              <div v-if="newPasswordError" class="field-error">{{ newPasswordError }}</div>
              <!-- 密码强度指示器 -->
              <div v-if="passwordForm.newPassword && passwordStrengthLevel > 0" class="password-strength">
                <div class="strength-label">密码强度：</div>
                <div class="strength-bars">
                  <div class="strength-bar" :class="'strength-' + passwordStrengthLevel"></div>
                </div>
                <div class="strength-text">{{ getStrengthText(passwordStrengthLevel) }}</div>
              </div>
            </div>
            
            <!-- 确认密码 -->
            <div class="profile-field">
              <label class="profile-label">确认密码</label>
              <input
                v-model="passwordForm.confirmPassword"
                type="password"
                class="profile-input"
                placeholder="请再次输入新密码"
                @blur="validateConfirmPassword"
                @keyup.enter="changePassword"
              />
              <div v-if="confirmPasswordError" class="field-error">{{ confirmPasswordError }}</div>
            </div>
            
            <!-- 修改密码按钮 -->
            <div class="profile-actions">
              <button class="btn-primary" @click="changePassword" :disabled="savingPassword">
                {{ savingPassword ? '修改中...' : '修改密码' }}
              </button>
              <button class="btn-secondary" @click="cancelPassword">取消</button>
            </div>
          </div>
          
          <!-- 退出登录 -->
          <div class="profile-section">
            <button @click="logout" class="btn-logout">退出登录</button>
          </div>
        </div>
        </div>
      </div>
    </transition>
      
    <!-- 邮箱变更弹窗 -->
      <div v-if="showEmailChangeModal" class="email-change-modal-overlay" @mousedown="handleModalMouseDown" @click="handleModalClick($event, closeEmailChangeModal)">
        <div class="email-change-modal" @click.stop @mousedown.stop>
          <!-- 第一步：验证当前邮箱 -->
          <div v-if="emailChangeStep === 1">
            <div class="email-change-header">
              <h3>验证当前邮箱</h3>
              <button class="email-change-close" @click="closeEmailChangeModal">×</button>
            </div>
            <div class="email-change-body" @keyup.enter="verifyCurrentEmail">
              <div class="email-display-wrapper">
                <div class="email-display-icon">📧</div>
                <div class="email-display">{{ maskEmail(profileForm.email) }}</div>
              </div>
              <div class="email-change-field">
                <input
                  v-model="emailChangeForm.code"
                  type="text"
                  class="profile-input email-code-input"
                  placeholder="请输入4位验证码"
                  maxlength="4"
                  @keyup.enter="verifyCurrentEmail"
                />
                <button
                  class="btn-secondary send-code-btn"
                  @click="sendEmailCode"
                  :disabled="sendingCode || emailCountdown > 0"
                >
                  {{ emailCountdown > 0 ? `${emailCountdown}秒` : '发送验证码' }}
                </button>
              </div>
              <div v-if="emailChangeError" class="field-error email-error">{{ emailChangeError }}</div>
            </div>
            <div class="email-change-footer" @keyup.enter="verifyCurrentEmail">
              <button class="btn-secondary" @click="closeEmailChangeModal">取消</button>
              <button class="btn-primary" @click="verifyCurrentEmail" :disabled="verifyingCurrentEmail">
                {{ verifyingCurrentEmail ? '验证中...' : '下一步' }}
              </button>
            </div>
          </div>

          <!-- 第二步：输入新邮箱 -->
          <div v-if="emailChangeStep === 2">
            <div class="email-change-header">
              <h3>输入新邮箱</h3>
              <button class="email-change-close" @click="closeEmailChangeModal">×</button>
            </div>
            <div class="email-change-body" @keyup.enter="confirmChangeEmail">
              <div class="profile-field">
                <label class="profile-label">新邮箱</label>
                <input
                  v-model="emailChangeForm.newEmail"
                  type="email"
                  class="profile-input"
                  placeholder="请输入新邮箱地址"
                  @blur="validateNewEmail"
                />
                <div v-if="newEmailError" class="field-error">{{ newEmailError }}</div>
              </div>
              <div class="email-change-field">
                <input
                  v-model="emailChangeForm.newEmailCode"
                  type="text"
                  class="profile-input email-code-input"
                  placeholder="请输入4位验证码"
                  maxlength="4"
                  @keyup.enter="confirmChangeEmail"
                />
                <button
                  class="btn-secondary send-code-btn"
                  @click="sendNewEmailCode"
                  :disabled="sendingNewEmailCode || newEmailCountdown > 0 || !!newEmailError || !emailChangeForm.newEmail"
                >
                  {{ newEmailCountdown > 0 ? `${newEmailCountdown}秒` : '发送验证码' }}
                </button>
              </div>
              <div v-if="emailChangeError" class="field-error email-error">{{ emailChangeError }}</div>
            </div>
            <div class="email-change-footer" @keyup.enter="confirmChangeEmail">
              <button class="btn-secondary" @click="emailChangeStep = 1">上一步</button>
              <button class="btn-primary" @click="confirmChangeEmail" :disabled="changingEmail || !!newEmailError">
                {{ changingEmail ? '提交中...' : '确认修改' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 手机号变更弹窗 -->
      <div v-if="showPhoneChangeModal" class="phone-change-modal-overlay" @mousedown="handleModalMouseDown" @click="handleModalClick($event, closePhoneChangeModal)">
        <div class="phone-change-modal" @click.stop @mousedown.stop>
          <!-- 第一步：验证当前手机号 -->
          <div v-if="phoneChangeStep === 1">
            <div class="phone-change-header">
              <h3>验证当前手机号</h3>
              <button class="phone-change-close" @click="closePhoneChangeModal">×</button>
            </div>
            <div class="phone-change-body" @keyup.enter="verifyCurrentPhone">
              <div class="phone-display-wrapper">
                <div class="phone-display-icon">📱</div>
                <div class="phone-display">{{ maskPhone(profileForm.phone) }}</div>
              </div>
              <div class="phone-change-field">
                <input
                  v-model="phoneChangeForm.code"
                  type="text"
                  class="profile-input phone-code-input"
                  placeholder="请输入4位验证码"
                  maxlength="4"
                  @keyup.enter="verifyCurrentPhone"
                />
                <button
                  class="btn-secondary send-code-btn"
                  @click="sendPhoneCode"
                  :disabled="sendingPhoneCode || phoneCountdown > 0"
                >
                  {{ phoneCountdown > 0 ? `${phoneCountdown}秒` : '发送验证码' }}
                </button>
              </div>
              <div v-if="phoneChangeError" class="field-error phone-error">{{ phoneChangeError }}</div>
            </div>
            <div class="phone-change-footer" @keyup.enter="verifyCurrentPhone">
              <button class="btn-secondary" @click="closePhoneChangeModal">取消</button>
              <button class="btn-primary" @click="verifyCurrentPhone" :disabled="verifyingCurrentPhone">
                {{ verifyingCurrentPhone ? '验证中...' : '下一步' }}
              </button>
            </div>
          </div>

          <!-- 第二步：输入新手机号 -->
          <div v-if="phoneChangeStep === 2">
            <div class="phone-change-header">
              <h3>输入新手机号</h3>
              <button class="phone-change-close" @click="closePhoneChangeModal">×</button>
            </div>
            <div class="phone-change-body" @keyup.enter="confirmChangePhone">
              <div class="profile-field">
                <label class="profile-label">新手机号</label>
                <input
                  v-model="phoneChangeForm.newPhone"
                  type="text"
                  class="profile-input"
                  placeholder="请输入新手机号"
                  @blur="validateNewPhone"
                />
                <div v-if="newPhoneError" class="field-error">{{ newPhoneError }}</div>
              </div>
              <div class="phone-change-field">
                <input
                  v-model="phoneChangeForm.newPhoneCode"
                  type="text"
                  class="profile-input phone-code-input"
                  placeholder="请输入4位验证码"
                  maxlength="4"
                  @keyup.enter="confirmChangePhone"
                />
                <button
                  class="btn-secondary send-code-btn"
                  @click="sendNewPhoneCode"
                  :disabled="sendingNewPhoneCode || newPhoneCountdown > 0 || !!newPhoneError || !phoneChangeForm.newPhone"
                >
                  {{ newPhoneCountdown > 0 ? `${newPhoneCountdown}秒` : '发送验证码' }}
                </button>
              </div>
              <div v-if="phoneChangeError" class="field-error phone-error">{{ phoneChangeError }}</div>
            </div>
            <div class="phone-change-footer" @keyup.enter="confirmChangePhone">
              <button class="btn-secondary" @click="phoneChangeStep = 1">上一步</button>
              <button class="btn-primary" @click="confirmChangePhone" :disabled="changingPhone || !!newPhoneError">
                {{ changingPhone ? '提交中...' : '确认修改' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    <!-- 自定义提示弹窗 -->
    <transition name="toast">
      <div v-if="showToast" class="toast-container" :class="'toast-' + toastType">
        <div class="toast-icon">
          <span v-if="toastType === 'success'">✓</span>
          <span v-else-if="toastType === 'error'">✕</span>
          <span v-else>ℹ</span>
        </div>
        <div class="toast-message">{{ toastMessage }}</div>
      </div>
    </transition>

    <!-- 设置别称弹窗 -->
    <transition name="modal-fade">
      <div v-if="showAliasModal" class="modal-overlay" @mousedown="handleAliasModalMouseDown">
        <div class="modal-content" @click.stop @mousedown.stop>
          <div class="modal-header">
            <h3>设置别称</h3>
            <button class="modal-close-btn" @click="closeAliasModal">×</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>别称</label>
              <div class="input-with-count">
                <input 
                  v-model="aliasForm.alias" 
                  type="text" 
                  placeholder="请输入别称（留空则删除别称）"
                  maxlength="10"
                  class="form-input"
                  @keyup.enter="saveAlias"
                />
                <span class="char-count" :class="{ 'char-count-full': aliasForm.alias.length >= 10 }">
                  {{ aliasForm.alias.length }}/10
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeAliasModal">取消</button>
            <button class="btn-confirm" @click="saveAlias">保存</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 自定义右键菜单 -->
    <transition name="fade">
      <div 
        v-if="showContextMenu" 
        class="context-menu"
        :style="{ left: contextMenuPosition.x + 'px', top: contextMenuPosition.y + 'px' }"
        @click.stop
      >
        <div 
          v-for="(item, index) in contextMenuItems" 
          :key="index"
          class="context-menu-item"
          :class="{ disabled: item.disabled, divider: item.divider, danger: item.danger }"
          @click="handleContextMenuClick(item)"
        >
          <span v-if="item.icon" class="menu-icon">{{ item.icon }}</span>
          <span class="menu-text">{{ item.label }}</span>
          <span v-if="item.shortcut" class="menu-shortcut">{{ item.shortcut }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
console.log('Chat.vue 脚本开始加载', new Date().toISOString())

// 立即添加全局右键菜单事件监听器（不依赖 Vue 生命周期）
;(function() {
  console.log('开始设置全局右键菜单监听器')
  
  // 存储菜单状态
  let showContextMenu = false
  let contextMenuPosition = { x: 0, y: 0 }
  let contextMenuItems = []
  let contextMenuTarget = null
  let contextMenuShowTime = 0
  let contextMenuElement = null
  
  // 创建菜单元素
  function createContextMenu() {
    if (contextMenuElement) return contextMenuElement
    
    const menu = document.createElement('div')
    menu.className = 'context-menu'
    menu.style.cssText = 'position:fixed;background:white;border:1px solid #e0e0e0;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);min-width:180px;z-index:10000;padding:4px 0;font-size:14px;display:none;'
    document.body.appendChild(menu)
    contextMenuElement = menu
    return menu
  }
  
  // 显示菜单
  function showMenu(x, y, items) {
    const menu = createContextMenu()
    contextMenuItems = items
    contextMenuPosition = { x, y }
    showContextMenu = true
    contextMenuShowTime = Date.now()
    
    // 清空并填充菜单项
    menu.innerHTML = items.map((item, index) => {
      if (item.divider) {
        return '<div class="context-menu-item divider"></div>'
      }
      const icon = item.icon ? `<span class="menu-icon">${item.icon}</span>` : ''
      const shortcut = item.shortcut ? `<span class="menu-shortcut">${item.shortcut}</span>` : ''
      const danger = item.danger ? 'danger' : ''
      const disabled = item.disabled ? 'disabled' : ''
      return `<div class="context-menu-item ${danger} ${disabled}" data-action="${item.action || ''}" data-index="${index}">
        ${icon}<span class="menu-text">${item.label}</span>${shortcut}
      </div>`
    }).join('')
    
    // 设置位置
    menu.style.left = x + 'px'
    menu.style.top = y + 'px'
    menu.style.display = 'block'
    
    // 调整位置防止超出屏幕
    setTimeout(() => {
      const rect = menu.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      let newX = x
      let newY = y
      
      if (x + rect.width > windowWidth) {
        newX = windowWidth - rect.width - 10
      }
      if (y + rect.height > windowHeight) {
        newY = windowHeight - rect.height - 10
      }
      newX = Math.max(10, newX)
      newY = Math.max(10, newY)
      
      menu.style.left = newX + 'px'
      menu.style.top = newY + 'px'
    }, 0)
    
    console.log('全局右键菜单已显示', { x, y, itemsCount: items.length })
  }
  
  // 隐藏菜单
  function hideMenu() {
    if (contextMenuElement) {
      contextMenuElement.style.display = 'none'
    }
    showContextMenu = false
    contextMenuItems = []
    contextMenuTarget = null
  }
  
  // 处理右键菜单
  // 注意：这个 IIFE 中的 handleContextMenu 已经被移除
  // 因为消息区域和用户列表项的右键菜单应该由 Vue 组件处理
  // 全局脚本（main.js 和 index.html）会处理其他区域的右键菜单
  
  // 处理鼠标按下（关闭菜单）- 这个仍然需要，用于关闭全局脚本创建的菜单
  function handleMouseDown(event) {
    if (!showContextMenu) return
    
    const timeSinceShow = Date.now() - contextMenuShowTime
    if (timeSinceShow < 100) return
    
    if (event.button === 2) return
    
    if (contextMenuElement && contextMenuElement.contains(event.target)) {
      // 点击菜单项
      const item = event.target.closest('.context-menu-item')
      if (item && !item.classList.contains('divider') && !item.classList.contains('disabled')) {
        const action = item.dataset.action
        console.log('菜单项被点击:', action)
        // 这里可以触发自定义事件，让 Vue 组件处理
        const customEvent = new CustomEvent('contextmenu-action', { 
          detail: { action, target: contextMenuTarget } 
        })
        document.dispatchEvent(customEvent)
      }
      return
    }
    
    hideMenu()
  }
  
  // 只添加 mousedown 监听器，不添加 contextmenu 监听器
  // contextmenu 监听器由全局脚本（main.js 和 index.html）和 Vue 组件处理
  document.addEventListener('mousedown', handleMouseDown, true)
  
  console.log('Chat.vue IIFE: 只添加了 mousedown 监听器（用于关闭全局菜单）')
})()

import { getUserList, getMessagesByUser, updateProfile, changePassword, getAdminInfo, uploadAvatar, changeEmail, changePhone, getUnreadCount, clearUnreadCount, uploadImage, setAlias, getAlias, recallMessage, getCustomEmojis, uploadCustomEmoji, deleteCustomEmoji, saveEmojiFromUrl, urgeOnline, getUrgeOnlineCountdown, patPat, checkAndSetOnlineAudioCooldown, saveLastChatUser, getLastChatUser } from '@/api/chat'
import { getUserInfo, getOnlineStatus, getSoundEnabled, updateSoundEnabled } from '@/api/user'
import { pinyin } from 'pinyin-pro'
import { sendEmailCode, sendSmsCode, checkEmail, checkPhone } from '@/api/auth'

console.log('Chat 组件定义开始', new Date().toISOString())

export default {
  name: 'Chat',
  directives: {
    'click-outside': {
      bind(el, binding) {
        // 记录鼠标按下时的位置和目标
        let mouseDownTarget = null
        let mouseDownTime = 0
        
        el.mouseDownEvent = function(event) {
          mouseDownTarget = event.target
          mouseDownTime = Date.now()
        }
        
        el.clickOutsideEvent = function(event) {
          // 检查点击的元素是否在 el 内部（包括子元素）
          const clickedInside = el === event.target || el.contains(event.target)
          
          // 如果点击在外部，执行回调
          if (!clickedInside) {
            // 检查是否是在选择文本
            // 1. 检查是否有文本被选中
            const hasTextSelection = window.getSelection && window.getSelection().toString().length > 0
            // 2. 检查鼠标按下和抬起的目标是否不同（可能是拖拽选择）
            const isDragSelection = mouseDownTarget && mouseDownTarget !== event.target
            // 3. 检查点击时间间隔（如果时间间隔很短，可能是快速点击；如果很长，可能是拖拽）
            const clickDuration = Date.now() - mouseDownTime
            
            // 只有在不是文本选择的情况下才关闭
            if (!hasTextSelection && !isDragSelection && clickDuration < 500) {
              binding.value(event)
            }
          }
          
          // 重置鼠标按下目标
          mouseDownTarget = null
          mouseDownTime = 0
        }
        
        // 监听鼠标按下事件
        document.addEventListener('mousedown', el.mouseDownEvent, true)
        // 使用捕获阶段，确保在其他点击事件之前处理
        document.addEventListener('click', el.clickOutsideEvent, true)
      },
      unbind(el) {
        if (el.mouseDownEvent) {
          document.removeEventListener('mousedown', el.mouseDownEvent, true)
        }
        if (el.clickOutsideEvent) {
          document.removeEventListener('click', el.clickOutsideEvent, true)
        }
      }
    }
  },
  data() {
    return {
      showUserListPanel: false, // 移动端用户列表面板显示状态
      userInfo: {},
      userList: [],
      adminInfo: null,
      currentChatUserId: null,
      currentChatUser: null,
      messages: [],
      inputMessage: '',
      ws: null,
      showAvatarMenu: false, // 是否显示头像下拉菜单
      showAliasModal: false, // 是否显示设置别称弹窗
      aliasForm: {
        alias: ''
      },
      aliasModalMouseDownTarget: null, // 记录设置别称弹窗鼠标按下时的目标
      modalMouseDownTarget: null, // 记录所有弹窗鼠标按下时的目标（通用）
      modalMouseDownPosition: null, // 记录鼠标按下时的位置 { x, y }
      modalMouseMoved: false, // 记录鼠标是否移动过（拖拽）
      shouldPreventClose: false, // 标志：是否应该阻止弹窗关闭
      userAliases: {}, // 存储用户别称 { userId: alias }
      // 右键菜单相关
      showContextMenu: false, // 是否显示右键菜单
      contextMenuPosition: { x: 0, y: 0 }, // 右键菜单位置
      contextMenuItems: [], // 右键菜单选项
      contextMenuTarget: null, // 右键菜单目标元素
      // 保存事件处理函数的引用，以便在组件销毁时移除
      contextMenuHandler: null,
      mouseDownHandler: null,
      contextMenuShowTime: 0, // 记录菜单显示的时间，用于防止立即关闭
      contextMenuActionHandler: null, // 全局右键菜单操作监听器
      showProfile: false,
      profileForm: {
        nickname: '',
        email: '',
        phone: ''
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      avatarPreview: '',
      nicknameError: '',
      emailError: '',
      phoneError: '',
      oldPasswordError: '',
      newPasswordError: '',
      confirmPasswordError: '',
      passwordStrengthLevel: 0,
      savingProfile: false,
      savingPassword: false,
      onlineStatus: {},
      offlineTime: {},
      toastMessage: '',
      toastType: 'success', // success, error, info
      showToast: false,
      // 邮箱变更相关
      showEmailChangeModal: false,
      emailChangeStep: 1, // 1: 验证当前邮箱, 2: 输入新邮箱
      emailChangeForm: {
        code: '',
        newEmail: '',
        newEmailCode: ''
      },
      emailChangeError: '',
      newEmailError: '',
      sendingCode: false,
      sendingNewEmailCode: false,
      emailCountdown: 0,
      newEmailCountdown: 0,
      changingEmail: false,
      verifyingCurrentEmail: false,
      // 手机号变更相关
      showPhoneChangeModal: false,
      phoneChangeStep: 1, // 1: 验证当前手机号, 2: 输入新手机号
      phoneChangeForm: {
        code: '',
        newPhone: '',
        newPhoneCode: ''
      },
      phoneChangeError: '',
      newPhoneError: '',
      sendingPhoneCode: false,
      sendingNewPhoneCode: false,
      phoneCountdown: 0,
      newPhoneCountdown: 0,
      changingPhone: false,
      verifyingCurrentPhone: false,
      // 未读消息计数
      unreadCount: {},
      unreadTimer: null,
      // 消息缓存，key为userId，value为消息列表
      messageCache: {},
      // 加载状态
      loadingMessages: false,
      // 是否启用消息动画（只在发送新消息时启用）
      enableMessageAnimation: false,
      // 标记是否正在切换用户，用于防止重复更新
      isSwitchingUser: false,
      // 夜间模式
      isDarkMode: false,
      // 声音提示开关（仅管理员）
      soundEnabled: 1, // 默认开启（1-开启，0-关闭）
      // 表情面板相关
      showEmojiPanel: false,
      emojiTab: 'default', // 表情面板标签：'default' 或 'custom'
      customEmojis: [], // 用户自定义表情列表
      showImagePreview: false,
      imagePreviewUrl: '',
      imageScale: 1, // 图片缩放比例
      imageTranslateX: 0, // 图片X轴偏移
      imageTranslateY: 0, // 图片Y轴偏移
      imagePreviewMouseX: 0, // 鼠标X坐标
      imagePreviewMouseY: 0, // 鼠标Y坐标
      isDraggingImage: false, // 是否正在拖拽图片
      // 图片上传弹窗相关
      showImageUploadModal: false, // 是否显示图片上传弹窗
      pendingImages: [], // 待发送的图片列表（File对象数组）
      previewImageIndex: -1, // 预览的图片索引
      hasDragged: false, // 是否发生了拖拽（用于区分点击和拖拽）
      dragStartX: 0, // 拖拽开始时的鼠标X坐标
      dragStartY: 0, // 拖拽开始时的鼠标Y坐标
      dragStartTranslateX: 0, // 拖拽开始时的X偏移
      dragStartTranslateY: 0, // 拖拽开始时的Y偏移
      emojiList: [
        '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
        '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
        '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
        '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
        '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
        '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
        '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯',
        '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
        '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈',
        '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾',
        '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿',
        '😾', '👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️',
        '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️',
        '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲',
        '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻',
        '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁', '👅', '👄',
        '💋', '💘', '💝', '💖', '💗', '💓', '💞', '💕', '💟', '❣️',
        '💔', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎',
        '💯', '💢', '💥', '💫', '💦', '💨', '🕳', '💣', '💬', '👁️‍🗨️',
        '🗨', '🗯', '💭', '💤', '👋', '🤚', '🖐', '✋', '🖖', '👌'
      ],
      // 催Ta上线相关
      showUrgeOnlineModal: false, // 是否显示催Ta上线弹窗
      urgeOnlineCountdown: 0, // 催Ta上线倒计时（秒）
      showUrgeReasonInput: false, // 是否显示缘由输入框
      urgeReason: '', // 催Ta上线缘由
      urgeOnlineTimer: null, // 催Ta上线倒计时定时器
      // 拍一拍相关
      isAvatarShaking: false, // 头像是否在摇晃（顶部头像）
      shakingAvatarUserId: '', // 正在摇晃的头像用户ID（消息中的头像，使用空字符串而不是null以确保Vue响应式）
      shakeAnimationKey: 0, // 动画触发键，用于强制重新触发动画
      patPatAudio: null, // 拍一拍音频对象
      // WebSocket相关
      wsCloseHandled: false, // WebSocket关闭事件是否已处理，防止重复处理
      redirectingToLogin: false, // 是否正在重定向到登录页，防止重复重定向
      // 用户上线音频相关
      onlineAudio: null, // 用户上线音频对象
      onlineAudioPlayed: {} // 记录每个用户在当前会话中是否已经播放过上线音频（key: userId, value: true/false），避免重复播放
    }
  },
  async mounted() {
    console.log('Chat 组件 mounted 开始执行')
    
    // 初始化夜间模式
    this.initDarkMode()
    
    // 先添加右键菜单事件监听器，确保无论什么情况都能响应右键
    this.contextMenuHandler = (event) => {
      const target = event.target
      
      // 处理聊天容器内的所有区域（包括消息区域、用户列表、空白处等）
      const isInChatContainer = target.closest('.chat-container') || 
                                target.closest('.chat-wrapper') ||
                                target.closest('.chat-panel') ||
                                target.closest('.user-list-panel') ||
                                target.closest('.chat-messages')
      
      if (!isInChatContainer) {
        // 不在聊天容器内，让全局脚本处理
        return
      }
      
      // 阻止事件继续传播和默认行为，避免全局脚本也处理
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation() // 阻止同一阶段的其他监听器
      
      console.log('✅ Vue组件 contextmenu 事件触发', event.target, { isInChatContainer })
      this.handleContextMenu(event)
    }
    this.mouseDownHandler = (event) => {
      this.handleMouseDown(event)
    }
    document.addEventListener('contextmenu', this.contextMenuHandler, true) // 使用捕获阶段
    document.addEventListener('mousedown', this.mouseDownHandler, true) // 使用捕获阶段
    console.log('右键菜单事件监听器已添加（在 mounted 开始）', {
      contextMenuHandler: !!this.contextMenuHandler,
      mouseDownHandler: !!this.mouseDownHandler
    })
    
    // 检查 token 是否存在
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('没有 token，跳转到登录页')
      this.$router.push('/login')
      return
    }
    
    // 验证 token 是否有效（通过调用需要认证的 API）
    try {
      await getUserInfo()
      console.log('getUserInfo 成功')
    } catch (error) {
      console.log('getUserInfo 失败，跳转到登录页', error)
      // 如果 token 过期或无效，request.js 的拦截器会自动重定向到 /login
      // 这里不需要额外处理，直接返回
      return
    }
    
    const userInfoStr = localStorage.getItem('userInfo')
    if (!userInfoStr) {
      this.$router.push('/login')
      return
    }
    this.userInfo = JSON.parse(userInfoStr)
    
    // 确保 userInfo 有 id 字段
    if (!this.userInfo.id && this.userInfo.userId) {
      this.userInfo.id = this.userInfo.userId
    }
    
    if (!this.userInfo.id) {
      this.showToastMessage('用户信息不完整，请重新登录', 'error')
      setTimeout(() => {
        this.$router.push('/login')
      }, 1500)
      return
    }
    
    this.profileForm.nickname = this.userInfo.nickname || ''
    this.profileForm.email = this.userInfo.email || ''
    this.profileForm.phone = this.userInfo.phone || ''
    this.avatarPreview = this.userInfo.avatar || ''
    this.initWebSocket()
    
    if (this.userInfo.admin === 1) {
      // 管理员：加载用户列表
      this.loadUserList()
      // 加载声音设置
      this.loadSoundEnabled()
    } else {
      // 普通用户：自动加载管理员信息并开始聊天
      this.loadAdminAndStartChat()
    }
    
    // 开始轮询未读消息（仅管理员需要）
    if (this.userInfo.admin === 1) {
      this.startUnreadPolling()
    }
    
    // 定期更新离线时间（每3秒检查一次在线状态）
    setInterval(() => {
      this.updateOfflineTime()
    }, 3000)
    
    // 添加点击外部区域关闭表情面板的监听器
    document.addEventListener('click', this.handleClickOutside)
    
    // 监听全局右键菜单的自定义事件
    this.contextMenuActionHandler = (event) => {
      console.log('✅ Vue 组件收到 contextmenu-action 事件:', event, event.detail)
      if (!event || !event.detail) {
        console.error('❌ 事件或事件详情为空:', event)
        return
      }
      const { action, target } = event.detail
      console.log('收到右键菜单操作:', action, target)
      if (!action) {
        console.error('❌ action 为空')
        return
      }
      this.handleContextMenuAction(action, target)
    }
    // 使用捕获阶段确保能接收到事件
    document.addEventListener('contextmenu-action', this.contextMenuActionHandler, true)
    console.log('✅ Vue 组件已添加 contextmenu-action 事件监听器（捕获阶段）')
    
    console.log('mounted 钩子执行完成')
    
    // 添加页面关闭事件监听器，确保WebSocket正确关闭
    this.handleBeforeUnload = () => {
      console.log('页面即将关闭，关闭WebSocket连接')
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 标记为已处理，避免重复处理
        this.wsCloseHandled = true
        // 关闭WebSocket连接，这会触发后端的onClose方法，记录下线时间
        this.ws.close()
      }
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    
    // 初始化催Ta上线倒计时检查
    this.checkUrgeOnlineCountdown()
    // 每1秒检查一次倒计时
    setInterval(() => {
      this.checkUrgeOnlineCountdown()
    }, 1000)
    
    // 实时更新离线时间显示（每1秒更新一次显示）
    setInterval(() => {
      // 更新离线时间计算
      Object.keys(this.offlineTime).forEach(userId => {
        const userIdInt = parseInt(userId)
        const offline = this.offlineTime[userId]
        if (offline && offline.lastTime && !this.isUserOnline(userIdInt)) {
          const seconds = Math.floor((Date.now() - offline.lastTime) / 1000)
          // 同时更新数字和字符串key
          this.offlineTime[userIdInt] = { lastTime: offline.lastTime, seconds }
          this.offlineTime[userIdInt.toString()] = { lastTime: offline.lastTime, seconds }
        }
      })
      // 触发视图更新
      this.$forceUpdate()
    }, 1000)
  },
  beforeUnmount() {
    // 移除页面关闭事件监听器
    if (this.handleBeforeUnload) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }
    
    // 关闭WebSocket连接，确保用户状态更新为离线
    if (this.ws) {
      this.wsCloseHandled = true
      this.ws.close()
    }
    
    if (this.unreadTimer) {
      clearInterval(this.unreadTimer)
    }
    // 移除点击外部区域关闭表情面板的监听器
    document.removeEventListener('click', this.handleClickOutside)
    // 清理鼠标移动监听器
    document.removeEventListener('mousemove', this.handleModalMouseMove)
    // 移除右键菜单事件监听器
    if (this.contextMenuHandler) {
      document.removeEventListener('contextmenu', this.contextMenuHandler)
    }
    if (this.mouseDownHandler) {
      document.removeEventListener('mousedown', this.mouseDownHandler)
    }
    // 移除全局右键菜单操作监听器
    if (this.contextMenuActionHandler) {
      document.removeEventListener('contextmenu-action', this.contextMenuActionHandler)
    }
  },
  methods: {
    async loadUserList() {
      try {
        const res = await getUserList()
        this.userList = res.data || []
        // 并行加载所有用户的别称
        const aliasPromises = this.userList.map(user => this.loadAlias(user.id))
        await Promise.all(aliasPromises)
        // 并行检查所有用户的在线状态
        const statusPromises = this.userList.map(user => this.checkOnlineStatus(user.id))
        await Promise.all(statusPromises)
        
        // 如果有用户列表且还没有选择聊天用户，尝试恢复最后聊天对象
        if (this.userList.length > 0 && !this.currentChatUserId) {
          try {
            // 从Redis获取最后聊天对象
            const lastChatRes = await getLastChatUser()
            if (lastChatRes && lastChatRes.data && lastChatRes.data.exists && lastChatRes.data.targetUserId) {
              const lastChatUserId = lastChatRes.data.targetUserId
              // 验证该用户是否还在用户列表中
              const userExists = this.userList.some(user => user.id === lastChatUserId)
              if (userExists) {
                // 恢复最后聊天对象
                await this.selectUser(lastChatUserId)
                console.log('恢复最后聊天对象:', lastChatUserId)
                return
              } else {
                console.log('最后聊天对象不在用户列表中，使用默认选择')
              }
            }
          } catch (e) {
            console.error('获取最后聊天对象失败', e)
            // 失败时继续使用默认逻辑
          }
          
          // 如果没有最后聊天对象或获取失败，选择第一个用户
          await this.selectUser(this.userList[0].id)
        }
      } catch (e) {
        console.error('加载用户列表失败', e)
      }
    },
    // 处理头像下拉菜单外部点击
    handleAvatarMenuClickOutside(event) {
      // 检查点击的元素是否在头像或下拉菜单内部
      const avatarElement = this.$el?.querySelector('.chat-user-avatar')
      if (avatarElement && !avatarElement.contains(event.target)) {
        // 检查是否是在选择文本
        const hasTextSelection = window.getSelection && window.getSelection().toString().length > 0
        if (!hasTextSelection) {
          this.showAvatarMenu = false
        }
      }
    },
    async loadAdminAndStartChat() {
      try {
        const res = await getAdminInfo()
        if (res.data && res.data.id) {
          this.adminInfo = res.data
          this.currentChatUserId = res.data.id
          this.currentChatUser = res.data
          // 加载别称
          await this.loadAlias(res.data.id)
          // 检查管理员在线状态
          this.checkOnlineStatus(res.data.id)
          await this.loadMessages()
        } else {
          // 如果没有管理员，仍然允许聊天（可能管理员还没设置）
          // 清空消息列表，允许用户发送消息
          this.messages = []
          this.adminInfo = null
          this.currentChatUserId = null
        }
      } catch (e) {
        console.error('加载管理员信息失败', e)
        // 不显示错误提示，允许用户继续使用
        this.messages = []
        this.adminInfo = null
        this.currentChatUserId = null
      }
    },
    // 切换移动端用户列表面板
    toggleUserListPanel() {
      this.showUserListPanel = !this.showUserListPanel
    },
    async selectUser(userId) {
      // 关闭设置别称弹窗（如果打开的话）
      if (this.showAliasModal) {
        this.closeAliasModal()
      }
      
      // 关闭头像下拉菜单（如果打开的话）
      if (this.showAvatarMenu) {
        this.showAvatarMenu = false
      }
      
      // 移动端选择用户后关闭用户列表面板
      if (this.showUserListPanel) {
        this.showUserListPanel = false
      }
      
      // 标记正在切换用户
      this.isSwitchingUser = true
      
      // 切换用户时立即禁用动画，避免切换时的动画闪烁
      this.enableMessageAnimation = false
      
      // 立即更新UI，让用户看到即时反馈
      this.currentChatUserId = userId
      this.currentChatUser = this.userList.find(u => u.id === userId)
      
      // 切换用户时，立即停止之前的倒计时并检查新用户的冷却时间
      this.stopUrgeOnlineCountdown()
      this.urgeOnlineCountdown = 0
      // 立即检查新用户的催Ta上线倒计时
      this.checkUrgeOnlineCountdown()
      
      // 先显示缓存的消息（如果有），避免闪烁 - 这是最优先的，不等待任何异步操作
      const cacheKey = userId.toString()
      const hasCache = this.messageCache[cacheKey] && this.messageCache[cacheKey].length > 0
      
      if (hasCache) {
        // 有缓存，立即显示，无需等待任何异步操作
        this.messages = [...this.messageCache[cacheKey]]
        this.loadingMessages = false
        // 使用 nextTick 确保 DOM 更新后再滚动
        this.$nextTick(async () => {
          // 检查是否有图片消息，如果有则等待所有图片加载完成后再滚动
          const hasImages = this.messages.some(msg => msg.imageUrl)
          if (hasImages) {
            await this.waitForImagesLoad()
          }
          this.scrollToBottom()
        })
      } else {
        // 没有缓存，显示加载状态
        this.messages = []
        this.loadingMessages = true
      }
      
      // 所有异步操作并行执行，不阻塞UI显示
      Promise.all([
        this.loadAlias(userId), // 加载别称
        this.checkOnlineStatus(userId), // 检查在线状态
        this.clearUnreadCountForUser(userId), // 清除未读计数
        this.loadMessages(hasCache, cacheKey), // 加载消息
        // 保存最后聊天对象到Redis（仅管理员）
        this.userInfo.admin === 1 ? this.saveLastChatUser(userId) : Promise.resolve()
      ]).finally(() => {
        // 确保当前用户ID没有变化
        if (this.currentChatUserId === userId) {
          this.loadingMessages = false
          // 标记切换完成
          this.isSwitchingUser = false
          // 加载完成后，延迟启用动画（为后续新消息准备）
          setTimeout(() => {
            this.enableMessageAnimation = true
          }, 200)
        } else {
          // 如果用户已切换，也要清除标志
          this.isSwitchingUser = false
        }
      })
    },
    // 保存最后聊天对象到Redis
    async saveLastChatUser(targetUserId) {
      try {
        await saveLastChatUser(targetUserId)
      } catch (e) {
        console.error('保存最后聊天对象失败', e)
        // 失败不影响主流程，静默处理
      }
    },
    async loadMessages(hasCache = false, cacheKey = null) {
      if (!this.currentChatUserId) {
        // 如果没有当前聊天对象，清空消息列表
        this.messages = []
        return
      }
      
      const key = cacheKey || this.currentChatUserId.toString()
      const targetUserId = this.currentChatUserId // 保存当前用户ID，防止切换时异步回调覆盖
      
      // 加载消息时禁用动画
      this.enableMessageAnimation = false
      
      try {
        const res = await getMessagesByUser(this.currentChatUserId)
        const newMessages = (res.data || []).map(msg => ({
          ...msg,
          isDelete: msg.isDelete !== undefined && msg.isDelete !== null ? msg.isDelete : 0
        }))
        
        // 再次检查用户ID是否变化（防止快速切换时的竞态条件）
        if (this.currentChatUserId !== targetUserId) {
          // 用户已切换，忽略这次加载结果
          return
        }
        
        // 如果正在切换用户，且有缓存显示，检查新消息是否与当前显示的消息相同
        if (this.isSwitchingUser && hasCache) {
          const currentMessages = this.messages
          
          // 检查当前显示的消息是否与新消息相同
          if (currentMessages && currentMessages.length === newMessages.length) {
            let isIdentical = true
            for (let i = 0; i < currentMessages.length; i++) {
              if (currentMessages[i].id !== newMessages[i]?.id) {
                isIdentical = false
                break
              }
            }
            
            if (isIdentical) {
              // 当前显示的消息与新消息完全相同，只更新缓存，不更新显示，避免闪烁
              this.messageCache[key] = [...newMessages]
              return
            }
          }
        }
        
        // 如果不在切换状态，或者消息不同，正常更新
        // 直接替换数组，Vue 会通过 key 来识别哪些元素需要更新
        this.messages = newMessages
        
        // 更新缓存
        this.messageCache[key] = [...newMessages]
        
        // 等待 DOM 更新
        await this.$nextTick()
        
        // 检查是否有图片消息，如果有则等待所有图片加载完成后再滚动
        const hasImages = newMessages.some(msg => msg.imageUrl)
        if (hasImages) {
          // 等待所有图片加载完成
          await this.waitForImagesLoad()
        }
        
        // 滚动到底部
        this.scrollToBottom()
      } catch (e) {
        console.error('加载消息失败', e)
        // 如果加载失败且当前用户ID没变化，保持现有消息（可能是缓存）
        if (this.currentChatUserId !== targetUserId) {
          // 如果用户已切换，清空消息
          this.messages = []
        }
      }
    },
    initWebSocket() {
      if (!this.userInfo || !this.userInfo.id) {
        console.error('用户ID不存在，无法建立WebSocket连接')
        return
      }
      
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsHost = process.env.NODE_ENV === 'development' ? 'localhost:8091' : window.location.host
      const wsUrl = `${wsProtocol}//${wsHost}/chat-api/websocket/chat/${this.userInfo.id}`
      
      console.log('连接WebSocket:', wsUrl)
      this.ws = new WebSocket(wsUrl)
      
      this.ws.onopen = () => {
        console.log('WebSocket连接成功')
        this.sendHeartbeat()
      }
      
      this.ws.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.type === 'chat_message') {
          // 如果是管理员，处理未读消息计数
          if (this.userInfo.admin === 1 && data.receiverId === this.userInfo.id) {
            // 先判断消息是否属于当前会话
            if (data.senderId === this.currentChatUserId) {
              // 这是当前会话的消息，清除未读计数（不增加）
              this.clearUnreadCountForUser(data.senderId)
            } else {
              // 消息不属于当前会话，增加未读计数
              const senderId = data.senderId.toString()
              // Vue 3 直接赋值即可，确保响应式
              if (!this.unreadCount[senderId]) {
                this.unreadCount[senderId] = 0
              }
              this.unreadCount[senderId] = (this.unreadCount[senderId] || 0) + 1
            }
          }
          
          if ((this.userInfo.admin === 1 && data.senderId === this.currentChatUserId) ||
              (this.userInfo.admin === 1 && data.receiverId === this.currentChatUserId) ||
              (this.userInfo.admin !== 1 && (data.senderId === this.currentChatUserId || data.receiverId === this.currentChatUserId))) {
            // 收到新消息时启用动画
            this.enableMessageAnimation = true
            // 确保消息对象有 isDelete 字段
            const messageData = { ...data, isDelete: data.isDelete !== undefined ? data.isDelete : 0 }
            this.messages.push(messageData)
            
            // 更新缓存
            const cacheKey = this.currentChatUserId.toString()
            if (!this.messageCache[cacheKey]) {
              this.messageCache[cacheKey] = []
            }
            this.messageCache[cacheKey].push(messageData)
            
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          } else if (this.userInfo.admin === 1) {
            // 如果是管理员，即使不是当前会话的消息，也要更新对应用户的缓存
            const senderId = data.senderId.toString()
            const receiverId = data.receiverId.toString()
            // 判断是哪个用户发送的，更新对应的缓存
            // 确保消息对象有 isDelete 字段
            const messageData = { ...data, isDelete: data.isDelete !== undefined ? data.isDelete : 0 }
            if (data.senderId === this.userInfo.id) {
              // 当前用户发送给其他用户的消息
              if (!this.messageCache[receiverId]) {
                this.messageCache[receiverId] = []
              }
              this.messageCache[receiverId].push(messageData)
            } else if (data.receiverId === this.userInfo.id) {
              // 其他用户发送给当前用户的消息
              if (!this.messageCache[senderId]) {
                this.messageCache[senderId] = []
              }
              this.messageCache[senderId].push(messageData)
            }
          }
        } else if (data.type === 'user_status') {
          const userId = data.userId
          const userIdNum = typeof userId === 'number' ? userId : parseInt(userId)
          // 记录之前的状态，用于检测状态变化（在更新状态之前记录）
          const previousStatus = this.onlineStatus[userIdNum]
          const wasOnline = previousStatus === true
          const wasOffline = previousStatus === false
          const wasUndefined = previousStatus === undefined
          // Vue 3 直接赋值即可，确保响应式
          this.onlineStatus[userIdNum] = data.isOnline
          if (data.isOnline) {
            // 如果上线了，清除离线时间
            delete this.offlineTime[userIdNum]
            delete this.offlineTime[userIdNum.toString()]
            // 如果是管理员，检查是否需要播放上线提示音
            // playOnlineAudio内部会检查Redis：如果用户离开时间超过10秒，才会播放
            if (this.userInfo.admin === 1) {
              // admin=1的用户不会对自己进行播报
              if (userIdNum !== this.userInfo.id) {
                // 如果用户当前是在线状态，都检查是否需要播放（后端会判断是否超过10秒）
                // 这样可以避免因为前端状态不同步导致的问题
                if (wasOffline) {
                  // 明确从离线变为在线，立即检查
                  console.log('✅ 检测到用户上线状态变化（从离线变为在线），检查是否需要播放提示音，userId:', userIdNum, 'previousStatus:', previousStatus, '当前状态:', data.isOnline)
                  // 清除播放标记，确保能立即检查
                  delete this.onlineAudioPlayed[userIdNum]
                  // 延迟一点调用，确保后端已经处理完上线逻辑
                  setTimeout(() => {
                    this.playOnlineAudio(userIdNum)
                  }, 200)
                } else if (wasUndefined && data.isOnline) {
                  // 如果之前状态未知，现在在线，也检查一下（可能是用户重新上线）
                  // 但只在用户确实在线时才检查，避免初始化时的误判
                  console.log('⚠️ 用户状态从未知变为在线，也检查是否需要播放提示音，userId:', userIdNum)
                  // 清除播放标记，确保能立即检查
                  delete this.onlineAudioPlayed[userIdNum]
                  // 延迟一点调用，确保后端已经处理完上线逻辑
                  setTimeout(() => {
                    this.playOnlineAudio(userIdNum)
                  }, 200)
                } else if (wasOnline) {
                  // 如果之前就在线，现在还在线，不检查（避免循环播放）
                  console.log('用户之前就在线，现在还在线，跳过检查，userId:', userIdNum)
                }
              } else {
                console.log('检测到管理员自己上线，不播放提示音，userId:', userIdNum)
              }
            }
          } else {
            // 如果下线了，记录离线时间，并清除播放标记（这样用户再次上线时可以播放）
            if (this.userInfo.admin === 1) {
              delete this.onlineAudioPlayed[userIdNum]
            }
            if (data.timestamp) {
              const seconds = Math.floor((Date.now() - data.timestamp) / 1000)
              // 同时使用数字和字符串作为key，Vue 3 直接赋值即可
              this.offlineTime[userIdNum] = { lastTime: data.timestamp, seconds: seconds }
              this.offlineTime[userIdNum.toString()] = { lastTime: data.timestamp, seconds: seconds }
            } else {
              this.checkOnlineStatus(userIdNum)
            }
          }
        } else if (data.type === 'pat_pat') {
          // 处理拍一拍消息
          console.log('收到拍一拍消息:', data)
          const senderId = data.senderId
          const receiverId = data.receiverId
          
          // 判断是否是当前会话的消息
          const isCurrentSession = (this.userInfo.admin === 1 && this.currentChatUserId && 
                                   (senderId === this.currentChatUserId || receiverId === this.currentChatUserId)) ||
                                  (this.userInfo.admin !== 1 && 
                                   ((senderId === this.userInfo.id && receiverId === (this.adminInfo?.id)) ||
                                    (senderId === (this.adminInfo?.id) && receiverId === this.userInfo.id)))
          
          if (isCurrentSession) {
            // 确保消息对象有 isDelete 字段
            const messageData = { ...data, isDelete: 2 }
            
            // 添加到消息列表
            this.messages.push(messageData)
            
            // 更新缓存
            const cacheKey = (this.userInfo.id === senderId ? receiverId : senderId).toString()
            if (!this.messageCache[cacheKey]) {
              this.messageCache[cacheKey] = []
            }
            this.messageCache[cacheKey].push(messageData)
            
            // 滚动到底部
            this.$nextTick(() => {
              this.scrollToBottom()
            })
            
            // 拍一拍头像抖动逻辑：
            // 1. 如果小葱拍了对方（senderId === this.userInfo.id），小葱看到聊天窗口中对方的头像（receiverId）抖动
            // 2. 如果小葱被对方拍了（receiverId === this.userInfo.id），小葱看到聊天窗口中小葱自己的头像（this.userInfo.id）抖动，并播放音频
            // 使用双重 $nextTick 确保消息已经渲染到DOM后再触发抖动
            this.$nextTick(() => {
              this.$nextTick(() => {
                // 统一类型比较
                const senderIdStr = String(senderId)
                const receiverIdStr = String(receiverId)
                const currentUserIdStr = String(this.userInfo.id)
                
                if (senderIdStr === currentUserIdStr) {
                  // 小葱拍了对方，小葱看到对方的头像抖动（消息列表中 senderId === receiverId 的消息头像）
                  console.log('小葱拍了对方，触发对方头像抖动，receiverId:', receiverId)
                  this.triggerAvatarShake(receiverId)
                } else if (receiverIdStr === currentUserIdStr) {
                  // 小葱被对方拍了，小葱看到小葱自己的头像抖动（消息列表中 senderId === this.userInfo.id 的消息头像），并播放音频
                  console.log('小葱被对方拍了，触发自己头像抖动，senderId:', senderId)
                  this.playPatPatAudio()
                  this.triggerAvatarShake(this.userInfo.id)
                }
              })
            })
          }
        } else if (data.type === 'message_recalled') {
          // 处理撤回消息通知
          console.log('收到撤回消息通知:', data)
          const messageId = data.messageId
          const senderId = data.senderId
          const receiverId = data.receiverId
          
          // 统一转换为数字进行比较，避免类型不匹配
          const messageIdNum = typeof messageId === 'string' ? parseInt(messageId) : messageId
          
          // 判断撤回的消息是否属于当前会话
          const isCurrentSession = (this.userInfo.admin === 1 && this.currentChatUserId && 
                                   (senderId === this.currentChatUserId || receiverId === this.currentChatUserId)) ||
                                  (this.userInfo.admin !== 1 && 
                                   ((senderId === this.userInfo.id && receiverId === (this.adminInfo?.id)) ||
                                    (senderId === (this.adminInfo?.id) && receiverId === this.userInfo.id)))
          
          // 确定缓存key
          const cacheKey = (this.userInfo.id === senderId ? receiverId : senderId).toString()
          
          // 更新缓存中的消息状态
          if (this.messageCache[cacheKey]) {
            const cachedMessageIndex = this.messageCache[cacheKey].findIndex(msg => {
              const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
              return msgId === messageIdNum
            })
            if (cachedMessageIndex !== -1) {
              // 创建新对象替换原对象，确保响应式更新（Vue 3 不需要 $set）
              const originalMsg = this.messageCache[cacheKey][cachedMessageIndex]
              const updatedMessage = { ...originalMsg, isDelete: 1 }
              this.messageCache[cacheKey][cachedMessageIndex] = updatedMessage
              console.log('已更新缓存中的消息状态为已撤回:', messageId, '原消息:', originalMsg, '更新后:', updatedMessage)
            }
          }
          
          // 更新当前消息列表中的消息状态 - 使用数组替换确保响应式更新
          const messageIndex = this.messages.findIndex(msg => {
            const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
            return msgId === messageIdNum
          })
          
          if (messageIndex !== -1) {
            // 创建新的消息数组，替换被撤回的消息
            const originalMsg = this.messages[messageIndex]
            const updatedMessages = this.messages.map((msg, index) => {
              if (index === messageIndex) {
                return { ...msg, isDelete: 1 }
              }
              return msg
            })
            // 直接替换整个数组，确保 Vue 检测到变化
            this.messages = updatedMessages
            console.log('已更新当前消息列表中的消息状态为已撤回:', messageId, '原消息:', originalMsg, '更新后:', this.messages[messageIndex])
            
            // 立即强制更新视图
            this.$nextTick(() => {
              this.$forceUpdate()
              console.log('已强制更新视图 - 撤回消息在当前列表')
            })
          } else if (isCurrentSession) {
            // 如果消息不在当前列表中，但属于当前会话，从缓存重新加载
            if (this.messageCache[cacheKey]) {
              this.messages = [...this.messageCache[cacheKey]]
              console.log('从缓存重新加载消息列表以显示撤回状态')
              this.$nextTick(() => {
                this.$forceUpdate()
              })
            }
          }
        }
      }
      
      this.ws.onerror = (error) => {
        console.error('WebSocket错误', error)
      }
      
      this.ws.onclose = (event) => {
        console.log('WebSocket连接关闭', event.code, event.reason)
        
        // 防止重复处理关闭事件
        if (this.wsCloseHandled) {
          console.log('WebSocket关闭事件已处理，跳过')
          return
        }
        
        // 如果关闭代码是1008（策略违规，可能包括认证失败）或其他认证相关错误
        if (event.code === 1008 || event.code === 1002 || event.code === 1003) {
          console.log('WebSocket连接因认证问题关闭，检查token后决定是否重定向')
          // 先检查token是否还存在，避免误判
          const token = localStorage.getItem('token')
          if (!token) {
            console.log('Token不存在，重定向到登录页')
            this.wsCloseHandled = true
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')
            // 使用路由跳转而不是直接修改location，避免页面刷新
            this.$router.push('/login').catch(err => {
              // 如果路由跳转失败，再使用location
              if (err.name !== 'NavigationDuplicated') {
                window.location.href = '/login'
              }
            })
          } else {
            console.log('Token存在，可能是临时网络问题，尝试重连')
            // Token存在，可能是临时问题，尝试重连而不是直接重定向
            setTimeout(() => {
              if (!this.wsCloseHandled) {
                this.initWebSocket()
              }
            }, 3000)
          }
          return
        }
        
        // 正常关闭（code 1000）或网络问题，尝试重连
        if (event.code === 1000 || event.code === 1001 || event.code === 1006) {
          console.log('WebSocket正常关闭或网络问题，尝试重连')
          setTimeout(() => {
            // 重连前检查token是否还存在
            const token = localStorage.getItem('token')
            if (!token) {
              console.log('Token不存在，不重连WebSocket，重定向到登录页')
              this.wsCloseHandled = true
              this.$router.push('/login').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  window.location.href = '/login'
                }
              })
              return
            }
            // 检查是否已经在重连中，避免重复重连
            if (!this.wsCloseHandled && this.ws?.readyState !== WebSocket.OPEN) {
              this.initWebSocket()
            }
          }, 3000)
        }
      }
    },
    sendMessage() {
      if (!this.inputMessage.trim()) return
      
      // 关闭表情面板
      this.closeEmojiPanel()
      
      // 如果是普通用户但没有管理员ID，尝试获取
      if (this.userInfo.admin !== 1 && !this.currentChatUserId) {
        this.loadAdminAndStartChat()
        // 等待一下，如果还是没有管理员，提示用户
        setTimeout(() => {
          if (!this.currentChatUserId) {
            this.showToastMessage('管理员尚未设置，无法发送消息', 'error')
            return
          }
        }, 500)
        return
      }
      
      // 管理员必须选择用户
      if (this.userInfo.admin === 1 && !this.currentChatUserId) {
        this.showToastMessage('请先选择聊天对象', 'error')
        return
      }
      
      if (!this.currentChatUserId) {
        this.showToastMessage('请先选择聊天对象', 'error')
        return
      }
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 发送消息时启用动画
        this.enableMessageAnimation = true
        this.ws.send(JSON.stringify({
          type: 'chat',
          receiverId: this.currentChatUserId,
          content: this.inputMessage
        }))
        this.inputMessage = ''
      } else {
        this.showToastMessage('连接未建立，请稍候再试', 'error')
      }
    },
    // 表情相关方法
    toggleEmojiPanel() {
      this.showEmojiPanel = !this.showEmojiPanel
      if (this.showEmojiPanel) {
        this.loadCustomEmojis()
      }
    },
    closeEmojiPanel() {
      this.showEmojiPanel = false
    },
    // 加载用户自定义表情
    async loadCustomEmojis() {
      try {
        const res = await getCustomEmojis()
        if (res && res.code === 0 && res.data) {
          this.customEmojis = res.data
        }
      } catch (e) {
        console.error('加载自定义表情失败', e)
      }
    },
    // 触发表情上传
    triggerEmojiUpload() {
      this.$refs.emojiInput.click()
    },
    // 处理表情上传
    async handleEmojiUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      if (!file.type.startsWith('image/')) {
        this.showToastMessage('请选择图片文件', 'error')
        return
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.showToastMessage('表情大小不能超过5MB', 'error')
        return
      }
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const res = await uploadCustomEmoji(formData)
        if (res && res.code === 0 && res.data) {
          this.showToastMessage('表情上传成功', 'success')
          // 重新加载表情列表
          await this.loadCustomEmojis()
          // 切换到自定义表情标签
          this.emojiTab = 'custom'
        } else {
          this.showToastMessage(res?.msg || '表情上传失败', 'error')
        }
      } catch (e) {
        console.error('上传表情失败', e)
        this.showToastMessage(e.message || '表情上传失败', 'error')
      } finally {
        // 清空input，允许重复选择同一文件
        event.target.value = ''
      }
    },
    // 删除自定义表情
    async deleteCustomEmoji(emojiId) {
      if (!confirm('确定要删除这个表情吗？')) {
        return
      }
      
      try {
        const res = await deleteCustomEmoji(emojiId)
        if (res && res.code === 0) {
          this.showToastMessage('删除成功', 'success')
          // 重新加载表情列表
          await this.loadCustomEmojis()
        } else {
          this.showToastMessage(res?.msg || '删除失败', 'error')
        }
      } catch (e) {
        console.error('删除表情失败', e)
        this.showToastMessage(e.message || '删除失败', 'error')
      }
    },
    // 发送自定义表情
    sendCustomEmoji(emoji) {
      if (!emoji || !emoji.emojiUrl) {
        return
      }
      
      // 检查是否有聊天对象
      if (this.userInfo.admin === 1 && !this.currentChatUserId) {
        this.showToastMessage('请先选择聊天对象', 'error')
        return
      }
      
      // 如果是普通用户但没有管理员ID，尝试获取
      if (this.userInfo.admin !== 1 && !this.currentChatUserId) {
        this.loadAdminAndStartChat()
        setTimeout(() => {
          if (!this.currentChatUserId) {
            this.showToastMessage('管理员尚未设置，无法发送消息', 'error')
            return
          }
          // 重试发送
          this.sendCustomEmoji(emoji)
        }, 500)
        return
      }
      
      // 通过WebSocket发送表情消息
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.enableMessageAnimation = true
        this.ws.send(JSON.stringify({
          type: 'chat',
          receiverId: this.currentChatUserId,
          content: '', // 表情消息内容为空
          emojiUrl: emoji.emojiUrl, // 表情URL
          isGif: emoji.isGif === 1 || emoji.isGif === true // 是否为动图
        }))
        
        // 关闭表情面板
        this.closeEmojiPanel()
      } else {
        this.showToastMessage('连接未建立，请稍候再试', 'error')
      }
    },
    insertEmoji(emoji) {
      // 获取输入框的引用
      const input = this.$refs.messageInput
      if (input) {
        // 获取当前光标位置
        const start = input.selectionStart || 0
        const end = input.selectionEnd || 0
        const text = this.inputMessage
        
        // 在光标位置插入表情
        this.inputMessage = text.substring(0, start) + emoji + text.substring(end)
        
        // 设置光标位置到插入表情之后
        this.$nextTick(() => {
          input.focus()
          input.setSelectionRange(start + emoji.length, start + emoji.length)
        })
      } else {
        // 如果没有引用，直接追加到末尾
        this.inputMessage += emoji
      }
    },
    handleClickOutside(event) {
      // 如果表情面板打开，且点击的不是表情按钮和表情面板，则关闭面板
      if (this.showEmojiPanel) {
        const emojiButton = event.target.closest('.emoji-button')
        const emojiPanel = event.target.closest('.emoji-panel')
        
        if (!emojiButton && !emojiPanel) {
          this.closeEmojiPanel()
        }
      }
    },
    // 图片相关方法
    selectImages() {
      // 触发文件选择
      this.$refs.imageInput.click()
    },
    async handleImageSelect(event) {
      const files = Array.from(event.target.files || [])
      
      if (files.length === 0) {
        event.target.value = ''
        return
      }
      
      // 验证文件类型
      const validFiles = []
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          this.showToastMessage(`${file.name} 不是图片文件`, 'error')
          continue
        }
        validFiles.push(file)
      }
      
      if (validFiles.length === 0) {
        event.target.value = ''
        return
      }
      
      // 单张照片：直接发送（不显示弹窗）
      if (validFiles.length === 1 && this.pendingImages.length === 0) {
        await this.uploadAndSendImage(validFiles[0])
        event.target.value = ''
        return
      }
      
      // 超过1张照片，或者已有待发送的图片：显示弹窗处理
      // 如果已有待发送的图片，合并到现有列表
      const currentCount = this.pendingImages.length
      const remainingSlots = 9 - currentCount
      
      if (validFiles.length > remainingSlots) {
        this.showToastMessage(`最多只能选择9张图片，当前已有${currentCount}张，只能再添加${remainingSlots}张`, 'error')
        validFiles.splice(remainingSlots)
      }
      
      // 添加到待发送列表
      this.pendingImages = [...this.pendingImages, ...validFiles]
      this.showImageUploadModal = true
      
      // 清空文件选择
      event.target.value = ''
    },
    // 图片上传弹窗相关方法
    closeImageUploadModal() {
      this.showImageUploadModal = false
      this.pendingImages = []
      this.modalMouseDownTarget = null
      this.modalMouseDownPosition = null
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      // 清理事件监听器
      document.removeEventListener('mousemove', this.handleModalMouseMove)
    },
    removePendingImage(index) {
      this.pendingImages.splice(index, 1)
    },
    addMoreImages() {
      if (this.pendingImages.length >= 9) {
        this.showToastMessage('最多只能选择9张图片', 'error')
        return
      }
      this.$refs.imageInput.click()
    },
    getImagePreview(file) {
      if (!file) return ''
      return URL.createObjectURL(file)
    },
    previewPendingImage(index) {
      this.previewImageIndex = index
    },
    async sendPendingImages() {
      if (this.pendingImages.length === 0) {
        this.showToastMessage('请至少选择一张图片', 'error')
        return
      }
      
      // 检查是否有聊天对象
      if (this.userInfo.admin === 1 && !this.currentChatUserId) {
        this.showToastMessage('请先选择聊天对象', 'error')
        return
      }
      
      if (!this.currentChatUserId) {
        if (this.userInfo.admin !== 1) {
          await this.loadAdminAndStartChat()
          if (!this.currentChatUserId) {
            this.showToastMessage('管理员尚未设置，无法发送消息', 'error')
            return
          }
        } else {
          this.showToastMessage('请先选择聊天对象', 'error')
          return
        }
      }
      
      // 上传并发送每张图片
      for (const file of this.pendingImages) {
        await this.uploadAndSendImage(file)
      }
      
      // 关闭弹窗并清空列表
      this.closeImageUploadModal()
    },
    async uploadAndSendImage(file) {
      try {
        // 检查是否有聊天对象
        if (this.userInfo.admin === 1 && !this.currentChatUserId) {
          this.showToastMessage('请先选择聊天对象', 'error')
          return
        }
        
        if (!this.currentChatUserId) {
          if (this.userInfo.admin !== 1) {
            await this.loadAdminAndStartChat()
            if (!this.currentChatUserId) {
              this.showToastMessage('管理员尚未设置，无法发送消息', 'error')
              return
            }
          } else {
            this.showToastMessage('请先选择聊天对象', 'error')
            return
          }
        }
        
        // 创建FormData
        const formData = new FormData()
        formData.append('file', file)
        
        // 上传图片
        const res = await uploadImage(formData)
        if (res.code !== 0) {
          this.showToastMessage(res.msg || '图片上传失败', 'error')
          return
        }
        
        const imageUrl = res.data.url
        
        // 通过WebSocket发送图片消息
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.enableMessageAnimation = true
          this.ws.send(JSON.stringify({
            type: 'chat',
            receiverId: this.currentChatUserId,
            content: '', // 图片消息内容为空
            imageUrl: imageUrl // 图片URL
          }))
        } else {
          this.showToastMessage('连接未建立，请稍候再试', 'error')
        }
      } catch (error) {
        console.error('上传图片失败', error)
        this.showToastMessage('图片上传失败', 'error')
      }
    },
    previewImage(imageUrl) {
      this.imagePreviewUrl = imageUrl
      this.showImagePreview = true
      // 重置缩放和偏移
      this.imageScale = 1
      this.imageTranslateX = 0
      this.imageTranslateY = 0
      // 重置拖拽状态
      this.isDraggingImage = false
      this.hasDragged = false
    },
    closeImagePreview() {
      this.showImagePreview = false
      // 重置缩放和偏移
      this.imageScale = 1
      this.imageTranslateX = 0
      this.imageTranslateY = 0
      // 重置拖拽状态
      this.isDraggingImage = false
      // 延迟清空URL，等待动画完成
      setTimeout(() => {
        this.imagePreviewUrl = ''
      }, 300)
    },
    handleImagePreviewMouseDown(event) {
      // 只有左键按下时才开始拖拽
      if (event.button !== 0) return
      
      event.preventDefault()
      event.stopPropagation()
      
      // 记录拖拽开始时的状态
      this.isDraggingImage = true
      this.hasDragged = false
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.dragStartTranslateX = this.imageTranslateX
      this.dragStartTranslateY = this.imageTranslateY
    },
    handleImagePreviewMouseMove(event) {
      // 记录鼠标位置（相对于overlay）
      const overlay = event.currentTarget
      const rect = overlay.getBoundingClientRect()
      this.imagePreviewMouseX = event.clientX - rect.left
      this.imagePreviewMouseY = event.clientY - rect.top
      
      // 如果正在拖拽，更新图片位置
      if (this.isDraggingImage) {
        event.preventDefault()
        // 计算鼠标移动的距离
        const deltaX = event.clientX - this.dragStartX
        const deltaY = event.clientY - this.dragStartY
        
        // 如果移动距离超过阈值，标记为拖拽
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          this.hasDragged = true
        }
        
        // 更新图片偏移量
        this.imageTranslateX = this.dragStartTranslateX + deltaX / this.imageScale
        this.imageTranslateY = this.dragStartTranslateY + deltaY / this.imageScale
      }
    },
    handleImagePreviewMouseUp(event) {
      // 结束拖拽
      if (this.isDraggingImage) {
        this.isDraggingImage = false
      }
    },
    handleImagePreviewClick(event) {
      // 如果刚刚进行了拖拽，不关闭预览
      if (this.hasDragged) {
        this.hasDragged = false
        return
      }
      // 否则关闭预览
      this.closeImagePreview()
    },
    handleImagePreviewWheel(event) {
      event.preventDefault()
      
      // 获取图片元素
      const img = event.currentTarget.querySelector('.image-preview-content')
      if (!img) return
      
      // 获取overlay和图片的位置信息
      const overlay = event.currentTarget
      const overlayRect = overlay.getBoundingClientRect()
      const imgRect = img.getBoundingClientRect()
      
      // 鼠标相对于overlay的位置
      const mouseX = event.clientX - overlayRect.left
      const mouseY = event.clientY - overlayRect.top
      
      // 计算缩放增量（滚轮向上放大，向下缩小）
      const delta = event.deltaY > 0 ? -0.15 : 0.15
      const oldScale = this.imageScale
      const newScale = Math.max(0.5, Math.min(5, oldScale + delta))
      
      // 如果缩放没有变化，直接返回
      if (oldScale === newScale) return
      
      // 计算overlay中心点
      const overlayCenterX = overlayRect.width / 2
      const overlayCenterY = overlayRect.height / 2
      
      // 鼠标相对于overlay中心的位置
      const mouseOffsetX = mouseX - overlayCenterX
      const mouseOffsetY = mouseY - overlayCenterY
      
      // 计算缩放比例变化
      const scaleRatio = newScale / oldScale
      
      // 计算新的偏移量，使鼠标位置在缩放后保持不变
      // 公式：newTranslate = oldTranslate * scaleRatio + (1 - scaleRatio) * mouseOffset
      const newTranslateX = this.imageTranslateX * scaleRatio + (1 - scaleRatio) * mouseOffsetX
      const newTranslateY = this.imageTranslateY * scaleRatio + (1 - scaleRatio) * mouseOffsetY
      
      // 更新缩放和偏移
      this.imageScale = newScale
      this.imageTranslateX = newTranslateX
      this.imageTranslateY = newTranslateY
    },
    sendHeartbeat() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'heartbeat' }))
        setTimeout(() => this.sendHeartbeat(), 30000)
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    // 等待所有图片加载完成
    async waitForImagesLoad() {
      return new Promise((resolve) => {
        // 等待DOM更新
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer
          if (!container) {
            resolve()
            return
          }
          
          // 查找所有消息中的图片元素
          const images = container.querySelectorAll('.message-image')
          
          if (images.length === 0) {
            // 没有图片，直接resolve
            resolve()
            return
          }
          
          let loadedCount = 0
          let errorCount = 0
          const totalImages = images.length
          
          // 如果所有图片已经加载完成（complete属性为true），直接resolve
          let alreadyLoadedCount = 0
          images.forEach(img => {
            if (img.complete && img.naturalHeight !== 0) {
              alreadyLoadedCount++
            }
          })
          
          if (alreadyLoadedCount === totalImages) {
            // 所有图片已经加载完成
            resolve()
            return
          }
          
          // 设置超时，避免某些图片加载失败导致一直等待
          const timeout = setTimeout(() => {
            console.log('等待图片加载超时，继续滚动')
            resolve()
          }, 5000) // 5秒超时
          
          // 为每个图片添加加载监听器
          const checkComplete = () => {
            if (loadedCount + errorCount >= totalImages) {
              clearTimeout(timeout)
              resolve()
            }
          }
          
          images.forEach((img) => {
            // 如果图片已经加载完成，直接计数
            if (img.complete && img.naturalHeight !== 0) {
              loadedCount++
            } else {
              // 添加加载成功监听器
              img.addEventListener('load', () => {
                loadedCount++
                checkComplete()
              }, { once: true })
              
              // 添加加载失败监听器
              img.addEventListener('error', () => {
                errorCount++
                checkComplete()
              }, { once: true })
            }
          })
          
          // 如果所有图片都已经加载完成，立即resolve
          checkComplete()
        })
      })
    },
    // 获取用户名的首字母（支持中文拼音首字母）
    getFirstLetter(name) {
      if (!name || name.trim() === '') {
        return '#'
      }
      
      const firstChar = name.trim().charAt(0)
      
      // 如果是英文字母
      if (/[a-zA-Z]/.test(firstChar)) {
        return firstChar.toUpperCase()
      }
      
      // 如果是中文字符，使用pinyin-pro获取拼音首字母
      if (/[\u4e00-\u9fa5]/.test(firstChar)) {
        try {
          // 使用pinyin-pro获取拼音首字母
          const pinyinStr = pinyin(firstChar, { pattern: 'first', toneType: 'none' })
          if (pinyinStr && pinyinStr.length > 0) {
            return pinyinStr.toUpperCase()
          }
        } catch (e) {
          console.warn('拼音转换失败:', e)
        }
        // 如果转换失败，返回#
        return '#'
      }
      
      // 其他字符（数字、特殊符号等）归类到#
      return '#'
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      if (minutes < 1) return '刚刚'
      if (minutes < 60) return `${minutes}分钟前`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}小时前`
      const days = Math.floor(hours / 24)
      if (days < 30) return `${days}天前`
      const months = Math.floor(days / 30)
      return `${months}个月前`
    },
    getDisplayName(user) {
      if (!user) return ''
      const alias = this.userAliases[user.id]
      if (alias) {
        return `${user.nickname || '未设置昵称'}(${alias})`
      }
      return user.nickname || '未设置昵称'
    },
    // 判断消息是否已撤回
    isMessageRecalled(msg) {
      return msg.isDelete === 1 || msg.isDelete === '1' || msg.isDelete === true
    },
    // 判断是否是拍一拍消息
    isMessagePatPat(msg) {
      return (msg.isDelete === 2 || msg.isDelete === '2') || (msg.content === '__PAT__')
    },
    getSenderName(msg) {
      if (msg.senderId === this.userInfo.id) {
        return this.userInfo.nickname || '小葱'
      }
      if (this.userInfo.admin === 1) {
        const user = this.userList.find(u => u.id === msg.senderId)
        if (user) {
          const alias = this.userAliases[user.id]
          if (alias) {
            return `${user.nickname}(${alias})`
          }
          return user.nickname
        }
        return '用户'
      } else {
        if (this.adminInfo) {
          const alias = this.userAliases[this.adminInfo.id]
          if (alias) {
            return `${this.adminInfo.nickname}(${alias})`
          }
          return this.adminInfo.nickname
        }
        return '管理员'
      }
    },
    // 加载别称
    async loadAlias(targetUserId) {
      if (!targetUserId) return
      try {
        const res = await getAlias(targetUserId)
        if (res && res.code === 0 && res.data) {
          // Vue 3 不需要 $set，直接赋值即可
          this.userAliases[targetUserId] = res.data.alias || null
        }
      } catch (e) {
        console.error('加载别称失败', e)
      }
    },
    // 打开设置别称弹窗
    openAliasModal() {
      this.showAvatarMenu = false
      const targetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
      if (!targetUserId) {
        this.showToastMessage('无法设置别称', 'error')
        return
      }
      this.aliasForm.alias = this.userAliases[targetUserId] || ''
      this.showAliasModal = true
    },
    // 关闭设置别称弹窗
    closeAliasModal() {
      this.showAliasModal = false
      this.aliasForm.alias = ''
      this.aliasModalMouseDownTarget = null
      this.modalMouseDownTarget = null
      this.modalMouseDownPosition = null
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      // 清理事件监听器
      document.removeEventListener('mousemove', this.handleModalMouseMove)
    },
    // 处理设置别称弹窗鼠标按下事件
    handleAliasModalMouseDown(event) {
      // 记录鼠标按下时的目标元素和位置
      this.aliasModalMouseDownTarget = event.target
      this.modalMouseDownTarget = event.target
      this.modalMouseDownPosition = { x: event.clientX, y: event.clientY }
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      
      // 添加鼠标移动监听器，检测拖拽
      document.addEventListener('mousemove', this.handleModalMouseMove)
      // 添加鼠标抬起监听器，清理状态
      document.addEventListener('mouseup', this.handleModalMouseUp, { once: true })
    },
    // 处理设置别称弹窗点击事件
    handleAliasModalClick(event) {
      if (this.shouldPreventModalClose(event, this.aliasModalMouseDownTarget)) {
        return
      }
      this.closeAliasModal()
    },
    // 处理鼠标移动事件（检测拖拽）
    handleModalMouseMove(event) {
      if (this.modalMouseDownPosition) {
        const deltaX = Math.abs(event.clientX - this.modalMouseDownPosition.x)
        const deltaY = Math.abs(event.clientY - this.modalMouseDownPosition.y)
        // 如果鼠标移动超过5像素，认为是拖拽操作
        if (deltaX > 5 || deltaY > 5) {
          this.modalMouseMoved = true
        }
      }
    },
    // 处理鼠标抬起事件
    handleModalMouseUp(event) {
      // 移除鼠标移动监听器
      document.removeEventListener('mousemove', this.handleModalMouseMove)
      
      // 在 mouseup 时检查是否应该阻止关闭
      // 这样可以确保即使用户在文本框外松开鼠标，也不会关闭弹窗
      if (this.modalMouseDownTarget) {
        const target = this.modalMouseDownTarget
        const isInputElement = target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.closest('input') ||
          target.closest('textarea') ||
          target.closest('.form-input') ||
          target.closest('.input-with-count') ||
          target.closest('.profile-form-input') ||
          target.closest('.email-change-input') ||
          target.closest('.phone-change-input')
        )
        
        // 如果鼠标按下时在输入框内，或者发生了拖拽，设置标志阻止关闭
        this.shouldPreventClose = isInputElement || this.modalMouseMoved
        
        // 延迟重置标志，确保 click 事件能检查到
        setTimeout(() => {
          this.shouldPreventClose = false
        }, 100)
      }
    },
    // 通用方法：检查是否应该阻止弹窗关闭（用于处理拖拽选择文本的情况）
    shouldPreventModalClose(event, mouseDownTarget) {
      // 首先检查 shouldPreventClose 标志（在 mouseup 时设置）
      if (this.shouldPreventClose) {
        return true
      }
      
      // 如果鼠标按下时的目标不存在，允许关闭
      if (!mouseDownTarget) {
        return false
      }
      
      // 检查鼠标按下时是否在输入框内
      const target = mouseDownTarget
      const isInputElement = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('.form-input') ||
        target.closest('.input-with-count') ||
        target.closest('.profile-form-input') ||
        target.closest('.email-change-input') ||
        target.closest('.phone-change-input')
      )
      
      // 检查是否有文本被选中
      const hasTextSelection = window.getSelection && window.getSelection().toString().length > 0
      
      // 如果鼠标按下时在输入框内，或者有文本被选中，或者鼠标移动过（拖拽），阻止关闭
      if (isInputElement || hasTextSelection || this.modalMouseMoved) {
        return true
      }
      
      return false
    },
    // 通用方法：处理弹窗鼠标按下事件
    handleModalMouseDown(event) {
      this.modalMouseDownTarget = event.target
      this.modalMouseDownPosition = { x: event.clientX, y: event.clientY }
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      
      // 添加鼠标移动监听器，检测拖拽
      document.addEventListener('mousemove', this.handleModalMouseMove)
      // 添加鼠标抬起监听器，清理状态
      document.addEventListener('mouseup', this.handleModalMouseUp, { once: true })
    },
    // 通用方法：处理弹窗点击事件（用于个人信息、邮箱变更、手机号变更等弹窗）
    handleModalClick(event, closeCallback) {
      if (this.shouldPreventModalClose(event, this.modalMouseDownTarget)) {
        return
      }
      if (closeCallback) {
        closeCallback()
      }
    },
    // 处理右键菜单
    handleContextMenu(event) {
      // 获取右键点击的目标元素
      const target = event.target
      
      // 根据不同的目标元素设置不同的菜单选项
      let menuItems = []
      
      // 检查是否点击在消息气泡上（包括图片）
      const isBubble = target.closest('.message-bubble')
      const isImage = target.closest('.message-image')
      const isEmoji = target.closest('.custom-emoji-display') || target.closest('.message-emoji')
      const isMessageItem = target.closest('.message-item')
      const isChatMessages = target.closest('.chat-messages')
      
      // 检查是否是撤回消息（撤回消息不应该显示复制菜单）
      const isRecallNotice = target.closest('.recall-notice')
      
      // 如果点击在表情上，显示表情相关菜单
      if (isEmoji && !isRecallNotice) {
        event.preventDefault()
        event.stopPropagation()
        
        console.log('右键菜单事件触发 - 表情', target)
        
        const messageItem = target.closest('.message-item')
        if (messageItem) {
          const senderId = messageItem.dataset.senderId || messageItem.getAttribute('data-sender-id')
          const messageId = messageItem.dataset.messageId || messageItem.getAttribute('data-message-id')
          const currentUserId = this.userInfo?.id || this.userInfo?.userId
          
          // 获取表情URL和是否为动图
          const emojiImg = messageItem.querySelector('.custom-emoji-display')
          const emojiUrl = emojiImg ? emojiImg.getAttribute('src') : null
          
          // 从消息数据中获取isGif信息（更准确）
          let isGif = 0
          if (messageId) {
            const messageIdNum = typeof messageId === 'string' ? parseInt(messageId) : messageId
            const message = this.messages.find(msg => {
              const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
              return msgId === messageIdNum
            })
            if (message && message.emojiUrl === emojiUrl) {
              isGif = message.isGif === 1 || message.isGif === true ? 1 : 0
            } else {
              // 降级方案：从class判断
              isGif = emojiImg && emojiImg.classList.contains('gif') ? 1 : 0
            }
          } else {
            // 降级方案：从class判断
            isGif = emojiImg && emojiImg.classList.contains('gif') ? 1 : 0
          }
          
          // 如果不是自己发送的表情，显示"保存表情包"选项
          if (emojiUrl && String(senderId) !== String(currentUserId)) {
            menuItems.push({ 
              label: '保存表情包', 
              icon: '💾', 
              action: 'saveEmoji',
              emojiUrl: emojiUrl,
              isGif: isGif
            })
          }
        }
      }
      // 如果点击在消息气泡或图片上（且不是撤回消息），显示消息相关菜单
      else if ((isBubble || isImage) && !isRecallNotice) {
        // 精准控制：只有点击在消息气泡或图片上才显示复制和撤回（排除撤回消息）
        event.preventDefault()
        event.stopPropagation()
        
        console.log('右键菜单事件触发 - 消息气泡', target)
        
        const messageItem = target.closest('.message-item')
        
        // 确保不是撤回的消息
        if (messageItem && !messageItem.querySelector('.recall-notice')) {
          // 尝试多种方式获取 data 属性
          const messageId = messageItem.dataset.messageId || messageItem.getAttribute('data-message-id')
          const messageTime = messageItem.dataset.messageTime || messageItem.getAttribute('data-message-time')
          const senderId = messageItem.dataset.senderId || messageItem.getAttribute('data-sender-id')
          
          // 检查是否是图片消息
          const messageBubble = messageItem.querySelector('.message-bubble')
          const imageElement = messageBubble ? messageBubble.querySelector('.message-image') : null
          const imageUrl = imageElement ? imageElement.getAttribute('src') : null
          
          // 如果是图片消息，添加下载图片选项
          if (imageUrl) {
            menuItems.push({ 
              label: '下载图片', 
              icon: '💾', 
              action: 'downloadImage',
              imageUrl: imageUrl // 保存图片URL
            })
            // 如果有文本内容，也显示复制选项
            const textSpan = messageBubble ? messageBubble.querySelector('span') : null
            if (textSpan && textSpan.textContent && textSpan.textContent.trim()) {
              menuItems.push({ 
                label: '复制', 
                icon: '📋', 
                action: 'copy',
                messageItem: messageItem,
                messageId: messageId
              })
            }
          } else {
            // 非图片消息，显示复制选项
            menuItems.push({ 
              label: '复制', 
              icon: '📋', 
              action: 'copy',
              messageItem: messageItem, // 直接保存消息项的引用
              messageId: messageId // 也保存消息ID作为备用
            })
          }
          
          // 只允许撤回2分钟内的消息，且只能撤回自己发送的消息
          if (messageId && messageTime && senderId) {
            const messageTimestamp = parseInt(messageTime)
            const currentTimestamp = Date.now()
            const timeDiff = currentTimestamp - messageTimestamp
            const twoMinutes = 2 * 60 * 1000 // 2分钟（毫秒）
            
            // 检查是否是当前用户发送的消息，且在2分钟内
            const currentUserId = this.userInfo?.id || this.userInfo?.userId
            
            if (String(senderId) === String(currentUserId) && timeDiff <= twoMinutes) {
              menuItems.push({ label: '撤回', icon: '↩️', action: 'recall', messageId: messageId })
            }
          }
        }
      }
      // 如果点击在聊天消息区域，但不在消息气泡上（包括空白处、时间戳、头像、撤回提示等）
      else if (isChatMessages) {
        // 在消息区域但不在消息气泡上，显示刷新和主题切换
        event.preventDefault()
        event.stopPropagation()
        
        console.log('右键菜单事件触发 - 消息区域（非气泡）', target)
        
        menuItems.push({ label: '刷新', icon: '🔄', action: 'refresh' })
        menuItems.push({ divider: true })
        menuItems.push({ 
          label: this.isDarkMode ? '切换到白天模式' : '切换到夜间模式', 
          icon: this.isDarkMode ? '☀️' : '🌙', 
          action: 'toggleTheme' 
        })
      }
      // 检查是否点击在用户列表项
      else if (target.closest('.user-item')) {
        event.preventDefault()
        event.stopPropagation()
        
        const userItem = target.closest('.user-item')
        menuItems = [
          { label: '查看资料', icon: '👤', action: 'viewProfile' }
        ]
      }
      // 检查是否点击在输入框
      else if (target.tagName === 'INPUT' || target.closest('input')) {
        event.preventDefault()
        event.stopPropagation()
        
        menuItems = [
          { label: '粘贴', icon: '📋', action: 'paste', shortcut: 'Ctrl+V' },
          { label: '全选', icon: '✓', action: 'selectAll', shortcut: 'Ctrl+A' },
          { divider: true },
          { label: '清空', icon: '🗑️', action: 'clear' }
        ]
      }
      // 检查是否点击在聊天容器内的空白处（用户列表空白、聊天面板空白等）
      else if (target.closest('.chat-container') || target.closest('.chat-wrapper') || 
               target.closest('.user-list-panel') || target.closest('.chat-panel') ||
               target.closest('.panel-header') || target.closest('.chat-header')) {
        // 排除已经处理过的元素
        const isProcessed = target.closest('.message-item') || 
                           target.closest('.user-item') || 
                           target.closest('input') ||
                           target.closest('button') ||
                           target.closest('.theme-toggle-btn')
        
        if (!isProcessed) {
          event.preventDefault()
          event.stopPropagation()
          
          console.log('右键菜单事件触发 - 空白区域', target)
          
          menuItems.push({ label: '刷新', icon: '🔄', action: 'refresh' })
          menuItems.push({ divider: true })
          menuItems.push({ 
            label: this.isDarkMode ? '切换到白天模式' : '切换到夜间模式', 
            icon: this.isDarkMode ? '☀️' : '🌙', 
            action: 'toggleTheme' 
          })
        } else {
          // 已处理的元素，不显示菜单
          return
        }
      } else {
        // 其他区域不处理，不阻止默认行为
        return
      }
      
      if (menuItems.length > 0) {
        // 设置菜单位置
        this.contextMenuPosition = {
          x: event.clientX,
          y: event.clientY
        }
        
        // 设置菜单选项和目标
        this.contextMenuItems = menuItems
        this.contextMenuTarget = target
        this.showContextMenu = true
        this.contextMenuShowTime = Date.now() // 记录显示时间
        
        // 确保菜单不会超出屏幕
        this.$nextTick(() => {
          this.adjustContextMenuPosition()
        })
      }
    },
    // 调整右键菜单位置，确保不超出屏幕
    adjustContextMenuPosition() {
      const menu = this.$el.querySelector('.context-menu')
      if (!menu) return
      
      const rect = menu.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      
      let x = this.contextMenuPosition.x
      let y = this.contextMenuPosition.y
      
      // 如果菜单超出右边界，向左移动
      if (x + rect.width > windowWidth) {
        x = windowWidth - rect.width - 10
      }
      
      // 如果菜单超出下边界，向上移动
      if (y + rect.height > windowHeight) {
        y = windowHeight - rect.height - 10
      }
      
      // 确保不超出左边界和上边界
      x = Math.max(10, x)
      y = Math.max(10, y)
      
      this.contextMenuPosition = { x, y }
    },
    // 处理鼠标按下事件（用于关闭右键菜单）
    handleMouseDown(event) {
      // 如果右键菜单没有显示，直接返回
      if (!this.showContextMenu) {
        return
      }
      
      // 防止在菜单刚显示时立即关闭（给一个小的延迟，比如100ms）
      const timeSinceShow = Date.now() - this.contextMenuShowTime
      if (timeSinceShow < 100) {
        console.log('菜单刚显示，忽略鼠标按下事件', timeSinceShow)
        return
      }
      
      // 检查是否点击在菜单内部
      const menu = this.$el?.querySelector('.context-menu')
      if (menu && menu.contains(event.target)) {
        // 点击在菜单内部，不关闭
        return
      }
      
      // 检查是否是右键点击（右键点击不应该关闭菜单）
      if (event.button === 2) {
        return
      }
      
      console.log('关闭右键菜单（鼠标按下）')
      this.showContextMenu = false
      this.contextMenuItems = []
      this.contextMenuTarget = null
    },
    // 关闭右键菜单
    closeContextMenu(event) {
      // 如果右键菜单没有显示，直接返回
      if (!this.showContextMenu) {
        return
      }
      
      // 如果点击事件存在，检查是否点击在菜单内部
      if (event) {
        const menu = this.$el?.querySelector('.context-menu')
        if (menu && menu.contains(event.target)) {
          // 点击在菜单内部，不关闭
          return
        }
      }
      
      console.log('关闭右键菜单')
      this.showContextMenu = false
      this.contextMenuItems = []
      this.contextMenuTarget = null
    },
    // 处理全局右键菜单的操作（来自全局事件监听器）
    handleContextMenuAction(action, target) {
      console.log('处理右键菜单操作:', action, target)
      this.contextMenuTarget = target
      
      // 根据不同的操作执行不同的逻辑
      switch (action) {
        case 'paste':
          this.handlePasteAction(target)
          break
        case 'selectAll':
          this.handleSelectAllAction(target)
          break
        case 'clear':
          this.handleClearAction(target)
          break
        case 'toggleTheme':
          this.toggleDarkMode()
          break
        default:
          console.log('未定义的操作:', action)
      }
    },
    // 处理右键菜单项点击（Vue 组件内的菜单）
    handleContextMenuClick(item) {
      if (item.disabled || item.divider) {
        return
      }
      
      this.closeContextMenu()
      
      // 根据不同的操作执行不同的逻辑
      switch (item.action) {
        case 'copy':
          this.handleCopyAction(item)
          break
        case 'downloadImage':
          this.handleDownloadImage(item.imageUrl)
          break
        case 'recall':
          this.handleRecallAction(item.messageId)
          break
        case 'refresh':
          this.handleRefreshAction()
          break
        case 'toggleTheme':
          this.toggleDarkMode()
          break
        case 'viewProfile':
          this.handleViewProfileAction()
          break
        case 'paste':
          this.handlePasteAction()
          break
        case 'selectAll':
          this.handleSelectAllAction()
          break
        case 'clear':
          this.handleClearAction()
          break
        case 'saveEmoji':
          this.handleSaveEmojiAction(item.emojiUrl, item.isGif)
          break
        default:
          console.log('未定义的操作:', item.action)
      }
    },
    // 复制操作
    handleCopyAction(item) {
      // 优先从菜单项中获取保存的消息项引用
      let messageItem = null
      
      if (item && item.messageItem) {
        messageItem = item.messageItem
      } else if (this.contextMenuTarget) {
        // 降级方案：从 contextMenuTarget 查找
        messageItem = this.contextMenuTarget.closest('.message-item')
      }
      
      // 如果还是找不到，尝试从当前显示的消息中查找
      if (!messageItem) {
        // 通过消息ID查找（如果有）
        const messageId = item?.messageId
        if (messageId) {
          const messageElement = this.$el.querySelector(`[data-message-id="${messageId}"]`)
          if (messageElement) {
            messageItem = messageElement
          }
        }
      }
      
      // 最后的降级方案：从DOM中查找最近的消息项
      if (!messageItem && this.contextMenuPosition) {
        const elements = document.elementsFromPoint(this.contextMenuPosition.x, this.contextMenuPosition.y)
        for (const el of elements) {
          const msgItem = el.closest('.message-item')
          if (msgItem) {
            messageItem = msgItem
            break
          }
        }
      }
      
      if (!messageItem) {
        console.error('无法找到消息项', { item, contextMenuTarget: this.contextMenuTarget, position: this.contextMenuPosition })
        this.showToastMessage('无法找到消息内容', 'error')
        return
      }
      
      // 获取消息气泡
      const messageBubble = messageItem.querySelector('.message-bubble')
      if (!messageBubble) {
        // 检查是否是撤回消息
        const recallNotice = messageItem.querySelector('.recall-notice')
        if (recallNotice) {
          this.showToastMessage('撤回的消息无法复制', 'info')
          return
        }
        console.error('无法找到消息气泡', messageItem)
        this.showToastMessage('无法找到消息内容', 'error')
        return
      }
      
      // 检查是否是图片消息
      const imageElement = messageBubble.querySelector('.message-image')
      if (imageElement) {
        // 如果是图片消息，检查是否有文本内容
        const textSpan = messageBubble.querySelector('span')
        if (!textSpan || !textSpan.textContent || !textSpan.textContent.trim()) {
          this.showToastMessage('图片无法复制，请右键保存图片', 'info')
          return
        }
      }
      
      // 获取消息文本内容
      // 方法1: 直接从 span 获取（如果有）
      let messageText = ''
      const textSpan = messageBubble.querySelector('span')
      if (textSpan) {
        messageText = textSpan.textContent || textSpan.innerText || ''
      }
      
      // 方法2: 如果方法1失败，从整个气泡获取文本（排除图片）
      if (!messageText || !messageText.trim()) {
        // 克隆气泡元素，移除图片，然后获取文本
        const clone = messageBubble.cloneNode(true)
        const images = clone.querySelectorAll('.message-image')
        images.forEach(img => img.remove())
        messageText = clone.textContent || clone.innerText || ''
      }
      
      // 方法3: 如果还是失败，直接从气泡获取所有文本
      if (!messageText || !messageText.trim()) {
        messageText = messageBubble.textContent || messageBubble.innerText || ''
      }
      
      // 清理文本（去除多余空白）
      messageText = messageText.trim()
      
      if (messageText) {
        navigator.clipboard.writeText(messageText).then(() => {
          this.showToastMessage('已复制到剪贴板', 'success')
        }).catch((err) => {
          console.error('复制失败:', err)
          // 降级方案：使用传统方法
          try {
            const textArea = document.createElement('textarea')
            textArea.value = messageText
            textArea.style.position = 'fixed'
            textArea.style.opacity = '0'
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            this.showToastMessage('已复制到剪贴板', 'success')
          } catch (e) {
            this.showToastMessage('复制失败，请手动复制', 'error')
          }
        })
      } else {
        this.showToastMessage('没有可复制的内容', 'info')
      }
    },
    // 撤回操作
    async handleRecallAction(messageId) {
      if (!messageId) {
        this.showToastMessage('无法撤回消息', 'error')
        return
      }
      
      // 统一转换为数字进行比较，避免类型不匹配
      const messageIdNum = typeof messageId === 'string' ? parseInt(messageId) : messageId
      
      // 先找到要撤回的消息，确定缓存key
      let targetCacheKey = null
      let messageIndex = this.messages.findIndex(msg => {
        const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
        return msgId === messageIdNum
      })
      if (messageIndex !== -1) {
        const msg = this.messages[messageIndex]
        targetCacheKey = (this.userInfo.id === msg.senderId ? msg.receiverId : msg.senderId).toString()
      } else {
        // 如果不在当前列表，从缓存中查找
        for (const cacheKey in this.messageCache) {
          const cachedMessageIndex = this.messageCache[cacheKey].findIndex(msg => {
            const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
            return msgId === messageIdNum
          })
          if (cachedMessageIndex !== -1) {
            targetCacheKey = cacheKey
            break
          }
        }
      }
      
      try {
        // 先立即更新UI，提供即时反馈
        if (messageIndex !== -1) {
          // 创建新的消息数组，替换被撤回的消息
          const updatedMessages = this.messages.map((msg, index) => {
            if (index === messageIndex) {
              return { ...msg, isDelete: 1 }
            }
            return msg
          })
          // 直接替换整个数组，确保 Vue 检测到变化
          this.messages = updatedMessages
        }
        
        const res = await recallMessage(messageId)
        if (res && res.code === 0) {
          this.showToastMessage('消息已撤回', 'success')
          
          // 确保消息状态已更新
          if (messageIndex !== -1 && this.messages[messageIndex].isDelete !== 1) {
            const updatedMessages = this.messages.map((msg, index) => {
              if (index === messageIndex) {
                return { ...msg, isDelete: 1 }
              }
              return msg
            })
            this.messages = updatedMessages
          }
          
          // 更新缓存中的消息状态
          if (targetCacheKey && this.messageCache[targetCacheKey]) {
            const cachedMessageIndex = this.messageCache[targetCacheKey].findIndex(msg => {
              const msgId = typeof msg.id === 'string' ? parseInt(msg.id) : msg.id
              return msgId === messageIdNum
            })
            if (cachedMessageIndex !== -1) {
              const updatedMessage = { ...this.messageCache[targetCacheKey][cachedMessageIndex], isDelete: 1 }
              this.messageCache[targetCacheKey][cachedMessageIndex] = updatedMessage
            }
          }
          
          // 强制视图更新
          this.$nextTick(() => {
            this.$forceUpdate()
          })
          
          // WebSocket也会通知，作为备用更新机制
        } else {
          // 如果撤回失败，恢复消息状态
          if (messageIndex !== -1) {
            const updatedMessages = this.messages.map((msg, index) => {
              if (index === messageIndex) {
                return { ...msg, isDelete: 0 }
              }
              return msg
            })
            this.messages = updatedMessages
          }
          this.showToastMessage(res?.msg || '撤回失败', 'error')
        }
      } catch (error) {
        console.error('撤回消息失败:', error)
        // 如果撤回失败，恢复消息状态
        if (messageIndex !== -1) {
          const updatedMessages = this.messages.map((msg, index) => {
            if (index === messageIndex) {
              return { ...msg, isDelete: 0 }
            }
            return msg
          })
          this.messages = updatedMessages
        }
        this.showToastMessage('撤回失败', 'error')
      }
    },
    // 转发操作
    handleForwardAction() {
      this.showToastMessage('转发功能开发中', 'info')
    },
    // 删除操作
    handleDeleteAction() {
      this.showToastMessage('删除功能开发中', 'info')
    },
    // 查看资料操作
    handleViewProfileAction() {
      this.showProfile = true
    },
    // 粘贴操作
    handlePasteAction(target) {
      // 找到实际的输入框元素
      let input = null
      if (target) {
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          input = target
        } else {
          input = target.closest('input') || target.closest('textarea')
        }
      }
      // 如果没找到，使用默认的输入框
      if (!input) {
        input = this.$refs.messageInput
      }
      
      if (input) {
        input.focus()
        navigator.clipboard.readText().then(text => {
          const start = input.selectionStart || 0
          const end = input.selectionEnd || 0
          const value = input.value || ''
          const textBefore = value.substring(0, start)
          const textAfter = value.substring(end)
          const newValue = textBefore + text + textAfter
          
          // 更新输入框的值
          input.value = newValue
          
          // 如果是 v-model 绑定的输入框，需要触发 input 事件
          input.dispatchEvent(new Event('input', { bubbles: true }))
          
          // 更新 Vue 数据（如果是 messageInput）
          if (input === this.$refs.messageInput) {
            this.inputMessage = newValue
          }
          
          // 设置光标位置
          this.$nextTick(() => {
            input.setSelectionRange(start + text.length, start + text.length)
          })
        }).catch((err) => {
          console.error('粘贴失败:', err)
          this.showToastMessage('粘贴失败', 'error')
        })
      }
    },
    // 全选操作
    handleSelectAllAction(target) {
      // 找到实际的输入框元素
      let input = null
      if (target) {
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          input = target
        } else {
          input = target.closest('input') || target.closest('textarea')
        }
      }
      // 如果没找到，使用默认的输入框
      if (!input) {
        input = this.$refs.messageInput
      }
      
      if (input) {
        input.focus()
        input.select()
      }
    },
    // 清空操作
    handleClearAction(target) {
      // 找到实际的输入框元素
      let input = null
      if (target) {
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          input = target
        } else {
          input = target.closest('input') || target.closest('textarea')
        }
      }
      // 如果没找到，使用默认的输入框
      if (!input) {
        input = this.$refs.messageInput
      }
      
      if (input) {
        input.value = ''
        // 如果是 v-model 绑定的输入框，需要触发 input 事件
        input.dispatchEvent(new Event('input', { bubbles: true }))
        
        // 更新 Vue 数据（如果是 messageInput）
        if (input === this.$refs.messageInput) {
          this.inputMessage = ''
        }
        
        input.focus()
      }
    },
    // 刷新操作
    handleRefreshAction() {
      window.location.reload()
    },
    // 显示催Ta上线弹窗
    openUrgeOnlineModal() {
      this.showUrgeOnlineModal = true
      this.showUrgeReasonInput = false
      this.urgeReason = ''
    },
    // 关闭催Ta上线弹窗
    closeUrgeOnlineModal() {
      this.showUrgeOnlineModal = false
      this.showUrgeReasonInput = false
      this.urgeReason = ''
    },
    // 确认催Ta上线
    async confirmUrgeOnline() {
      const targetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
      if (!targetUserId) {
        this.showToastMessage('无法确定目标用户', 'error')
        return
      }
      
      try {
        const res = await urgeOnline(targetUserId, this.showUrgeReasonInput ? this.urgeReason : null)
        if (res && res.code === 0) {
          this.showToastMessage('催Ta上线成功', 'success')
          this.closeUrgeOnlineModal()
          // 开始倒计时
          this.urgeOnlineCountdown = 300
          this.startUrgeOnlineCountdown()
        } else {
          this.showToastMessage(res?.msg || '催Ta上线失败', 'error')
        }
      } catch (e) {
        console.error('催Ta上线失败', e)
        this.showToastMessage(e.message || '催Ta上线失败', 'error')
      }
    },
    // 检查催Ta上线倒计时
    async checkUrgeOnlineCountdown() {
      const targetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
      if (!targetUserId) {
        // 如果没有目标用户，停止倒计时
        this.stopUrgeOnlineCountdown()
        return
      }
      
      // 保存当前目标用户ID，防止在异步请求期间用户切换导致显示错误
      const checkTargetUserId = targetUserId
      
      try {
        const res = await getUrgeOnlineCountdown(targetUserId)
        // 检查在异步请求期间用户是否已切换
        const currentTargetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
        if (currentTargetUserId !== checkTargetUserId) {
          // 用户已切换，忽略这次检查结果
          return
        }
        
        if (res && res.code === 0 && res.data) {
          const countdown = res.data.countdown || 0
          this.urgeOnlineCountdown = countdown
          if (countdown > 0) {
            this.startUrgeOnlineCountdown()
          } else {
            this.stopUrgeOnlineCountdown()
          }
        }
      } catch (e) {
        console.error('检查催Ta上线倒计时失败', e)
      }
    },
    // 开始倒计时
    startUrgeOnlineCountdown() {
      if (this.urgeOnlineTimer) {
        clearInterval(this.urgeOnlineTimer)
      }
      this.urgeOnlineTimer = setInterval(() => {
        if (this.urgeOnlineCountdown > 0) {
          this.urgeOnlineCountdown--
        } else {
          this.stopUrgeOnlineCountdown()
        }
      }, 1000)
    },
    // 停止倒计时
    stopUrgeOnlineCountdown() {
      if (this.urgeOnlineTimer) {
        clearInterval(this.urgeOnlineTimer)
        this.urgeOnlineTimer = null
      }
      this.urgeOnlineCountdown = 0
    },
    // 格式化倒计时显示
    formatCountdown(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    // 拍一拍（从消息头像触发）
    async handlePatPatFromMessage(msg) {
      // 只能拍对方，不能拍自己
      if (msg.senderId === this.userInfo.id) {
        return
      }
      
      // 确定目标用户ID（拍对方）
      const targetUserId = msg.senderId
      if (!targetUserId) {
        return
      }
      
      // 检查对方是否在线
      if (!this.isUserOnline(targetUserId)) {
        // 对方不在线，不执行拍一拍
        return
      }
      
      await this.executePatPat(targetUserId)
    },
    // 执行拍一拍
    async executePatPat(targetUserId) {
      if (!targetUserId) {
        return
      }
      
      // 再次检查对方是否在线（防止状态变化）
      if (!this.isUserOnline(targetUserId)) {
        return
      }
      
      try {
        const res = await patPat(targetUserId)
        if (res && res.code === 0) {
          // 拍一拍成功，不需要在这里触发动画
          // 动画会在WebSocket收到消息时由被拍的人触发
        } else {
          // 如果失败（可能是冷却中），不显示错误提示，静默处理
          // this.showToastMessage(res?.msg || '拍一拍失败', 'error')
        }
      } catch (e) {
        // 静默处理错误，不显示提示
        console.error('拍一拍失败', e)
      }
    },
    // 触发头像摇晃动画
    // targetUserId: 要抖动的用户ID（在消息列表中显示的头像）
    // 拍一拍逻辑：
    // - 小葱拍了对方：小葱看到消息列表中对方的头像（receiverId）抖动
    // - 小葱被对方拍了：小葱看到消息列表中小葱自己的头像（this.userInfo.id）抖动
    triggerAvatarShake(targetUserId) {
      console.log('触发头像抖动，targetUserId:', targetUserId, '当前shakingAvatarUserId:', this.shakingAvatarUserId)
      
      // 统一转换为字符串类型，确保类型一致
      const targetUserIdStr = String(targetUserId)
      
      // 先重置，确保动画能重新触发
      this.shakingAvatarUserId = ''
      this.isAvatarShaking = false
      this.shakeAnimationKey = 0
      
      // 使用 requestAnimationFrame 和 $nextTick 确保DOM更新后再设置，让动画能正确触发
      requestAnimationFrame(() => {
        this.$nextTick(() => {
          // 设置摇晃状态，用于匹配消息列表中的头像（通过 msg.senderId 匹配）
          this.shakingAvatarUserId = targetUserIdStr
          // 增加动画键，强制重新触发动画
          this.shakeAnimationKey = Date.now()
          console.log('设置shakingAvatarUserId为:', this.shakingAvatarUserId, 'shakeAnimationKey:', this.shakeAnimationKey, '消息列表长度:', this.messages.length)
          
          // 检查消息列表中是否有匹配的头像（统一类型比较）
          const matchingMessages = this.messages.filter(msg => 
            !this.isMessageRecalled(msg) && 
            !this.isMessagePatPat(msg) && 
            String(msg.senderId) === targetUserIdStr
          )
          const hasMatchingAvatar = matchingMessages.length > 0
          console.log('是否有匹配的头像:', hasMatchingAvatar, 'targetUserIdStr:', targetUserIdStr, '匹配的消息数:', matchingMessages.length)
          
          // 打印所有消息的senderId用于调试
          if (!hasMatchingAvatar) {
            console.warn('未找到匹配的头像！消息列表详情:', this.messages.map(msg => ({
              id: msg.id,
              senderId: msg.senderId,
              senderIdType: typeof msg.senderId,
              receiverId: msg.receiverId,
              isRecalled: this.isMessageRecalled(msg),
              isPatPat: this.isMessagePatPat(msg),
              content: msg.content?.substring(0, 20)
            })))
          } else {
            console.log('找到匹配的头像，消息ID:', matchingMessages.map(m => m.id))
          }
          
          // 强制触发视图更新
          this.$forceUpdate()
          
          // 再次使用 $nextTick 确保类已经添加到DOM，然后通过直接操作DOM来强制触发动画
          this.$nextTick(() => {
            // 通过直接操作DOM来确保动画触发
            const messageItems = document.querySelectorAll('.message-item')
            let foundCount = 0
            messageItems.forEach(item => {
              const senderIdAttr = item.getAttribute('data-sender-id')
              if (senderIdAttr && String(senderIdAttr) === targetUserIdStr) {
                const avatarEl = item.querySelector('.message-avatar')
                if (avatarEl) {
                  foundCount++
                  // 先移除类，强制浏览器重新计算
                  avatarEl.classList.remove('avatar-shaking')
                  // 强制重排
                  void avatarEl.offsetHeight
                  // 使用 requestAnimationFrame 确保浏览器重新计算样式后再添加类
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      avatarEl.classList.add('avatar-shaking')
                      console.log('通过DOM操作添加avatar-shaking类到元素，senderId:', senderIdAttr, '元素:', avatarEl)
                    })
                  })
                }
              }
            })
            console.log('DOM操作完成，找到', foundCount, '个匹配的头像元素')
          })
          
          // 判断聊天窗口顶部显示的头像是否是要抖动的用户，如果是则触发顶部头像抖动
          let shouldShakeTopAvatar = false
          if (this.userInfo.admin === 1) {
            // 管理员：如果当前聊天的用户就是要抖动的用户（统一类型比较）
            shouldShakeTopAvatar = this.currentChatUserId && String(this.currentChatUserId) === targetUserIdStr
            console.log('管理员模式 - currentChatUserId:', this.currentChatUserId, 'targetUserIdStr:', targetUserIdStr, 'shouldShakeTopAvatar:', shouldShakeTopAvatar)
          } else {
            // 普通用户：如果要抖动的用户是管理员（统一类型比较）
            shouldShakeTopAvatar = this.adminInfo && String(this.adminInfo.id) === targetUserIdStr
            console.log('普通用户模式 - adminInfo.id:', this.adminInfo?.id, 'targetUserIdStr:', targetUserIdStr, 'shouldShakeTopAvatar:', shouldShakeTopAvatar)
          }
          
          if (shouldShakeTopAvatar) {
            console.log('设置 isAvatarShaking = true')
            this.isAvatarShaking = true
          }
          
          // 延迟重置，让动画能完整播放
          setTimeout(() => {
            // 先通过DOM移除类，确保动画能完整播放
            const avatarElements = document.querySelectorAll('.message-avatar.avatar-shaking')
            avatarElements.forEach(el => {
              el.classList.remove('avatar-shaking')
            })
            // 然后重置Vue数据
            this.shakingAvatarUserId = ''
            this.isAvatarShaking = false
            this.shakeAnimationKey = 0
            console.log('重置shakingAvatarUserId和isAvatarShaking，移除了', avatarElements.length, '个avatar-shaking类')
          }, 500) // 摇晃500ms
        })
      })
    },
    // 播放拍一拍音频
    playPatPatAudio() {
      // 检查声音提示是否开启（仅管理员需要检查）
      if (this.userInfo.admin === 1 && this.soundEnabled !== 1) {
        console.log('声音提示已关闭，不播放拍一拍音频')
        return
      }
      try {
        if (!this.patPatAudio) {
          // 优先使用前端打包的音频文件（从public目录），如果失败则使用后端路径
          // public目录下的文件在打包后会直接复制到dist根目录，所以路径是 /mp3/paipai.mp3
          const frontendAudioPath = '/mp3/paipai.mp3'
          const backendAudioPath = process.env.NODE_ENV === 'development' 
            ? 'http://localhost:8091/chat-api/mp3/paipai.mp3'
            : '/chat-api/mp3/paipai.mp3'
          
          // 先尝试使用前端打包的音频文件
          this.patPatAudio = new Audio(frontendAudioPath)
          this.patPatAudio.volume = 0.5 // 设置音量
          
          // 监听错误，如果前端音频加载失败，则使用后端路径
          const handleError = () => {
            console.warn('前端音频文件加载失败，尝试使用后端路径:', backendAudioPath)
            const backendAudio = new Audio(backendAudioPath)
            backendAudio.volume = 0.5
            // 如果后端音频也加载失败，记录错误但不阻止后续播放尝试
            backendAudio.addEventListener('error', () => {
              console.error('后端音频文件也加载失败，请检查音频文件是否存在')
            }, { once: true })
            this.patPatAudio = backendAudio
          }
          this.patPatAudio.addEventListener('error', handleError, { once: true })
        }
        // 重置音频到开头并播放
        this.patPatAudio.currentTime = 0
        this.patPatAudio.play().catch(e => {
          console.error('播放拍一拍音频失败', e)
        })
      } catch (e) {
        console.error('播放拍一拍音频失败', e)
      }
    },
    // 播放用户上线音频（仅管理员）
    async playOnlineAudio(targetUserId) {
      // 只有管理员才播放上线提示音
      if (this.userInfo.admin !== 1) {
        console.log('非管理员用户，不播放上线提示音')
        return
      }
      
      // 检查声音提示是否开启
      if (this.soundEnabled !== 1) {
        console.log('声音提示已关闭，不播放上线提示音')
        return
      }
      
      // 如果没有传入targetUserId，不播放
      if (!targetUserId) {
        console.warn('播放上线音频失败：缺少targetUserId')
        return
      }
      
      // admin=1的用户不会对自己进行播报
      if (targetUserId === this.userInfo.id) {
        console.log('管理员自己上线，不播放提示音，targetUserId:', targetUserId)
        return
      }
      
      // 如果已经为这个用户播放过音频（在当前会话中），不再播放，避免重复播放
      if (this.onlineAudioPlayed[targetUserId]) {
        console.log('用户在当前会话中已经播放过上线提示音，不再播放，userId:', targetUserId)
        return
      }
      
      console.log('🔔 检查用户上线音频倒计时，targetUserId:', targetUserId, 'soundEnabled:', this.soundEnabled)
      
      try {
        // 检查Redis倒计时（新逻辑：检查用户离开时间是否超过10秒）
        const res = await checkAndSetOnlineAudioCooldown(targetUserId)
        console.log('📥 上线音频倒计时检查响应:', JSON.stringify(res, null, 2))
        console.log('📥 响应详情 - code:', res?.code, 'data存在:', !!res?.data, 'data:', res?.data)
        console.log('📥 条件检查 - res存在:', !!res, 'code===0:', res?.code === 0, 'data存在:', !!res?.data)
        
        // 注意：后端返回成功时code是0，不是200
        // 先检查基本条件
        if (!res) {
          console.error('❌ 响应为空')
          return
        }
        
        if (res.code !== 0) {
          console.error('❌ 响应code不是0，code:', res.code, 'msg:', res.msg)
          return
        }
        
        if (!res.data) {
          console.error('❌ 响应中data为空或不存在')
          return
        }
        
        // 如果用户离开时间超过10秒，可以播放
        // 使用宽松的判断，因为后端可能返回布尔值或字符串
        const canPlay = res.data.canPlay === true || res.data.canPlay === 'true' || res.data.canPlay === 1
        console.log('📥 canPlay判断 - canPlay值:', res.data.canPlay, '类型:', typeof res.data.canPlay, '结果:', canPlay)
        
        if (canPlay) {
          console.log('✅ 用户离开时间超过10秒，播放上线提示音，targetUserId:', targetUserId, '原因:', res.data.reason)
          // 立即标记为已播放，防止并发调用导致重复播放
          this.onlineAudioPlayed[targetUserId] = true
          
          if (!this.onlineAudio) {
            // 优先使用前端打包的音频文件（从public目录），如果失败则使用后端路径
            // public目录下的文件在打包后会直接复制到dist根目录，所以路径是 /mp3/online.mp3
            const frontendAudioPath = '/mp3/online.mp3'
            const backendAudioPath = process.env.NODE_ENV === 'development' 
              ? 'http://localhost:8091/chat-api/mp3/online.mp3'
              : '/chat-api/mp3/online.mp3'
            
            console.log('初始化上线音频，优先使用前端路径:', frontendAudioPath)
            // 先尝试使用前端打包的音频文件
            this.onlineAudio = new Audio(frontendAudioPath)
            this.onlineAudio.volume = 0.5 // 设置音量
            this.onlineAudio.loop = false // 确保不循环播放
            
            // 监听错误，如果前端音频加载失败，则使用后端路径
            const handleError = () => {
              console.warn('前端音频文件加载失败，尝试使用后端路径:', backendAudioPath)
              const backendAudio = new Audio(backendAudioPath)
              backendAudio.volume = 0.5
              backendAudio.loop = false
              // 如果后端音频也加载失败，记录错误但不阻止后续播放尝试
              backendAudio.addEventListener('error', () => {
                console.error('后端音频文件也加载失败，请检查音频文件是否存在')
              }, { once: true })
              this.onlineAudio = backendAudio
            }
            this.onlineAudio.addEventListener('error', handleError, { once: true })
          }
          // 先停止之前的播放（如果有）
          this.onlineAudio.pause()
          this.onlineAudio.currentTime = 0
          // 播放音频（只播放一次，不循环）
          this.onlineAudio.play().then(() => {
            console.log('上线提示音播放成功')
          }).catch(e => {
            console.error('播放用户上线音频失败', e)
            // 播放失败时，清除标记，允许重试
            delete this.onlineAudioPlayed[targetUserId]
          })
        } else {
          // 用户离开时间不足10秒或在播放倒计时内，不播放音频
          console.log(`❌ 用户 ${targetUserId} 不播放上线提示音，原因: ${res.data.reason || '未知'}, 剩余时间：${res.data.remaining || 0}秒`)
          // 不设置播放标记，因为实际上没有播放
          // 这样如果条件满足后用户再次上线，可以播放
        }
      } catch (e) {
        console.error('播放用户上线音频异常:', e)
        // 异常情况下不播放音频，避免误报
        console.warn('异常情况，不播放音频，等待下次检查')
      }
    },
    // 下载图片
    // 保存表情包操作
    async handleSaveEmojiAction(emojiUrl, isGif) {
      if (!emojiUrl) {
        this.showToastMessage('表情地址无效', 'error')
        return
      }
      
      try {
        // 调用后端API保存表情
        const res = await saveEmojiFromUrl(emojiUrl, isGif)
        if (res && res.code === 0) {
          this.showToastMessage('表情保存成功', 'success')
          // 重新加载表情列表
          await this.loadCustomEmojis()
        } else {
          this.showToastMessage(res?.msg || '表情保存失败', 'error')
        }
      } catch (e) {
        console.error('保存表情失败', e)
        this.showToastMessage(e.message || '表情保存失败', 'error')
      }
    },
    async handleDownloadImage(imageUrl) {
      if (!imageUrl) {
        this.showToastMessage('图片地址无效', 'error')
        return
      }
      
      // 从 URL 中提取文件名
      let fileName = 'image.jpg'
      try {
        const urlParts = imageUrl.split('/')
        const lastPart = urlParts[urlParts.length - 1]
        if (lastPart && lastPart.includes('.')) {
          fileName = lastPart.split('?')[0]
        } else {
          fileName = `image_${Date.now()}.jpg`
        }
      } catch (e) {
        fileName = `image_${Date.now()}.jpg`
      }
      
      try {
        // 方法1: 尝试使用 fetch 下载（适用于同域或支持CORS的图片）
        try {
          const response = await fetch(imageUrl, {
            mode: 'cors',
            credentials: 'omit'
          })
          
          if (response.ok) {
            const blob = await response.blob()
            this.downloadBlob(blob, fileName)
            this.showToastMessage('图片下载成功', 'success')
            return
          }
        } catch (fetchError) {
          console.log('Fetch方法失败，尝试其他方法:', fetchError)
        }
        
        // 方法2: 使用 img + canvas 方式（绕过CORS限制）
        await this.downloadImageViaCanvas(imageUrl, fileName)
        this.showToastMessage('图片下载成功', 'success')
      } catch (error) {
        console.error('下载图片失败:', error)
        
        // 方法3: 降级方案 - 直接打开新窗口（让用户手动保存）
        try {
          const link = document.createElement('a')
          link.href = imageUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          link.click()
          this.showToastMessage('已在新窗口打开图片，请右键保存', 'info')
        } catch (fallbackError) {
          this.showToastMessage('下载图片失败，请尝试右键图片另存为', 'error')
        }
      }
    },
    // 使用 canvas 下载图片（绕过CORS）
    downloadImageViaCanvas(imageUrl, fileName) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous' // 尝试跨域
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            
            // 将 canvas 转换为 blob
            canvas.toBlob((blob) => {
              if (blob) {
                this.downloadBlob(blob, fileName)
                resolve()
              } else {
                reject(new Error('Canvas转换失败'))
              }
            }, 'image/png')
          } catch (error) {
            reject(error)
          }
        }
        
        img.onerror = () => {
          reject(new Error('图片加载失败'))
        }
        
        // 设置超时
        setTimeout(() => {
          reject(new Error('图片加载超时'))
        }, 10000)
        
        img.src = imageUrl
      })
    },
    // 下载 Blob 对象
    downloadBlob(blob, fileName) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.style.display = 'none'
      
      document.body.appendChild(link)
      link.click()
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)
    },
    // 保存别称
    async saveAlias() {
      const targetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
      if (!targetUserId) {
        this.showToastMessage('无法设置别称', 'error')
        return
      }
      try {
        const res = await setAlias({
          targetUserId: targetUserId.toString(),
          alias: this.aliasForm.alias.trim() || null
        })
        console.log('设置别称响应:', res)
        if (res && res.code === 0) {
          // 更新本地别称（Vue 3 不需要 $set，直接赋值即可）
          if (this.aliasForm.alias.trim()) {
            this.userAliases[targetUserId] = this.aliasForm.alias.trim()
          } else {
            this.userAliases[targetUserId] = null
            // 如果设置为 null，也可以删除该属性
            delete this.userAliases[targetUserId]
          }
          // 强制触发响应式更新
          this.userAliases = { ...this.userAliases }
          this.showToastMessage('设置别称成功', 'success')
          this.closeAliasModal()
        } else {
          console.error('设置别称失败，响应:', res)
          this.showToastMessage(res?.msg || res?.message || '设置别称失败', 'error')
        }
      } catch (e) {
        console.error('设置别称失败，异常:', e)
        this.showToastMessage(e?.message || '设置别称失败', 'error')
      }
    },
    getSenderAvatar(msg) {
      // 优先使用消息中直接包含的头像信息
      if (msg.senderAvatar) {
        return msg.senderAvatar
      }
      // 如果没有，则从用户信息中获取
      if (msg.senderId === this.userInfo.id) {
        return this.userInfo.avatar || null
      }
      if (this.userInfo.admin === 1) {
        const user = this.userList.find(u => u.id === msg.senderId)
        return user ? user.avatar : null
      } else {
        return this.adminInfo ? this.adminInfo.avatar : null
      }
    },
    getAvatarUrl(msg) {
      // 优先使用消息中直接包含的头像信息
      if (msg && msg.senderAvatar) {
        return msg.senderAvatar
      }
      // 如果没有，则从用户信息中获取
      if (msg && msg.senderId === this.userInfo.id) {
        return this.userInfo.avatar || null
      }
      if (this.userInfo.admin === 1) {
        const user = this.userList.find(u => u.id === msg.senderId)
        return user ? user.avatar : null
      } else {
        return this.adminInfo ? this.adminInfo.avatar : null
      }
    },
    showToastMessage(message, type = 'success') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    isUserOnline(userId) {
      return this.onlineStatus[userId] === true
    },
    async checkOnlineStatus(userId) {
      try {
        const userIdNum = typeof userId === 'number' ? userId : parseInt(userId)
        // 记录之前的状态，用于检测状态变化（在更新状态之前记录）
        const previousStatus = this.onlineStatus[userIdNum]
        const wasOnline = previousStatus === true
        const wasOffline = previousStatus === false
        const wasUndefined = previousStatus === undefined
        const res = await getOnlineStatus(userIdNum)
        this.onlineStatus[userIdNum] = res.data.isOnline
        if (!res.data.isOnline && res.data.lastActiveTime) {
          // 如果用户下线了，清除播放标记（这样用户再次上线时可以播放）
          if (this.userInfo.admin === 1) {
            delete this.onlineAudioPlayed[userIdNum]
          }
          const lastTime = res.data.lastActiveTime
          const seconds = Math.floor((Date.now() - lastTime) / 1000)
          // 同时使用数字和字符串作为key，确保能找到
          this.offlineTime[userIdNum] = { lastTime: lastTime, seconds: seconds }
          this.offlineTime[userIdNum.toString()] = { lastTime: lastTime, seconds: seconds }
        } else if (res.data.isOnline) {
          // 如果在线，清除离线时间
          delete this.offlineTime[userIdNum]
          delete this.offlineTime[userIdNum.toString()]
          // checkOnlineStatus是定期检查，不是真正的上线事件，不触发播放
          // 播放逻辑只在WebSocket的user_status消息中处理（真正的实时上线事件）
          // 定期检查只是为了同步状态，不应该触发播放逻辑，避免重复调用导致显示"在播放倒计时内"
        }
      } catch (e) {
        console.error('检查在线状态失败', e)
      }
    },
    updateOfflineTime() {
      // 更新所有离线用户的离线时间
      Object.keys(this.offlineTime).forEach(userId => {
        const userIdInt = parseInt(userId)
        if (!this.isUserOnline(userIdInt)) {
          const offline = this.offlineTime[userId]
          if (offline && offline.lastTime) {
            const seconds = Math.floor((Date.now() - offline.lastTime) / 1000)
            // 同时更新数字和字符串key，确保一致性
            this.offlineTime[userIdInt] = { lastTime: offline.lastTime, seconds: seconds }
            this.offlineTime[userIdInt.toString()] = { lastTime: offline.lastTime, seconds: seconds }
          }
        } else {
          // 如果上线了，清除离线时间
          delete this.offlineTime[userIdInt]
          delete this.offlineTime[userIdInt.toString()]
        }
      })
      
      // 定期检查当前聊天对象的在线状态（每5秒检查一次）
      if (this.currentChatUserId && this.currentChatUserId > 0) {
        this.checkOnlineStatus(this.currentChatUserId)
      }
      
      // 如果是普通用户，检查管理员状态
      if (this.userInfo.admin !== 1 && this.adminInfo && this.adminInfo.id) {
        this.checkOnlineStatus(this.adminInfo.id)
      }
      
      // 如果是管理员，检查所有用户状态
      if (this.userInfo.admin === 1) {
        this.userList.forEach(user => {
          this.checkOnlineStatus(user.id)
        })
      }
    },
    getOfflineTime(userId) {
      if (!userId) return '离线'
      
      // 先检查是否在线
      if (this.isUserOnline(userId)) {
        return '在线中'
      }
      
      // 获取离线时间，尝试多种key格式（字符串和数字）
      const userIdStr = userId.toString()
      const userIdNum = typeof userId === 'number' ? userId : parseInt(userId)
      
      let offline = this.offlineTime[userIdStr] || this.offlineTime[userIdNum]
      
      // 如果找到了离线时间数据
      if (offline && offline.lastTime) {
        // 重新计算秒数（确保是最新的）
        const seconds = Math.floor((Date.now() - offline.lastTime) / 1000)
        
        if (seconds < 60) return `离线${seconds}秒`
        const minutes = Math.floor(seconds / 60)
        if (minutes < 60) return `离线${minutes}分钟`
        const hours = Math.floor(minutes / 60)
        if (hours < 24) return `离线${hours}小时`
        const days = Math.floor(hours / 24)
        if (days < 30) return `离线${days}天`
        const months = Math.floor(days / 30)
        return `离线${months}月`
      }
      
      // 如果没有离线时间数据，尝试从API获取（但不阻塞显示）
      if (userId && userId > 0) {
        // 异步获取，不等待结果
        this.checkOnlineStatus(userIdNum)
      }
      
      return '离线'
    },
    // 获取未读消息数量
    async getUnreadCount() {
      if (this.userInfo.admin !== 1) {
        return // 只有管理员需要获取未读消息
      }
      try {
        const res = await getUnreadCount()
        if (res && res.code === 0 && res.data) {
          // 直接使用服务器返回的数据，不要强制清除
          // 未读计数只应该在以下情况清除：
          // 1. 用户点击会话时（selectUser方法中）
          // 2. 收到属于当前会话的消息时（WebSocket消息处理中）
          this.unreadCount = res.data
        }
      } catch (e) {
        console.error('获取未读消息数量失败', e)
      }
    },
    // 开始轮询未读消息
    startUnreadPolling() {
      this.getUnreadCount()
      this.unreadTimer = setInterval(() => {
        this.getUnreadCount()
      }, 3000) // 每3秒轮询一次
    },
    // 获取指定用户的未读消息数
    getUnreadCountForUser(user) {
      if (!user || !user.id) return 0
      const count = this.unreadCount[user.id.toString()]
      return count || 0
    },
    // 格式化未读消息数量（超过99显示99+）
    formatUnreadCount(count) {
      if (count > 99) {
        return '99+'
      }
      return count.toString()
    },
    // 清除指定用户的未读消息计数
    async clearUnreadCountForUser(userId) {
      try {
        await clearUnreadCount({ senderId: userId })
        // 从本地数据中清除
        const userIdStr = userId.toString()
        if (this.unreadCount[userIdStr]) {
          delete this.unreadCount[userIdStr]
        }
      } catch (e) {
        console.error('清除未读消息计数失败', e)
      }
    },
    validateNickname() {
      this.nicknameError = ''
      if (!this.profileForm.nickname || this.profileForm.nickname.trim() === '') {
        this.nicknameError = '昵称不能为空'
        return false
      }
      if (this.profileForm.nickname.length < 2) {
        this.nicknameError = '昵称至少2个字符'
        return false
      }
      return true
    },
    validateOldPassword() {
      this.oldPasswordError = ''
      if (!this.passwordForm.oldPassword || this.passwordForm.oldPassword.trim() === '') {
        this.oldPasswordError = '请输入旧密码'
        return false
      }
      return true
    },
    checkStrength(pwd) {
      if (!pwd) return 0
      let score = 0
      if (pwd.length >= 8) score++
      if (/[A-Z]/.test(pwd)) score++
      if (/[a-z]/.test(pwd)) score++
      if (/\d/.test(pwd)) score++
      if (/[^A-Za-z0-9]/.test(pwd)) score++
      if (score >= 4) return 3
      if (score === 3) return 2
      return score > 0 ? 1 : 0
    },
    getStrengthText(level) {
      if (level === 3) return '强'
      if (level === 2) return '中'
      if (level === 1) return '弱'
      return ''
    },
    handlePasswordInput() {
      const pwd = this.passwordForm.newPassword || ''
      if (!pwd) {
        this.passwordStrengthLevel = 0
        this.newPasswordError = ''
        return
      }
      this.passwordStrengthLevel = this.checkStrength(pwd)
      this.validateNewPassword(true)
    },
    validateNewPassword(isInput = false) {
      const pwd = this.passwordForm.newPassword || ''
      this.newPasswordError = ''
      if (!pwd) {
        this.passwordStrengthLevel = 0
        if (!isInput) {
          this.newPasswordError = '请输入新密码'
        }
        return false
      }
      if (pwd.length < 8) {
        this.newPasswordError = '密码长度至少为8个字符'
        this.passwordStrengthLevel = this.checkStrength(pwd)
        return false
      }
      if (pwd === this.passwordForm.oldPassword) {
        this.newPasswordError = '新密码不能与旧密码相同'
        this.passwordStrengthLevel = this.checkStrength(pwd)
        return false
      }
      this.passwordStrengthLevel = this.checkStrength(pwd)
      return true
    },
    validateConfirmPassword() {
      this.confirmPasswordError = ''
      if (!this.passwordForm.confirmPassword || this.passwordForm.confirmPassword.trim() === '') {
        this.confirmPasswordError = '请再次输入新密码'
        return false
      }
      if (this.passwordForm.confirmPassword !== this.passwordForm.newPassword) {
        this.confirmPasswordError = '两次输入的密码不一致'
        return false
      }
      return true
    },
    async handleAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      
      if (!file.type.startsWith('image/')) {
        this.showToastMessage('请选择图片文件', 'error')
        return
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.showToastMessage('图片大小不能超过5MB', 'error')
        return
      }
      
      // 先预览
      const reader = new FileReader()
      reader.onload = (e) => {
        this.avatarPreview = e.target.result
      }
      reader.readAsDataURL(file)
      
      // 上传到OSS
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await uploadAvatar(formData)
        if (res.data && res.data.url) {
          this.avatarPreview = res.data.url
          this.userInfo.avatar = res.data.url
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
          this.showToastMessage('头像上传成功', 'success')
        } else {
          this.showToastMessage('头像上传失败', 'error')
        }
      } catch (e) {
        console.error('上传头像失败', e)
        this.showToastMessage(e.message || '头像上传失败', 'error')
      }
    },
    async updateProfile() {
      if (!this.validateNickname()) {
        return
      }
      
      this.savingProfile = true
      try {
        const updateData = { nickname: this.profileForm.nickname }
        // 如果头像已上传到OSS，更新头像URL
        if (this.avatarPreview && !this.avatarPreview.startsWith('data:')) {
          updateData.avatar = this.avatarPreview
        }
        
        await updateProfile(updateData)
        this.userInfo.nickname = this.profileForm.nickname
        if (this.avatarPreview && !this.avatarPreview.startsWith('data:')) {
          this.userInfo.avatar = this.avatarPreview
        }
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        this.showToastMessage('修改成功', 'success')
        this.showProfile = false
      } catch (e) {
        this.showToastMessage(e.message || '修改失败', 'error')
      } finally {
        this.savingProfile = false
      }
    },
    cancelProfile() {
      this.profileForm.nickname = this.userInfo.nickname || ''
      this.avatarPreview = this.userInfo.avatar || ''
      this.nicknameError = ''
    },
    async changePassword() {
      if (!this.validateOldPassword() || !this.validateNewPassword() || !this.validateConfirmPassword()) {
        return
      }
      
      this.savingPassword = true
      try {
        await changePassword(this.passwordForm)
        this.showToastMessage('密码修改成功', 'success')
        this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        this.oldPasswordError = ''
        this.newPasswordError = ''
        this.confirmPasswordError = ''
        this.passwordStrengthLevel = 0
      } catch (e) {
        this.showToastMessage(e.message || '修改失败', 'error')
      } finally {
        this.savingPassword = false
      }
    },
    cancelPassword() {
      this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.oldPasswordError = ''
      this.newPasswordError = ''
      this.confirmPasswordError = ''
      this.passwordStrengthLevel = 0
    },
    // 邮箱变更相关方法
    maskEmail(email) {
      if (!email) return ''
      const parts = email.split('@')
      if (parts.length !== 2) return email
      const username = parts[0]
      const domain = parts[1]
      if (username.length <= 2) return email
      const masked = username.substring(0, 3) + '******' + username.substring(username.length - 2)
      return masked + '@' + domain
    },
    maskPhone(phone) {
      if (!phone || phone.length < 11) return phone
      return phone.substring(0, 3) + '****' + phone.substring(7)
    },
    async sendEmailCode() {
      if (!this.profileForm.email) {
        this.emailChangeError = '邮箱不能为空'
        return
      }
      this.sendingCode = true
      this.emailChangeError = ''
      try {
        const res = await sendEmailCode({ email: this.profileForm.email, scene: 'change_email' })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('验证码已发送，请查收邮箱')
          this.startEmailCountdown()
        } else {
          this.emailChangeError = res?.msg || '发送验证码失败'
        }
      } catch (e) {
        this.emailChangeError = e.message || '发送验证码失败'
      } finally {
        this.sendingCode = false
      }
    },
    startEmailCountdown() {
      this.emailCountdown = 60
      const timer = setInterval(() => {
        this.emailCountdown--
        if (this.emailCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async verifyCurrentEmail() {
      if (!this.emailChangeForm.code || this.emailChangeForm.code.length !== 4) {
        this.emailChangeError = '请输入4位验证码'
        return
      }
      this.verifyingCurrentEmail = true
      this.emailChangeError = ''
      try {
        const res = await changeEmail({
          email: this.profileForm.email,
          code: this.emailChangeForm.code
        })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('当前邮箱验证成功')
          this.emailChangeStep = 2
        } else {
          this.emailChangeError = res?.msg || '验证码错误'
        }
      } catch (e) {
        this.emailChangeError = e.message || '验证失败，请稍后重试'
      } finally {
        this.verifyingCurrentEmail = false
      }
    },
    validateNewEmail() {
      this.newEmailError = ''
      if (!this.emailChangeForm.newEmail) {
        this.newEmailError = '新邮箱不能为空'
        return
      }
      if (!/^[A-Za-z0-9+_.-]+@(.+)$/.test(this.emailChangeForm.newEmail)) {
        this.newEmailError = '邮箱格式不正确'
        return
      }
      if (this.emailChangeForm.newEmail === this.profileForm.email) {
        this.newEmailError = '新邮箱不能与当前邮箱相同'
        return
      }
    },
    async sendNewEmailCode() {
      this.validateNewEmail()
      if (this.newEmailError) {
        return
      }
      // 检查新邮箱是否已被使用
      try {
        const res = await checkEmail(this.emailChangeForm.newEmail)
        if (res.exists) {
          this.newEmailError = '该邮箱已被其他账号使用'
          return
        }
      } catch (e) {
        // 检查失败不影响发送验证码
      }
      this.sendingNewEmailCode = true
      this.emailChangeError = ''
      try {
        const res = await sendEmailCode({ email: this.emailChangeForm.newEmail, scene: 'change_email' })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('验证码已发送到新邮箱，请查收')
          this.startNewEmailCountdown()
        } else {
          this.emailChangeError = res?.msg || '发送验证码失败'
        }
      } catch (e) {
        this.emailChangeError = e.message || '发送验证码失败，请稍后重试'
      } finally {
        this.sendingNewEmailCode = false
      }
    },
    startNewEmailCountdown() {
      this.newEmailCountdown = 60
      const timer = setInterval(() => {
        this.newEmailCountdown--
        if (this.newEmailCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async confirmChangeEmail() {
      this.validateNewEmail()
      if (this.newEmailError) {
        return
      }
      if (!this.emailChangeForm.newEmailCode || this.emailChangeForm.newEmailCode.length !== 4) {
        this.emailChangeError = '请输入4位验证码'
        return
      }
      this.changingEmail = true
      this.emailChangeError = ''
      try {
        const res = await changeEmail({
          email: this.profileForm.email,
          newEmail: this.emailChangeForm.newEmail,
          newEmailCode: this.emailChangeForm.newEmailCode
        })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('邮箱修改成功')
          this.profileForm.email = this.emailChangeForm.newEmail
          this.userInfo.email = this.emailChangeForm.newEmail
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
          this.closeEmailChangeModal()
        } else {
          this.emailChangeError = res?.msg || '修改邮箱失败'
        }
      } catch (e) {
        this.emailChangeError = e.message || '修改邮箱失败，请稍后重试'
      } finally {
        this.changingEmail = false
      }
    },
    closeEmailChangeModal() {
      this.showEmailChangeModal = false
      this.emailChangeStep = 1
      this.modalMouseDownTarget = null
      this.modalMouseDownPosition = null
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      // 清理事件监听器
      document.removeEventListener('mousemove', this.handleModalMouseMove)
      this.emailChangeForm.code = ''
      this.emailChangeForm.newEmail = ''
      this.emailChangeForm.newEmailCode = ''
      this.emailChangeError = ''
      this.newEmailError = ''
      this.emailCountdown = 0
      this.newEmailCountdown = 0
      this.sendingCode = false
      this.sendingNewEmailCode = false
      this.changingEmail = false
      this.verifyingCurrentEmail = false
    },
    // 手机号变更相关方法
    async sendPhoneCode() {
      if (!this.profileForm.phone) {
        this.phoneChangeError = '手机号不能为空'
        return
      }
      this.sendingPhoneCode = true
      this.phoneChangeError = ''
      try {
        const res = await sendSmsCode({ phone: this.profileForm.phone, scene: 'change_phone' })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('验证码已发送，请查收短信')
          this.startPhoneCountdown()
        } else {
          this.phoneChangeError = res?.msg || '发送验证码失败'
        }
      } catch (e) {
        this.phoneChangeError = e.message || '发送验证码失败'
      } finally {
        this.sendingPhoneCode = false
      }
    },
    startPhoneCountdown() {
      this.phoneCountdown = 60
      const timer = setInterval(() => {
        this.phoneCountdown--
        if (this.phoneCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async verifyCurrentPhone() {
      if (!this.phoneChangeForm.code || this.phoneChangeForm.code.length !== 4) {
        this.phoneChangeError = '请输入4位验证码'
        return
      }
      this.verifyingCurrentPhone = true
      this.phoneChangeError = ''
      try {
        const res = await changePhone({
          phone: this.profileForm.phone,
          code: this.phoneChangeForm.code
        })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('当前手机号验证成功')
          this.phoneChangeStep = 2
        } else {
          this.phoneChangeError = res?.msg || '验证码错误'
        }
      } catch (e) {
        this.phoneChangeError = e.message || '验证失败，请稍后重试'
      } finally {
        this.verifyingCurrentPhone = false
      }
    },
    validateNewPhone() {
      this.newPhoneError = ''
      if (!this.phoneChangeForm.newPhone) {
        this.newPhoneError = '新手机号不能为空'
        return
      }
      if (!/^1[3-9]\d{9}$/.test(this.phoneChangeForm.newPhone)) {
        this.newPhoneError = '手机号格式不正确'
        return
      }
      if (this.phoneChangeForm.newPhone === this.profileForm.phone) {
        this.newPhoneError = '新手机号不能与当前手机号相同'
        return
      }
    },
    async sendNewPhoneCode() {
      this.validateNewPhone()
      if (this.newPhoneError) {
        return
      }
      // 检查新手机号是否已被使用
      try {
        const res = await checkPhone(this.phoneChangeForm.newPhone)
        if (res.exists) {
          this.newPhoneError = '该手机号已被其他账号使用'
          return
        }
      } catch (e) {
        // 检查失败不影响发送验证码
      }
      this.sendingNewPhoneCode = true
      this.phoneChangeError = ''
      try {
        const res = await sendSmsCode({ phone: this.phoneChangeForm.newPhone, scene: 'change_phone' })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('验证码已发送到新手机号，请查收')
          this.startNewPhoneCountdown()
        } else {
          this.phoneChangeError = res?.msg || '发送验证码失败'
        }
      } catch (e) {
        this.phoneChangeError = e.message || '发送验证码失败，请稍后重试'
      } finally {
        this.sendingNewPhoneCode = false
      }
    },
    startNewPhoneCountdown() {
      this.newPhoneCountdown = 60
      const timer = setInterval(() => {
        this.newPhoneCountdown--
        if (this.newPhoneCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async confirmChangePhone() {
      this.validateNewPhone()
      if (this.newPhoneError) {
        return
      }
      if (!this.phoneChangeForm.newPhoneCode || this.phoneChangeForm.newPhoneCode.length !== 4) {
        this.phoneChangeError = '请输入4位验证码'
        return
      }
      this.changingPhone = true
      this.phoneChangeError = ''
      try {
        const res = await changePhone({
          phone: this.profileForm.phone,
          newPhone: this.phoneChangeForm.newPhone,
          newPhoneCode: this.phoneChangeForm.newPhoneCode
        })
        // request.js 拦截器已处理，成功时直接返回 res，code === 0
        if (res && res.code === 0) {
          this.$message.success('手机号修改成功')
          this.profileForm.phone = this.phoneChangeForm.newPhone
          this.userInfo.phone = this.phoneChangeForm.newPhone
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
          this.closePhoneChangeModal()
        } else {
          this.phoneChangeError = res?.msg || '修改手机号失败'
        }
      } catch (e) {
        this.phoneChangeError = e.message || '修改手机号失败，请稍后重试'
      } finally {
        this.changingPhone = false
      }
    },
    closePhoneChangeModal() {
      this.showPhoneChangeModal = false
      this.phoneChangeStep = 1
      this.modalMouseDownTarget = null
      this.modalMouseDownPosition = null
      this.modalMouseMoved = false
      this.shouldPreventClose = false
      // 清理事件监听器
      document.removeEventListener('mousemove', this.handleModalMouseMove)
      this.phoneChangeForm.code = ''
      this.phoneChangeForm.newPhone = ''
      this.phoneChangeForm.newPhoneCode = ''
      this.phoneChangeError = ''
      this.newPhoneError = ''
      this.phoneCountdown = 0
      this.newPhoneCountdown = 0
      this.sendingPhoneCode = false
      this.sendingNewPhoneCode = false
      this.changingPhone = false
      this.verifyingCurrentPhone = false
    },
    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      if (this.ws) {
        this.ws.close()
      }
      this.$router.push('/login')
    },
    // 加载声音设置
    async loadSoundEnabled() {
      if (this.userInfo.admin !== 1) {
        return
      }
      try {
        const res = await getSoundEnabled()
        if (res && res.code === 0 && res.data !== undefined) {
          this.soundEnabled = res.data
        }
      } catch (e) {
        console.error('加载声音设置失败', e)
      }
    },
    // 切换声音提示开关
    async toggleSoundEnabled() {
      if (this.userInfo.admin !== 1) {
        return
      }
      const newValue = this.soundEnabled === 1 ? 0 : 1
      try {
        const res = await updateSoundEnabled(newValue)
        if (res && res.code === 0) {
          this.soundEnabled = newValue
          this.showToastMessage(newValue === 1 ? '已开启声音提示' : '已关闭声音提示', 'success')
        } else {
          this.showToastMessage(res?.msg || '更新失败', 'error')
        }
      } catch (e) {
        console.error('更新声音设置失败', e)
        this.showToastMessage('更新失败', 'error')
      }
    },
    // 夜间模式切换
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
      this.applyDarkMode()
      // 保存到本地存储
      localStorage.setItem('darkMode', this.isDarkMode ? 'true' : 'false')
    },
    applyDarkMode() {
      if (this.isDarkMode) {
        document.documentElement.classList.add('dark-mode')
      } else {
        document.documentElement.classList.remove('dark-mode')
      }
    },
    initDarkMode() {
      // 从本地存储读取夜间模式设置
      const savedDarkMode = localStorage.getItem('darkMode')
      if (savedDarkMode === 'true') {
        // 用户之前明确选择了夜间模式
        this.isDarkMode = true
        this.applyDarkMode()
      } else if (savedDarkMode === 'false') {
        // 用户之前明确选择了白天模式
        this.isDarkMode = false
        this.applyDarkMode()
      } else {
        // 首次访问或没有保存的设置，默认启用夜间模式
        this.isDarkMode = true
        this.applyDarkMode()
        localStorage.setItem('darkMode', 'true')
      }
    }
  },
  computed: {
    nameInitial() {
      const name = this.profileForm.nickname || this.userInfo.nickname || '用户'
      return name.charAt(0).toUpperCase() || 'U'
    },
    // 是否显示催Ta上线按钮
    shouldShowUrgeOnlineButton() {
      if (!this.currentChatUserId && this.userInfo.admin === 1) {
        return false
      }
      const targetUserId = this.userInfo.admin === 1 ? this.currentChatUserId : (this.adminInfo ? this.adminInfo.id : null)
      if (!targetUserId) {
        return false
      }
      // 只有离线时才显示
      return !this.isUserOnline(targetUserId)
    },
    // 是否禁用催Ta上线按钮
    isUrgeOnlineDisabled() {
      return this.urgeOnlineCountdown > 0
    },
    groupedUserList() {
      try {
        if (!this.userList || !Array.isArray(this.userList) || this.userList.length === 0) {
          return []
        }
        
        // 按首字母分组
        const groups = {}
        
        this.userList.forEach(user => {
          if (!user) return
          const nickname = user.nickname || '未设置昵称'
          const firstLetter = this.getFirstLetter(nickname)
          
          if (!groups[firstLetter]) {
            groups[firstLetter] = []
          }
          groups[firstLetter].push(user)
        })
        
        // 对每个分组内的用户进行排序
        Object.keys(groups).forEach(letter => {
          if (groups[letter] && Array.isArray(groups[letter])) {
            groups[letter].sort((a, b) => {
              const nameA = (a?.nickname || '未设置昵称').toLowerCase()
              const nameB = (b?.nickname || '未设置昵称').toLowerCase()
              return nameA.localeCompare(nameB, 'zh-CN')
            })
          }
        })
        
        // 转换为数组并排序：A-Z在前，#在最后
        const result = []
        const letters = Object.keys(groups).sort((a, b) => {
          if (a === '#') return 1
          if (b === '#') return -1
          return a.localeCompare(b)
        })
        
        letters.forEach(letter => {
          if (groups[letter] && Array.isArray(groups[letter]) && groups[letter].length > 0) {
            result.push({
              letter: letter,
              users: groups[letter]
            })
          }
        })
        
        return result
      } catch (e) {
        console.error('分组用户列表失败', e)
        return []
      }
    }
  },
  watch: {
    showAvatarMenu(newVal) {
      if (newVal) {
        // 下拉菜单打开时，添加全局点击监听器
        this.$nextTick(() => {
          document.addEventListener('click', this.handleAvatarMenuClickOutside)
        })
      } else {
        // 下拉菜单关闭时，移除全局点击监听器
        document.removeEventListener('click', this.handleAvatarMenuClickOutside)
      }
    }
  }
}
</script>

<style scoped>
.chat-container {
  width: 100%;
  height: 100vh;
  /* 页面整体背景：更克制的柔和渐变，突出内容而不过分抢眼 */
  background:
    radial-gradient(130% 160% at 0% 0%, #e8edff 0%, #f5f7ff 32%, #fdfcfb 68%, #e9f1ff 100%);
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.chat-container::before {
  content: '';
  position: fixed;
  inset: 0;
  /* 柔和但存在感更强的光晕，用来打底（白天模式下更明显一些） */
  background:
    radial-gradient(120% 140% at 10% 0%, rgba(99, 102, 241, 0.22), transparent 60%),
    radial-gradient(120% 150% at 90% 100%, rgba(236, 72, 153, 0.18), transparent 55%),
    radial-gradient(120% 150% at 12% 100%, rgba(45, 212, 191, 0.18), transparent 55%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.3) 40%, transparent 70%);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  z-index: 0;
  pointer-events: none;
  opacity: 1;
  transform-origin: center;
  animation:
    chatBackgroundFloat 26s ease-in-out infinite alternate,
    chatBackgroundBreath 18s ease-in-out infinite alternate;
}

.chat-container:hover::before {
  /* 轻微的交互反馈：鼠标悬停时背景有细微呼吸感 */
  opacity: 1;
  filter: brightness(1.02);
}

/* 流光高光带：在底层光晕之上缓慢掠过，形成“流光”效果 */
.chat-container::after {
  content: '';
  position: fixed;
  inset: -10%;
  background:
    linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.0) 30%,
      rgba(255, 255, 255, 0.45) 48%,
      rgba(148, 163, 255, 0.35) 52%,
      rgba(236, 72, 153, 0.22) 56%,
      rgba(255, 255, 255, 0.0) 70%,
      transparent 100%
    );
  mix-blend-mode: screen;
  opacity: 0.0;
  pointer-events: none;
  z-index: 0;
  transform: translate3d(-25%, -10%, 0) rotate(8deg);
  animation: chatBackgroundShimmer 22s ease-in-out infinite;
}

@keyframes chatBackgroundFloat {
  0% {
    transform: translate3d(-0.8%, -0.8%, 0) scale(1);
  }
  50% {
    transform: translate3d(1%, 0.8%, 0) scale(1.01);
  }
  100% {
    transform: translate3d(-1.2%, 1.2%, 0) scale(1.008);
  }
}

@keyframes chatBackgroundBreath {
  0% {
    filter: brightness(0.97) saturate(1);
  }
  50% {
    filter: brightness(1.06) saturate(1.06);
  }
  100% {
    filter: brightness(1.01) saturate(1.03);
  }
}

@keyframes chatBackgroundShimmer {
  0% {
    opacity: 0.0;
    transform: translate3d(-30%, -15%, 0) rotate(8deg);
  }
  25% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
    transform: translate3d(10%, -5%, 0) rotate(8deg);
  }
  75% {
    opacity: 0.45;
  }
  100% {
    opacity: 0.0;
    transform: translate3d(35%, 10%, 0) rotate(8deg);
  }
}

.chat-wrapper {
  display: flex;
  height: calc(100vh - 40px);
  max-width: 1200px;
  margin: 20px auto;
  width: calc(100% - 40px);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* 用户列表面板 */
.user-list-panel {
  width: 280px;
  border-right: 1px solid rgba(224, 224, 224, 0.5);
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.6);
}

.panel-header {
  padding: 20px 16px;
  border-bottom: 1px solid rgba(224, 224, 224, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.5px;
}

.user-count {
  background: #e0e0e0;
  color: #666;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.user-list-content {
  flex: 1;
  overflow-y: auto;
}

.user-group {
  margin-bottom: 4px;
}

.user-group-header {
  padding: 8px 16px;
  background: #f5f5f5;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid #e0e0e0;
}

.user-item {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(240, 240, 240, 0.5);
  background: transparent;
}

.user-item:hover {
  background: rgba(245, 245, 245, 0.6);
}

.user-item.active {
  background: rgba(227, 242, 253, 0.5);
  border-left: 3px solid #2196f3;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1a237e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 16px;
  flex-shrink: 0;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.user-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 15px;
  color: #333;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.unread-badge {
  background-color: #ff4d4f;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
}

.user-status {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-status .status-online {
  color: #4caf50;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
}

.user-status .status-offline {
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
}

.empty-list {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 聊天面板 */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.8);
}

.chat-header {
  padding: 18px 24px;
  border-bottom: 1px solid rgba(224, 224, 224, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

/* 桌面端隐藏移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
}

.chat-user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1a237e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  flex-shrink: 0;
  overflow: visible;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chat-user-avatar:hover:not(.avatar-shaking) {
  transform: scale(1.05);
}

.chat-user-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* 头像摇晃动画 */
@keyframes avatarShake {
  0%, 100% { transform: translateX(0) rotate(0deg); }
  10% { transform: translateX(-8px) rotate(-10deg); }
  20% { transform: translateX(8px) rotate(10deg); }
  30% { transform: translateX(-8px) rotate(-10deg); }
  40% { transform: translateX(8px) rotate(10deg); }
  50% { transform: translateX(-6px) rotate(-8deg); }
  60% { transform: translateX(6px) rotate(8deg); }
  70% { transform: translateX(-4px) rotate(-5deg); }
  80% { transform: translateX(4px) rotate(5deg); }
  90% { transform: translateX(-2px) rotate(-2deg); }
}

.avatar-shaking {
  animation: avatarShake 0.5s ease-in-out !important;
  transition: none !important;
}

.chat-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.chat-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.chat-status {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-online {
  color: #4caf50;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
}

.status-offline {
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.online {
  background: #4caf50;
}

.status-dot.offline {
  background: #ccc;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar-small {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1a237e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.user-avatar-small .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-avatar-small:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: rgba(245, 245, 245, 0.3);
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
}

.message-item {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: flex-start;
}

.message-item.own {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1a237e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  flex-shrink: 0;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.message-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* 可拍一拍的头像样式 */
.message-avatar.pat-pat-able {
  cursor: pointer;
  transition: transform 0.2s;
}

.message-avatar.pat-pat-able:hover:not(.avatar-shaking) {
  transform: scale(1.1);
}

/* 消息列表中的头像抖动动画 */
.message-avatar.avatar-shaking {
  animation: avatarShake 0.5s ease-in-out !important;
  transition: none !important;
}

.message-item.own .message-avatar {
  background: #4caf50;
}

.message-content {
  max-width: 60%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-item.own .message-content {
  align-items: flex-end;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid rgba(224, 224, 224, 0.3);
}

.message-item.own .message-bubble {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border: none;
  box-shadow: 0 2px 12px rgba(33, 150, 243, 0.3);
}

.message-image {
  max-width: 300px;
  max-height: 400px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
  display: block;
}

.message-item.own .message-image {
  border-radius: 8px;
}

.message-time {
  font-size: 12px;
  color: #999;
  padding: 4px 8px 0;
  font-weight: 400;
}

.recall-notice {
  text-align: center;
  font-size: 13px;
  color: #999;
  padding: 8px 12px;
  font-style: italic;
  font-weight: 400;
}

.recall-notice.pat-pat-notice {
  font-weight: 500;
  color: #666;
}

.message-item.recalled .message-content {
  width: 100%;
  max-width: 100%;
  align-items: center;
}

.message-item.pat-pat .message-content {
  width: 100%;
  max-width: 100%;
  align-items: center;
}

/* 消息气泡动画效果 */
.message-enter-active {
  animation: messageFadeIn 0.3s ease-out;
}

.message-leave-active {
  animation: messageFadeOut 0.2s ease-in;
}

.message-move {
  transition: transform 0.3s ease;
}

@keyframes messageFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes messageFadeOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
}

.chat-input {
  padding: 20px 24px;
  border-top: 1px solid rgba(224, 224, 224, 0.3);
  display: flex;
  gap: 12px;
  align-items: center;
  background: transparent;
  position: relative;
}

.input-wrapper {
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
  min-width: 0; /* 允许flex收缩 */
}

.image-button {
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(224, 224, 224, 0.5);
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.image-button:hover:not(:disabled) {
  background: rgba(245, 245, 245, 0.9);
  border-color: #2196f3;
  transform: scale(1.05);
}

.image-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.emoji-button {
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(224, 224, 224, 0.5);
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.emoji-button:hover:not(:disabled) {
  background: rgba(245, 245, 245, 0.9);
  border-color: #2196f3;
  transform: scale(1.05);
}

.emoji-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-input input {
  flex: 1;
  padding: 12px 18px;
  border: 1px solid rgba(224, 224, 224, 0.5);
  border-radius: 24px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  min-width: 0; /* 允许flex收缩 */
  width: 100%;
}

.chat-input input::placeholder {
  color: #999;
}

.chat-input input:focus {
  border-color: #2196f3;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.15);
}

.chat-input input:disabled {
  background: rgba(245, 245, 245, 0.6);
  cursor: not-allowed;
}

.send-button {
  padding: 12px 32px;
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 60px;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.send-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
}

.send-button:disabled {
  background: rgba(204, 204, 204, 0.6);
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* 表情选择面板样式 */
.emoji-panel {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 表情面板渐变动画 */
.emoji-panel-enter-active {
  animation: emojiPanelFadeIn 0.3s ease-out;
}

.emoji-panel-leave-active {
  animation: emojiPanelFadeOut 0.2s ease-in;
}

@keyframes emojiPanelFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes emojiPanelFadeOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
}

.emoji-panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
}

.emoji-panel-header span {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.emoji-panel-close {
  background: transparent;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.emoji-panel-close:hover {
  background: #e0e0e0;
}

/* 表情面板标签页 */
.emoji-panel-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.emoji-tab {
  flex: 1;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.emoji-tab:hover {
  background: #f0f0f0;
  color: #333;
}

.emoji-tab.active {
  color: #2196f3;
  border-bottom-color: #2196f3;
  font-weight: 500;
}

.emoji-list {
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  overflow-y: auto;
  max-height: 240px;
  position: relative;
}

/* 自定义表情列表 */
.custom-emoji-list {
  padding: 8px;
  overflow-y: auto;
  max-height: 240px;
}

.custom-emoji-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.custom-emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.custom-emoji-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-emoji-item:hover {
  border-color: #2196f3;
  transform: scale(1.05);
  background: #f0f7ff;
}

.custom-emoji-item:hover .delete-emoji-btn {
  opacity: 1;
}

.custom-emoji-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.custom-emoji-img.gif {
  /* 动图保持动画 */
}

.delete-emoji-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;
  line-height: 1;
  padding: 0;
}

.delete-emoji-btn:hover {
  background: rgba(244, 67, 54, 0.9);
  opacity: 1;
}

.add-emoji-btn {
  aspect-ratio: 1;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #999;
}

.add-emoji-btn:hover {
  border-color: #2196f3;
  background: #f0f7ff;
  color: #2196f3;
}

.add-icon {
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  margin-bottom: 4px;
}

.add-text {
  font-size: 12px;
}

/* 消息中的表情显示（无气泡框） */
.message-emoji {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 4px;
}

.message-item.own .message-emoji {
  justify-content: flex-end;
}

.custom-emoji-display {
  width: 120px;
  height: 120px;
  object-fit: contain;
  display: block;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.custom-emoji-display:hover {
  transform: scale(1.1);
}

.custom-emoji-display.gif {
  /* 动图保持动画 */
}

.emoji-item {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  aspect-ratio: 1;
  min-height: 32px;
  width: 100%;
  height: 100%;
  position: relative;
  transform-origin: center center;
  box-sizing: border-box;
}

.emoji-item:hover {
  background: #f0f0f0;
  border-color: #2196f3;
  transform: scale(1.15);
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.emoji-item:active {
  transform: scale(0.95);
}

/* 头像下拉菜单样式 */
.avatar-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 120px;
  overflow: hidden;
}

.avatar-menu .menu-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
  color: #333;
}

.avatar-menu .menu-item:hover {
  background: #f5f5f5;
}

.avatar-menu .menu-item:active {
  background: #e0e0e0;
}

/* 设置别称弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.input-with-count .form-input {
  padding-right: 50px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #2196f3;
}

.input-with-count {
  position: relative;
}

.char-count {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #999;
  pointer-events: none;
}

.char-count-full {
  color: #f44336;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: white;
}

.btn-cancel,
.btn-confirm {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-confirm {
  background: #2196f3;
  color: white;
}

.btn-confirm:hover {
  background: #1976d2;
}

.btn-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 个人信息弹窗样式 - 浅色风格 */
.profile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.profile-modal-content {
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* 个人信息弹窗动画 */
.profile-modal-enter-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.profile-modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.profile-modal-enter-active .profile-modal-content {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.profile-modal-leave-active .profile-modal-content {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.profile-modal-enter-from {
  opacity: 0;
}

.profile-modal-enter-from .profile-modal-content {
  opacity: 0;
  transform: translateY(-20px) scale(0.96);
}

.profile-modal-enter-to {
  opacity: 1;
}

.profile-modal-enter-to .profile-modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.profile-modal-leave-from {
  opacity: 1;
}

.profile-modal-leave-from .profile-modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.profile-modal-leave-to {
  opacity: 0;
}

.profile-modal-leave-to .profile-modal-content {
  opacity: 0;
  transform: translateY(-15px) scale(0.95);
}

.profile-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}

.profile-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.profile-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  color: #666;
  font-size: 20px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.profile-close-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.profile-modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.profile-modal-body::-webkit-scrollbar {
  width: 6px;
}

.profile-modal-body::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.profile-modal-body::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.profile-modal-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.profile-section {
  margin-bottom: 32px;
}

.profile-section:last-child {
  margin-bottom: 0;
}

.profile-section-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 12px;
  position: relative;
}

.profile-section-title::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 60px;
  height: 2px;
  background: #2196f3;
}

.profile-field {
  margin-bottom: 20px;
}

.profile-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.profile-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
}

.profile-input:focus {
  border-color: #2196f3;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.field-error {
  margin-top: 6px;
  font-size: 12px;
  color: #f44336;
}

.avatar-upload-area {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-preview-wrapper {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}

.avatar-preview {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
}

.avatar-preview-default {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196f3, #4caf50);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  border: 2px solid #e0e0e0;
}

.avatar-upload-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.avatar-upload-btn:hover {
  background: #f5f5f5;
  border-color: #2196f3;
  color: #2196f3;
}

.password-strength {
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-left: 2px;
}

.strength-label {
  font-size: 13px;
  color: #666;
  margin-right: 8px;
}

.strength-bars {
  flex: 1;
  max-width: 100px;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
}

.strength-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-1 {
  width: 33%;
  background-color: #f44336;
}

.strength-2 {
  width: 66%;
  background-color: #ff9800;
}

.strength-3 {
  width: 100%;
  background-color: #4caf50;
}

.strength-text {
  margin-left: 8px;
  font-size: 12px;
  color: #2196f3;
}

.profile-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
}

.btn-primary, .btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: #2196f3;
  color: white;
  font-weight: 500;
}

.btn-primary:hover:not(:disabled) {
  background: #1976d2;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.profile-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}

.email-field-row,
.phone-field-row {
  display: flex;
  gap: 10px;
}

.email-change-btn,
.phone-change-btn {
  flex-shrink: 0;
  padding: 10px 16px;
  white-space: nowrap;
}

.btn-secondary {
  background: white;
  border: 1px solid #e0e0e0;
  color: #666;
}

.btn-secondary:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn-logout {
  width: 100%;
  padding: 12px;
  background: #f44336;
  border: none;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-logout:hover {
  background: #d32f2f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
}

/* 自定义提示弹窗 */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  min-width: 280px;
  max-width: 400px;
  padding: 14px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 3000;
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-success {
  background: #4caf50;
  color: white;
}

.toast-error {
  background: #f44336;
  color: white;
}

.toast-info {
  background: #2196f3;
  color: white;
}

.toast-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
}

.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* 邮箱变更弹窗样式 */
.email-change-modal-overlay,
.phone-change-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.email-change-modal,
.phone-change-modal {
  width: 90%;
  max-width: 520px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.email-change-header,
.phone-change-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.email-change-header h3,
.phone-change-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.email-change-close,
.phone-change-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #999;
  font-size: 24px;
  font-weight: 300;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.email-change-close:hover,
.phone-change-close:hover {
  background: #f5f5f5;
  color: #333;
}

.email-change-body,
.phone-change-body {
  padding: 24px;
  min-height: 180px;
}

.email-display-wrapper,
.phone-display-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.email-display-icon,
.phone-display-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.email-display,
.phone-display {
  text-align: center;
  font-size: 16px;
  color: #333;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.email-change-field,
.phone-change-field {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.email-code-input,
.phone-code-input {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 8px;
  font-family: 'Courier New', monospace;
}

.email-code-input::placeholder,
.phone-code-input::placeholder {
  letter-spacing: 0;
  font-size: 14px;
  font-weight: normal;
  font-family: inherit;
}

.send-code-btn {
  flex-shrink: 0;
  white-space: nowrap;
  padding: 10px 20px;
  font-size: 13px;
}

.email-error,
.phone-error {
  text-align: center;
  padding: 10px;
  background: #ffebee;
  border-radius: 6px;
  border: 1px solid #ffcdd2;
  margin-top: 8px;
}

.email-change-footer,
.phone-change-footer {
  padding: 16px 24px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  cursor: pointer;
}

.image-preview-content {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  cursor: grab;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transition: transform 0.1s ease-out;
  user-select: none;
  -webkit-user-drag: none;
}

.image-preview-content.dragging {
  cursor: grabbing;
  transition: none;
}

.image-preview-overlay.dragging {
  cursor: grabbing;
}

/* 图片预览动画 - 渐变放大/缩小 */
.image-preview-enter-active,
.image-preview-leave-active {
  transition: opacity 0.3s ease;
}

.image-preview-enter-active .image-preview-content,
.image-preview-leave-active .image-preview-content {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.image-preview-enter-from {
  opacity: 0;
}

.image-preview-enter-from .image-preview-content {
  transform: scale(0.5);
  opacity: 0;
}

.image-preview-enter-to {
  opacity: 1;
}

.image-preview-enter-to .image-preview-content {
  transform: scale(1);
  opacity: 1;
}

.image-preview-leave-from {
  opacity: 1;
}

.image-preview-leave-from .image-preview-content {
  transform: scale(1);
  opacity: 1;
}

.image-preview-leave-to {
  opacity: 0;
}

.image-preview-leave-to .image-preview-content {
  transform: scale(0.5);
  opacity: 0;
}

/* 图片上传弹窗样式 */
.image-upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.image-upload-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.image-upload-modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.image-upload-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.image-count {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.image-count.full {
  color: #f44336;
  font-weight: 600;
}

.modal-close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.modal-close-btn:hover {
  background: #f5f5f5;
}

.image-upload-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.image-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s;
}

.image-item:hover {
  border-color: #2196f3;
  transform: scale(1.02);
}

.image-preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.image-delete-btn:hover {
  background: rgba(244, 67, 54, 0.8);
}

.add-image-item {
  border: 2px dashed #ccc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  cursor: pointer;
}

.add-image-item:hover {
  border-color: #2196f3;
  background: #f0f7ff;
}

.add-image-icon {
  font-size: 32px;
  color: #999;
  margin-bottom: 8px;
}

.add-image-text {
  font-size: 14px;
  color: #666;
}

.image-limit-tip {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  color: #856404;
  font-size: 13px;
  text-align: center;
}

.image-upload-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-send {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #333;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-send {
  background: #2196f3;
  color: white;
  font-weight: 500;
}

.btn-send:hover:not(:disabled) {
  background: #1976d2;
}

.btn-send:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 催Ta上线弹窗样式 */
.urge-online-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.urge-online-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  transform-origin: center center;
}

/* 弹窗淡入淡出动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0 !important;
}

/* 弹窗缩放动画 */
.modal-scale-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
}

.modal-scale-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0.06, 0.68, 0.19) !important;
}

.modal-scale-enter-from {
  opacity: 0 !important;
  transform: scale(0.8) translateY(-20px) !important;
}

.modal-scale-leave-to {
  opacity: 0 !important;
  transform: scale(0.9) translateY(10px) !important;
}

.urge-online-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #4FC3F7;
}

.urge-online-modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.urge-online-modal-content {
  padding: 30px 24px;
}

.urge-question {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0 0 24px;
  text-align: center;
}

.urge-reason-section {
  margin-top: 20px;
}

/* 缘由输入区域和按钮的切换动画 */
.reason-fade-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.reason-fade-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0.06, 0.68, 0.19);
}

.reason-fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
}

.reason-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
}

.reason-fade-slide-enter-to,
.reason-fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 500px;
}

.urge-reason-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.urge-reason-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.urge-reason-input:focus {
  border-color: #4FC3F7;
}

.urge-reason-count {
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.urge-reason-link {
  text-align: left;
  margin-top: 16px;
}

.urge-reason-btn {
  background: transparent;
  border: none;
  color: #4FC3F7;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  transition: color 0.2s;
}

.urge-reason-btn:hover {
  color: #29B6F6;
}

.urge-online-modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.cancel-btn {
  background: #f5f5f5;
  color: #333;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-btn {
  background: #4FC3F7;
  color: white;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 195, 247, 0.4);
  background: #29B6F6;
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* 右键菜单样式 */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 10000;
  padding: 4px 0;
  font-size: 14px;
}

.context-menu-item {
  padding: 10px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  color: #333;
  user-select: none;
}

.context-menu-item:hover:not(.disabled):not(.divider) {
  background: #f5f5f5;
}

.context-menu-item.disabled {
  color: #999;
  cursor: not-allowed;
  opacity: 0.5;
}

.context-menu-item.divider {
  height: 1px;
  padding: 0;
  margin: 4px 0;
  background: #e0e0e0;
  cursor: default;
}

.context-menu-item.danger {
  color: #f44336;
}

.context-menu-item.danger:hover {
  background: #ffebee;
  color: #d32f2f;
}

.menu-icon {
  margin-right: 8px;
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.menu-text {
  flex: 1;
}

.menu-shortcut {
  margin-left: 16px;
  color: #999;
  font-size: 12px;
}

/* 弹窗淡入淡出动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-active .image-upload-modal,
.modal-fade-leave-active .image-upload-modal {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-active .modal-content,
.modal-fade-leave-active .modal-content {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-from .image-upload-modal {
  transform: scale(0.9);
  opacity: 0;
}

.modal-fade-enter-from .modal-content {
  transform: translateY(-20px) scale(0.96);
  opacity: 0;
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-enter-to .image-upload-modal {
  transform: scale(1);
  opacity: 1;
}

.modal-fade-enter-to .modal-content {
  transform: translateY(0) scale(1);
  opacity: 1;
}

.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-leave-from .image-upload-modal {
  transform: scale(1);
  opacity: 1;
}

.modal-fade-leave-from .modal-content {
  transform: translateY(0) scale(1);
  opacity: 1;
}

.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-leave-to .image-upload-modal {
  transform: scale(0.9);
  opacity: 0;
}

.modal-fade-leave-to .modal-content {
  transform: translateY(-15px) scale(0.95);
  opacity: 0;
}

/* 夜间模式切换按钮样式 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 催Ta上线按钮 */
.urge-online-btn {
  padding: 8px 16px;
  background: #4FC3F7;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin-left: 12px;
}

.urge-online-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 195, 247, 0.4);
  background: #29B6F6;
}

.urge-online-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.countdown-text {
  font-size: 13px;
}

/* 声音切换按钮样式 */
.sound-toggle-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 18px;
  padding: 0;
  margin-right: 8px;
}

.sound-toggle-btn:hover {
  background: #f5f5f5;
  border-color: #2196f3;
  transform: scale(1.05);
}

.sound-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.sound-toggle-btn:hover .sound-icon {
  transform: scale(1.1);
}

.sound-icon.enabled {
  filter: drop-shadow(0 0 2px rgba(76, 175, 80, 0.5));
}

.sound-icon.disabled {
  opacity: 0.5;
  filter: grayscale(100%);
}

.theme-toggle-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 18px;
  padding: 0;
}

.theme-toggle-btn:hover {
  background: #f5f5f5;
  border-color: #2196f3;
  transform: scale(1.05);
}

.theme-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.theme-toggle-btn:hover .theme-icon {
  transform: rotate(20deg);
}

.theme-icon.sun {
  filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.5));
}

.theme-icon.moon {
  filter: drop-shadow(0 0 2px rgba(156, 163, 175, 0.5));
}
</style>

<!-- 夜间模式全局样式（非scoped） -->
<style>
/* ==================== 夜间模式样式 ==================== */
:root.dark-mode {
  --dark-bg-primary: #1a1a2e;
  --dark-bg-secondary: #16213e;
  --dark-bg-tertiary: #0f3460;
  --dark-bg-card: #1e2a4a;
  --dark-bg-input: #232b42;
  --dark-bg-hover: #2a3550;
  --dark-text-primary: #e4e6eb;
  --dark-text-secondary: #b0b3b8;
  --dark-text-muted: #8a8d91;
  --dark-border: #3a3f4b;
  --dark-accent: #4f8cff;
  --dark-accent-hover: #3b7ced;
  --dark-success: #42b883;
  --dark-danger: #ff6b6b;
  --dark-shadow: rgba(0, 0, 0, 0.4);
}

/* 主容器 - 夜间模式：夜空渐变背景 */
:root.dark-mode .chat-container {
  background: 
    radial-gradient(ellipse at bottom, #1a1a2e 0%, #16213e 50%, #0f1419 100%),
    linear-gradient(180deg, #0a0e1a 0%, #1a1a2e 25%, #16213e 50%, #0f1419 75%, #050709 100%) !important;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

/* 夜空渐变光晕层 */
:root.dark-mode .chat-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 15% 25%, rgba(79, 140, 255, 0.2) 0%, transparent 40%),
    radial-gradient(circle at 85% 75%, rgba(156, 39, 176, 0.15) 0%, transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 55%),
    radial-gradient(circle at 70% 20%, rgba(100, 181, 246, 0.12) 0%, transparent 50%),
    linear-gradient(180deg, rgba(10, 14, 26, 0.7) 0%, rgba(26, 26, 46, 0.5) 40%, rgba(15, 20, 25, 0.8) 80%, rgba(5, 7, 9, 0.9) 100%);
  pointer-events: none;
  z-index: 0;
  opacity: 0.85;
  animation: nightSkyGlow 15s ease-in-out infinite alternate;
}

/* 星星层 - 下落星星效果 */
:root.dark-mode .chat-container::after {
  content: '';
  position: fixed;
  top: -100%;
  left: 0;
  width: 100%;
  height: 300%;
  background-image: 
    /* 大星星 - 更亮更明显 */
    radial-gradient(2px 2px at 15% 20%, #fff, transparent),
    radial-gradient(2px 2px at 35% 45%, rgba(255, 255, 255, 0.95), transparent),
    radial-gradient(2px 2px at 55% 15%, #fff, transparent),
    radial-gradient(2px 2px at 75% 60%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(2px 2px at 90% 30%, rgba(255, 255, 255, 0.95), transparent),
    radial-gradient(2px 2px at 25% 70%, rgba(255, 255, 255, 0.85), transparent),
    radial-gradient(2px 2px at 65% 80%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(2px 2px at 45% 25%, rgba(255, 255, 255, 0.95), transparent),
    radial-gradient(2px 2px at 10% 60%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(2px 2px at 80% 10%, rgba(255, 255, 255, 0.95), transparent),
    /* 中等星星 */
    radial-gradient(1.5px 1.5px at 20% 35%, rgba(255, 255, 255, 0.85), transparent),
    radial-gradient(1.5px 1.5px at 40% 55%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1.5px 1.5px at 60% 25%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(1.5px 1.5px at 80% 65%, rgba(255, 255, 255, 0.85), transparent),
    radial-gradient(1.5px 1.5px at 30% 80%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1.5px 1.5px at 70% 40%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(1.5px 1.5px at 50% 70%, rgba(255, 255, 255, 0.85), transparent),
    radial-gradient(1.5px 1.5px at 85% 50%, rgba(255, 255, 255, 0.8), transparent),
    /* 小星星 - 更多更密集 */
    radial-gradient(1px 1px at 10% 50%, rgba(255, 255, 255, 0.75), transparent),
    radial-gradient(1px 1px at 50% 10%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 30% 65%, rgba(255, 255, 255, 0.75), transparent),
    radial-gradient(1px 1px at 70% 85%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 85% 50%, rgba(255, 255, 255, 0.75), transparent),
    radial-gradient(1px 1px at 45% 90%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 95% 15%, rgba(255, 255, 255, 0.75), transparent),
    radial-gradient(1px 1px at 5% 75%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 22% 12%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1px 1px at 67% 33%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 88% 78%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1px 1px at 12% 88%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 38% 42%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1px 1px at 92% 58%, rgba(255, 255, 255, 0.7), transparent);
  background-size: 100% 100%;
  background-position: 0% 0%;
  background-repeat: repeat-y;
  pointer-events: none;
  z-index: 1;
  opacity: 0.95;
  animation: starFall 30s linear infinite, starTwinkle 4s ease-in-out infinite alternate;
}

/* 星星下落动画 - 从上方下落 */
@keyframes starFall {
  0% {
    transform: translateY(0);
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0.8;
  }
}

/* 星星闪烁动画 */
@keyframes starTwinkle {
  0% {
    opacity: 0.7;
    filter: brightness(0.8);
  }
  50% {
    opacity: 1;
    filter: brightness(1.2);
  }
  100% {
    opacity: 0.85;
    filter: brightness(1);
  }
}

/* 夜空光晕呼吸动画 */
@keyframes nightSkyGlow {
  0% {
    opacity: 0.8;
    filter: brightness(0.95);
  }
  50% {
    opacity: 0.9;
    filter: brightness(1.05);
  }
  100% {
    opacity: 0.85;
    filter: brightness(1);
  }
}

:root.dark-mode .chat-wrapper {
  background: var(--dark-bg-secondary) !important;
  box-shadow: 0 2px 12px var(--dark-shadow) !important;
}

/* 用户列表面板 */
:root.dark-mode .user-list-panel {
  background: var(--dark-bg-secondary) !important;
  border-right-color: var(--dark-border) !important;
}

:root.dark-mode .panel-header {
  background: var(--dark-bg-card) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .panel-header h3 {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .user-count {
  background: var(--dark-bg-tertiary) !important;
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .user-group-header {
  background: var(--dark-bg-card) !important;
  color: var(--dark-text-secondary) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .user-item {
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .user-item:hover {
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .user-item.active {
  background: var(--dark-bg-tertiary) !important;
  border-left-color: var(--dark-accent) !important;
}

:root.dark-mode .user-name {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .user-status .status-offline {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .empty-list {
  color: var(--dark-text-muted) !important;
}

/* 聊天面板 */
:root.dark-mode .chat-panel {
  background: var(--dark-bg-secondary) !important;
}

:root.dark-mode .chat-header {
  background: var(--dark-bg-card) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .chat-title {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .chat-status .status-offline {
  color: var(--dark-text-muted) !important;
}

/* 声音切换按钮 - 夜间模式 */
:root.dark-mode .sound-toggle-btn {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
}

:root.dark-mode .sound-toggle-btn:hover {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
}

/* 主题切换按钮 - 夜间模式 */
:root.dark-mode .theme-toggle-btn {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
}

:root.dark-mode .theme-toggle-btn:hover {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
}

/* 催Ta上线按钮 - 夜间模式 */
:root.dark-mode .urge-online-btn {
  background: #4FC3F7 !important;
}

:root.dark-mode .urge-online-modal-overlay {
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(6px) !important;
}

:root.dark-mode .urge-online-modal {
  background: var(--dark-bg-card) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6) !important;
  transform-origin: center center !important;
}

/* 确保夜间模式下弹窗元素本身不会阻止动画 */
:root.dark-mode .urge-online-modal-overlay.modal-fade-enter-active,
:root.dark-mode .urge-online-modal-overlay.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:root.dark-mode .urge-online-modal-overlay.modal-fade-enter-from,
:root.dark-mode .urge-online-modal-overlay.modal-fade-leave-to {
  opacity: 0 !important;
}

:root.dark-mode .urge-online-modal.modal-scale-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
}

:root.dark-mode .urge-online-modal.modal-scale-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0.06, 0.68, 0.19) !important;
}

:root.dark-mode .urge-online-modal.modal-scale-enter-from {
  opacity: 0 !important;
  transform: scale(0.8) translateY(-20px) !important;
}

:root.dark-mode .urge-online-modal.modal-scale-leave-to {
  opacity: 0 !important;
  transform: scale(0.9) translateY(10px) !important;
}

:root.dark-mode .urge-online-modal-header {
  background: #4FC3F7 !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .urge-question {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .urge-reason-label {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .urge-reason-input {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .urge-reason-input:focus {
  border-color: var(--dark-accent) !important;
}

:root.dark-mode .urge-reason-count {
  color: var(--dark-text-muted) !important;
}

/* 夜间模式下缘由切换动画 */
:root.dark-mode .reason-fade-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:root.dark-mode .reason-fade-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.55, 0.06, 0.68, 0.19) !important;
}

:root.dark-mode .reason-fade-slide-enter-from {
  opacity: 0 !important;
  transform: translateY(-10px) !important;
  max-height: 0 !important;
  margin-top: 0 !important;
  overflow: hidden !important;
}

:root.dark-mode .reason-fade-slide-leave-to {
  opacity: 0 !important;
  transform: translateY(-5px) !important;
  max-height: 0 !important;
  margin-top: 0 !important;
  overflow: hidden !important;
}

:root.dark-mode .reason-fade-slide-enter-to,
:root.dark-mode .reason-fade-slide-leave-from {
  opacity: 1 !important;
  transform: translateY(0) !important;
  max-height: 500px !important;
}

:root.dark-mode .urge-online-modal-footer {
  border-top-color: var(--dark-border) !important;
}

:root.dark-mode .cancel-btn {
  background: var(--dark-bg-input) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .cancel-btn:hover {
  background: var(--dark-bg-hover) !important;
}

/* 消息区域 */
:root.dark-mode .chat-messages {
  background: var(--dark-bg-primary) !important;
}

:root.dark-mode .empty-chat {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .message-bubble {
  background: var(--dark-bg-card) !important;
  color: var(--dark-text-primary) !important;
  box-shadow: 0 1px 3px var(--dark-shadow) !important;
}

:root.dark-mode .message-item.own .message-bubble {
  background: var(--dark-accent) !important;
  color: white !important;
}

:root.dark-mode .message-time {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .recall-notice {
  color: var(--dark-text-muted) !important;
}

/* 输入区域 */
:root.dark-mode .chat-input {
  background: var(--dark-bg-card) !important;
  border-top-color: var(--dark-border) !important;
}

:root.dark-mode .chat-input input {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .chat-input input::placeholder {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .chat-input input:focus {
  border-color: var(--dark-accent) !important;
}

:root.dark-mode .chat-input input:disabled {
  background: var(--dark-bg-secondary) !important;
}

:root.dark-mode .image-button,
:root.dark-mode .emoji-button {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
}

:root.dark-mode .image-button:hover:not(:disabled),
:root.dark-mode .emoji-button:hover:not(:disabled) {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
}

:root.dark-mode .send-button {
  background: var(--dark-accent) !important;
}

:root.dark-mode .send-button:hover:not(:disabled) {
  background: var(--dark-accent-hover) !important;
}

:root.dark-mode .send-button:disabled {
  background: var(--dark-bg-tertiary) !important;
  color: var(--dark-text-muted) !important;
}

/* 表情面板 */
:root.dark-mode .emoji-panel {
  background: var(--dark-bg-card) !important;
  border-color: var(--dark-border) !important;
  box-shadow: 0 4px 16px var(--dark-shadow) !important;
}

:root.dark-mode .emoji-panel-header {
  background: var(--dark-bg-secondary) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .emoji-panel-header span {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .emoji-panel-close {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .emoji-panel-close:hover {
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .emoji-item:hover {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
}

/* 表情面板标签页 - 夜间模式 */
:root.dark-mode .emoji-panel-tabs {
  background: var(--dark-bg-secondary) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .emoji-tab {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .emoji-tab:hover {
  background: var(--dark-bg-hover) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .emoji-tab.active {
  color: var(--dark-accent) !important;
  border-bottom-color: var(--dark-accent) !important;
}

/* 自定义表情 - 夜间模式 */
:root.dark-mode .custom-emoji-item {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
}

:root.dark-mode .custom-emoji-item:hover {
  border-color: var(--dark-accent) !important;
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .add-emoji-btn {
  border-color: var(--dark-border) !important;
  background: var(--dark-bg-input) !important;
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .add-emoji-btn:hover {
  border-color: var(--dark-accent) !important;
  background: var(--dark-bg-hover) !important;
  color: var(--dark-accent) !important;
}

/* 消息中的表情 - 夜间模式 */
:root.dark-mode .message-emoji {
  /* 表情消息不需要特殊样式，保持原样 */
}

/* 头像下拉菜单 */
:root.dark-mode .avatar-menu {
  background: var(--dark-bg-card) !important;
  border-color: var(--dark-border) !important;
  box-shadow: 0 4px 16px var(--dark-shadow) !important;
}

:root.dark-mode .avatar-menu .menu-item {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .avatar-menu .menu-item:hover {
  background: var(--dark-bg-hover) !important;
}

/* 模态框通用样式 */
:root.dark-mode .modal-overlay,
:root.dark-mode .profile-modal-overlay,
:root.dark-mode .email-change-modal-overlay,
:root.dark-mode .phone-change-modal-overlay,
:root.dark-mode .image-upload-modal-overlay {
  background: rgba(0, 0, 0, 0.7) !important;
}

:root.dark-mode .modal-content,
:root.dark-mode .profile-modal-content,
:root.dark-mode .email-change-modal,
:root.dark-mode .phone-change-modal,
:root.dark-mode .image-upload-modal {
  background: var(--dark-bg-card) !important;
  box-shadow: 0 8px 40px var(--dark-shadow) !important;
}

:root.dark-mode .modal-header,
:root.dark-mode .profile-modal-header,
:root.dark-mode .email-change-header,
:root.dark-mode .phone-change-header,
:root.dark-mode .image-upload-modal-header {
  background: var(--dark-bg-secondary) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .modal-header h3,
:root.dark-mode .profile-modal-header h3,
:root.dark-mode .email-change-header h3,
:root.dark-mode .phone-change-header h3,
:root.dark-mode .image-upload-modal-header h3 {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .modal-close-btn,
:root.dark-mode .profile-close-btn,
:root.dark-mode .email-change-close,
:root.dark-mode .phone-change-close {
  background: var(--dark-bg-input) !important;
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .modal-close-btn:hover,
:root.dark-mode .profile-close-btn:hover,
:root.dark-mode .email-change-close:hover,
:root.dark-mode .phone-change-close:hover {
  background: var(--dark-bg-hover) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .modal-body,
:root.dark-mode .profile-modal-body,
:root.dark-mode .email-change-body,
:root.dark-mode .phone-change-body,
:root.dark-mode .image-upload-modal-body {
  background: var(--dark-bg-card) !important;
}

:root.dark-mode .modal-footer,
:root.dark-mode .email-change-footer,
:root.dark-mode .phone-change-footer,
:root.dark-mode .image-upload-modal-footer {
  background: var(--dark-bg-secondary) !important;
  border-top-color: var(--dark-border) !important;
}

/* 表单元素 */
:root.dark-mode .form-input,
:root.dark-mode .profile-input {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .form-input::placeholder,
:root.dark-mode .profile-input::placeholder {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .form-input:focus,
:root.dark-mode .profile-input:focus {
  border-color: var(--dark-accent) !important;
  box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.2) !important;
}

:root.dark-mode .form-input:disabled,
:root.dark-mode .profile-input:disabled {
  background: var(--dark-bg-secondary) !important;
}

:root.dark-mode .form-group label,
:root.dark-mode .profile-label {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .char-count {
  color: var(--dark-text-muted) !important;
}

/* 个人资料相关 */
:root.dark-mode .profile-section-title {
  color: var(--dark-text-primary) !important;
  border-bottom-color: var(--dark-border) !important;
}

:root.dark-mode .profile-section-title::after {
  background: var(--dark-accent) !important;
}

:root.dark-mode .avatar-upload-btn {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .avatar-upload-btn:hover {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
  color: var(--dark-accent) !important;
}

:root.dark-mode .email-display-wrapper,
:root.dark-mode .phone-display-wrapper {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
}

:root.dark-mode .email-display,
:root.dark-mode .phone-display {
  color: var(--dark-text-primary) !important;
}

/* 按钮样式 */
:root.dark-mode .btn-cancel {
  background: var(--dark-bg-input) !important;
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .btn-cancel:hover {
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .btn-confirm,
:root.dark-mode .btn-primary,
:root.dark-mode .btn-send {
  background: var(--dark-accent) !important;
}

:root.dark-mode .btn-confirm:hover:not(:disabled),
:root.dark-mode .btn-primary:hover:not(:disabled),
:root.dark-mode .btn-send:hover:not(:disabled) {
  background: var(--dark-accent-hover) !important;
}

:root.dark-mode .btn-confirm:disabled,
:root.dark-mode .btn-primary:disabled,
:root.dark-mode .btn-send:disabled {
  background: var(--dark-bg-tertiary) !important;
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .btn-secondary {
  background: var(--dark-bg-input) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .btn-secondary:hover {
  background: var(--dark-bg-hover) !important;
  border-color: var(--dark-accent) !important;
}

:root.dark-mode .btn-logout {
  background: var(--dark-danger) !important;
}

:root.dark-mode .btn-logout:hover {
  background: #e55b5b !important;
}

/* 图片上传相关 */
:root.dark-mode .image-item {
  border-color: var(--dark-border) !important;
}

:root.dark-mode .image-item:hover {
  border-color: var(--dark-accent) !important;
}

:root.dark-mode .add-image-item {
  border-color: var(--dark-border) !important;
  background: var(--dark-bg-input) !important;
}

:root.dark-mode .add-image-item:hover {
  border-color: var(--dark-accent) !important;
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .add-image-icon {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .add-image-text {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .image-count {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .image-limit-tip {
  background: rgba(255, 193, 7, 0.15) !important;
  border-color: rgba(255, 193, 7, 0.4) !important;
  color: #ffc107 !important;
}

/* 图片预览 */
:root.dark-mode .image-preview-overlay {
  background: rgba(0, 0, 0, 0.9) !important;
}

/* 右键菜单 */
:root.dark-mode .context-menu {
  background: var(--dark-bg-card) !important;
  border-color: var(--dark-border) !important;
  box-shadow: 0 4px 16px var(--dark-shadow) !important;
}

:root.dark-mode .context-menu-item {
  color: var(--dark-text-primary) !important;
}

:root.dark-mode .context-menu-item:hover:not(.disabled):not(.divider) {
  background: var(--dark-bg-hover) !important;
}

:root.dark-mode .context-menu-item.disabled {
  color: var(--dark-text-muted) !important;
}

:root.dark-mode .context-menu-item.divider {
  background: var(--dark-border) !important;
}

:root.dark-mode .context-menu-item.danger:hover {
  background: rgba(255, 107, 107, 0.15) !important;
}

:root.dark-mode .menu-shortcut {
  color: var(--dark-text-muted) !important;
}

/* Toast 提示 */
:root.dark-mode .toast-container {
  box-shadow: 0 4px 16px var(--dark-shadow) !important;
}

/* 滚动条样式 */
:root.dark-mode ::-webkit-scrollbar-track {
  background: var(--dark-bg-secondary) !important;
}

:root.dark-mode ::-webkit-scrollbar-thumb {
  background: var(--dark-bg-tertiary) !important;
}

:root.dark-mode ::-webkit-scrollbar-thumb:hover {
  background: var(--dark-border) !important;
}

/* 错误信息 */
:root.dark-mode .field-error {
  color: var(--dark-danger) !important;
}

:root.dark-mode .email-error,
:root.dark-mode .phone-error {
  background: rgba(255, 107, 107, 0.15) !important;
  border-color: rgba(255, 107, 107, 0.4) !important;
  color: var(--dark-danger) !important;
}

/* 密码强度 */
:root.dark-mode .strength-bars {
  background: var(--dark-bg-input) !important;
}

:root.dark-mode .strength-label {
  color: var(--dark-text-secondary) !important;
}

:root.dark-mode .strength-text {
  color: var(--dark-accent) !important;
}

/* 头像预览 */
:root.dark-mode .avatar-preview {
  border-color: var(--dark-border) !important;
}

:root.dark-mode .avatar-preview-default {
  border-color: var(--dark-border) !important;
}

/* 未读消息徽章保持原色 */
:root.dark-mode .unread-badge {
  background-color: #ff4d4f !important;
}

/* 在线状态点保持原色 */
:root.dark-mode .status-dot.online {
  background: #4caf50 !important;
}

:root.dark-mode .status-dot.offline {
  background: #6b7280 !important;
}

/* 消息头像背景 */
:root.dark-mode .message-avatar {
  background: var(--dark-accent) !important;
}

:root.dark-mode .message-item.own .message-avatar {
  background: var(--dark-success) !important;
}

:root.dark-mode .user-avatar,
:root.dark-mode .user-avatar-small,
:root.dark-mode .chat-user-avatar {
  background: var(--dark-accent) !important;
}

/* 确保头像抖动动画在夜间模式下也能正常工作 */
:root.dark-mode .avatar-shaking {
  animation: avatarShake 0.5s ease-in-out !important;
  transition: none !important;
}

/* 平滑过渡动画 */
:root.dark-mode * {
  transition-property: background-color, border-color, color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease;
}

/* 排除抖动动画的元素，避免过渡动画干扰 */
:root.dark-mode .avatar-shaking {
  transition: none !important;
}

/* ==================== 移动端适配 ==================== */
@media (max-width: 768px) {
  /* 基础容器适配 */
  .chat-container {
    height: 100vh;
    height: 100dvh; /* 使用动态视口高度，避免移动端浏览器地址栏影响 */
  }

  .chat-container::before,
  .chat-container::after {
    display: none; /* 移动端禁用复杂背景动画，提升性能 */
  }

  .chat-wrapper {
    height: 100vh;
    height: 100dvh;
    max-width: 100%;
    margin: 0;
    width: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  /* 移动端遮罩层 */
  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* 移动端菜单按钮 */
  .mobile-menu-btn {
    display: flex !important; /* 确保在移动端显示 */
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    border: none;
    background: rgba(0, 0, 0, 0.05);
    color: #333;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: background 0.2s;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    z-index: 10;
    position: relative;
  }

  .mobile-menu-btn:active {
    background: rgba(0, 0, 0, 0.15);
  }

  .mobile-menu-btn svg {
    width: 24px;
    height: 24px;
    stroke: currentColor;
  }
  
  /* 夜间模式下的菜单按钮 */
  :root.dark-mode .mobile-menu-btn {
    background: rgba(255, 255, 255, 0.1) !important;
    color: var(--dark-text-primary) !important;
  }
  
  :root.dark-mode .mobile-menu-btn:active {
    background: rgba(255, 255, 255, 0.2) !important;
  }

  /* 用户列表面板 - 移动端隐藏，改为抽屉式 */
  .user-list-panel {
    position: fixed;
    left: -100%;
    top: 0;
    width: 80%;
    max-width: 320px;
    height: 100vh;
    height: 100dvh;
    z-index: 1000;
    transition: left 0.3s ease;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    border-right: 1px solid rgba(224, 224, 224, 0.5);
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    padding-top: env(safe-area-inset-top); /* 适配手机状态栏 */
  }

  .user-list-panel.show {
    left: 0;
  }
  
  /* 夜间模式下的用户列表面板 */
  :root.dark-mode .user-list-panel {
    background: var(--dark-bg-secondary) !important;
    border-right-color: var(--dark-border) !important;
  }

  /* 聊天面板 - 移动端全屏 */
  .chat-panel {
    width: 100%;
  }

  /* 聊天头部 - 移动端优化 */
  .chat-header {
    padding: 12px 16px;
    padding-top: max(12px, env(safe-area-inset-top)); /* 适配手机状态栏 */
    min-height: 56px;
  }

  .chat-header-left {
    gap: 10px;
    align-items: center;
  }

  .chat-user-avatar {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .chat-title {
    font-size: 16px;
  }

  /* 消息区域 - 移动端优化 */
  .chat-messages {
    padding: 12px 16px;
  }

  .message-item {
    margin-bottom: 12px;
    gap: 8px;
  }

  .message-avatar {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .message-bubble {
    padding: 10px 14px;
    font-size: 15px;
    max-width: 75%;
    line-height: 1.5;
  }

  .message-image {
    max-width: 200px;
    max-height: 300px;
  }

  .message-time {
    font-size: 11px;
  }

  /* 输入区域 - 移动端优化 */
  .chat-input {
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom)); /* 适配iPhone底部安全区域 */
    flex-wrap: nowrap;
    gap: 8px;
  }

  .input-wrapper {
    gap: 8px;
    flex: 1;
    min-width: 0; /* 允许flex收缩 */
  }

  .input-wrapper input {
    font-size: 16px; /* 防止iOS自动缩放 */
    padding: 10px 14px;
    min-width: 0;
    flex: 1;
  }

  .image-button,
  .emoji-button {
    width: 40px;
    height: 40px;
    min-width: 40px;
    flex-shrink: 0;
    font-size: 18px;
  }

  .send-button {
    padding: 10px 20px;
    font-size: 15px;
    min-width: 60px;
    min-height: 40px;
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* 面板头部 - 移动端优化 */
  .panel-header {
    padding: 16px;
  }

  .panel-header h3 {
    font-size: 16px;
  }

  /* 用户列表项 - 移动端优化 */
  .user-item {
    padding: 12px 16px;
    gap: 10px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    font-size: 15px;
  }

  .user-name {
    font-size: 14px;
    margin-bottom: 4px;
  }

  .user-status {
    font-size: 11px;
  }

  /* 弹窗 - 移动端优化 */
  .modal-overlay,
  .profile-modal-overlay,
  .email-change-modal-overlay,
  .phone-change-modal-overlay,
  .image-upload-modal-overlay {
    padding: 16px;
    align-items: flex-end; /* 移动端从底部弹出 */
  }

  .modal-content,
  .profile-modal-content,
  .email-change-modal,
  .phone-change-modal,
  .image-upload-modal {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 20px 20px 0 0;
    margin: 0;
  }

  .modal-header,
  .profile-modal-header,
  .email-change-header,
  .phone-change-header,
  .image-upload-modal-header {
    padding: 16px 20px;
  }

  .modal-header h3,
  .profile-modal-header h3,
  .email-change-header h3,
  .phone-change-header h3,
  .image-upload-modal-header h3 {
    font-size: 18px;
  }

  .modal-body,
  .profile-modal-body,
  .email-change-body,
  .phone-change-body,
  .image-upload-modal-body {
    padding: 16px 20px;
    max-height: calc(90vh - 120px);
    max-height: calc(90dvh - 120px);
  }

  .modal-footer,
  .email-change-footer,
  .phone-change-footer,
  .image-upload-modal-footer {
    padding: 16px 20px;
    gap: 10px;
  }

  .btn-cancel,
  .btn-send,
  .btn-primary,
  .btn-secondary {
    padding: 12px 24px;
    font-size: 15px;
    min-height: 44px; /* 移动端最小触摸目标 */
  }

  /* 表单元素 - 移动端优化 */
  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .form-group input,
  .form-group textarea {
    font-size: 16px; /* 防止iOS自动缩放 */
    padding: 12px;
    min-height: 44px; /* 移动端最小触摸目标 */
  }

  /* 图片上传弹窗 - 移动端优化 */
  .image-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .image-item {
    aspect-ratio: 1;
  }

  .image-preview-thumb {
    max-width: 100%;
    max-height: 100%;
  }

  /* 图片预览 - 移动端优化 */
  .image-preview-overlay {
    padding: 0;
  }

  .image-preview-container {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .image-preview-close {
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    font-size: 24px;
  }

  /* 表情面板 - 移动端优化 */
  .emoji-panel {
    width: 100%;
    max-width: 100%;
    height: 50vh;
    height: 50dvh;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 20px 20px 0 0;
  }

  /* 头像菜单 - 移动端优化 */
  .avatar-menu {
    min-width: 120px;
    font-size: 14px;
  }

  .menu-item {
    padding: 12px 16px;
    min-height: 44px;
  }

  /* 提示消息 - 移动端优化 */
  .toast-message {
    left: 16px;
    right: 16px;
    width: auto;
    max-width: calc(100% - 32px);
    padding: 12px 16px;
    font-size: 14px;
  }

  /* 右键菜单 - 移动端优化 */
  .context-menu {
    min-width: 140px;
    font-size: 14px;
  }

  .context-menu-item {
    padding: 12px 16px;
    min-height: 44px;
  }

  /* 空状态 - 移动端优化 */
  .empty-chat {
    padding: 60px 20px;
  }

  .empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .empty-text {
    font-size: 14px;
  }

  /* 用户组标题 - 移动端优化 */
  .user-group-header {
    padding: 6px 16px;
    font-size: 12px;
  }

  /* 未读徽章 - 移动端优化 */
  .unread-badge {
    font-size: 10px;
    padding: 2px 5px;
    min-width: 16px;
    height: 16px;
  }

  /* 主题切换按钮 - 移动端优化 */
  .theme-toggle-btn {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  /* 用户头像小图标 - 移动端优化 */
  .user-avatar-small {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  /* 催Ta上线按钮 - 移动端优化 */
  .urge-online-btn {
    padding: 8px 14px;
    font-size: 12px;
    min-height: 36px;
    white-space: nowrap;
  }
  
  /* 催Ta上线弹窗 - 移动端优化 */
  .urge-online-modal-overlay {
    padding: 16px;
    align-items: flex-end;
  }
  
  .urge-online-modal {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 20px 20px 0 0;
    margin: 0;
  }
  
  .urge-online-modal-header {
    padding: 16px 20px;
  }
  
  .urge-online-modal-header h3 {
    font-size: 18px;
  }
  
  .urge-online-modal-content {
    padding: 20px;
    max-height: calc(90vh - 180px);
    max-height: calc(90dvh - 180px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .urge-question {
    font-size: 16px;
    margin-bottom: 20px;
  }
  
  .urge-reason-input {
    font-size: 16px;
    min-height: 44px;
    padding: 12px;
  }
  
  .urge-online-modal-footer {
    padding: 16px 20px;
    gap: 10px;
  }
  
  .modal-btn {
    padding: 12px 24px;
    font-size: 15px;
    min-height: 44px;
    flex: 1;
  }

  /* 禁用hover效果（移动端不需要） */
  .user-item:hover,
  .chat-user-avatar:hover:not(.avatar-shaking),
  .action-btn:hover,
  .menu-item:hover {
    background: inherit;
    transform: none;
  }

  /* 优化触摸反馈 */
  .user-item:active,
  .action-btn:active,
  .send-btn:active,
  .menu-item:active {
    opacity: 0.7;
    transform: scale(0.98);
  }

  /* 滚动条优化（移动端隐藏滚动条但保持滚动功能） */
  .chat-messages::-webkit-scrollbar,
  .user-list-content::-webkit-scrollbar {
    width: 4px;
  }

  .chat-messages::-webkit-scrollbar-thumb,
  .user-list-content::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
}

/* 小屏幕手机优化（宽度小于480px） */
@media (max-width: 480px) {
  .chat-header {
    padding: 10px 12px;
  }

  .chat-messages {
    padding: 10px 12px;
  }

  .chat-input {
    padding: 10px 12px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom));
    gap: 8px;
    flex-wrap: nowrap;
  }

  .message-bubble {
    max-width: 80%;
    padding: 8px 12px;
    font-size: 14px;
  }

  .message-image {
    max-width: 180px;
    max-height: 250px;
  }

  .input-wrapper {
    flex: 1;
    min-width: 0;
    max-width: calc(100% - 120px); /* 为按钮留出空间 */
  }

  .input-wrapper input {
    font-size: 16px;
    padding: 8px 12px;
    min-width: 0;
    width: 100%;
  }

  .image-button,
  .emoji-button {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 16px;
    flex-shrink: 0;
  }

  .send-button {
    padding: 8px 16px;
    font-size: 14px;
    min-width: 56px;
    min-height: 36px;
    flex-shrink: 0;
    white-space: nowrap;
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .user-list-panel {
    width: 85%;
    max-width: 280px;
  }

  .modal-content,
  .profile-modal-content,
  .email-change-modal,
  .phone-change-modal,
  .image-upload-modal {
    max-height: 95vh;
    max-height: 95dvh;
  }

  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 横屏模式优化 */
@media (max-width: 768px) and (orientation: landscape) {
  .chat-wrapper {
    height: 100vh;
    height: 100dvh;
  }

  .chat-header {
    padding: 8px 16px;
    min-height: 48px;
  }

  .chat-messages {
    padding: 8px 16px;
  }

  .chat-input {
    padding: 8px 16px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
  }

  .emoji-panel {
    height: 40vh;
    height: 40dvh;
  }
}

/* 高DPI屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .message-image,
  .avatar-img,
  .image-preview-thumb {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}
</style>
